'use client';

import { c as _c } from "react/compiler-runtime";
import type { PrV2ReadModelComment } from '@/data/v2/pr/comment-read-model';
import type { PrV2FileContentParams } from '@/data/v2/pr/use-pr-v2-file-content';
import type { OrderedFiles } from '@/lib/ai/ordered-files-service/order-files-prompts';
import type { FileDiffEntry } from '@/lib/pr-v2/code-changes/types';
import type { PrV2CommentAnchorKind } from '@/lib/pr-v2/comment-anchors';
import type { FileReviewDecision } from '@/lib/pr-v2/file-review-decisions';
import type { CreatePrV2CommentInput, PrV2CommentResponse, PrV2CommentType, PrV2ReactionContent, PrV2ReviewThread, PrV2SummaryResponse } from '@/lib/pr-v2/types';
import type { DiffNavigationContext } from '@/lib/stores/navigation/diff-navigation-store';
import type { PrV2FileDraftState } from '@/lib/stores/pr-v2-draft-store';
import type { CodeContent, File } from '@/types/github';
import type { AnnotationSide, DiffLineAnnotation, FileContents, FileDiffMetadata, FileDiffOptions, FileOptions, LineAnnotation, SelectedLineRange } from '@pierre/diffs';
import { cloneFileDiffMetadata, parseDiffFromFile } from '@pierre/diffs';
import { File as CodeFile, FileDiff, MultiFileDiff, PatchDiff } from '@pierre/diffs/react';
import { useQueryClient } from '@tanstack/react-query';
import { Bot, Send, X } from 'lucide-react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useHotkeys } from 'react-hotkeys-hook';
import { toast } from 'sonner';
import type { PrV2ApplySuggestionPayload } from './comments/pr-v2-suggestion-block';
import type { PrV2DecisionGutterMarkerClickPayload } from './decisions/pr-v2-decision-gutter-markers';
import type { PrV2PendingScrollTarget } from './diff/hooks/use-pr-v2-pending-comment-comparison';
import type { PrV2ChapterEntry, PrV2ChapterModeState } from './diff/lib/pr-v2-chapter-mode';
import type { PrV2GroupHeaderArgs } from './diff/pr-v2-group-section';
import type { PrV2FileTreeOpenBreakpoint, PrV2GroupedCodeContent } from './file-tree-v2/pr-v2-file-tree-utils';
import type { DraftCommentCodeSource } from './lib/draft-comment-ai-chat';
import type { PrV2ThreadCardAiPromptContext, PrV2ThreadCardMutationState, PrV2ThreadReactionsProps } from './shared/thread/pr-v2-thread-card';
import { usePrV2GroupCardSlot } from '../providers/pr-v2-sidebar-provider';
import { disposePrV2DecisionGutterMarkers, syncPrV2DecisionGutterMarkers } from './decisions/pr-v2-decision-gutter-markers';
import { ADD_COMMENT_BUTTON_CSS, isLineInDiffHunks } from './diff/add-comment-button/add-comment-button';
import { usePrV2CommentNavigation } from './diff/hooks/use-pr-v2-comment-navigation';
import { usePrV2CurrentGroup } from './diff/hooks/use-pr-v2-current-group';
import { usePrV2FileVisibilityState } from './diff/hooks/use-pr-v2-file-visibility-state';
import { isPrV2FileNavigationHotkeyTarget, isPrV2HotkeyInteractiveTarget, isPrV2HotkeyTextEntryTarget } from './diff/hooks/use-pr-v2-hotkey-interactive-target';
import { PR_V2_REPO_FILE_LINK_UNSAFE_CSS, usePrV2RepoFileLinks } from './diff/hooks/use-pr-v2-repo-file-links';
import { usePrV2ScrollToCommentWithOffset } from './diff/hooks/use-pr-v2-scroll-to-comment-with-offset';
import { calculatePrV2DefaultComparison, findPrV2PreviousPushComparison, usePrV2VersionComparison } from './diff/hooks/use-pr-v2-version-comparison';
import { getNextFilenameInOrder, resolveActiveFilename, resolveKeyboardAnchorFilename, resolveViewportActiveFilename } from './diff/lib/pr-v2-active-file-controller';
import { applyPrV2Suggestion } from './diff/lib/pr-v2-apply-suggestion';
import { buildPrV2ChapterModeModel, EMPTY_PR_V2_CHAPTER_MODE_STATE, isPrV2LargePrChapterMode, resolvePrV2ActiveChapterEntry } from './diff/lib/pr-v2-chapter-mode';
import { getCompactVersionLabel, getPrV2ComparisonChoices } from './diff/lib/pr-v2-comparison-choices';
import { getDefaultCollapsedPrV2Filenames } from './diff/lib/pr-v2-default-collapsed-files';
import { buildPrV2CompareCommands, buildPrV2FileRows, buildPrV2ViewedCommands } from './diff/lib/pr-v2-diff-commands';
import { PrV2ChapterSwitcher } from './diff/pr-v2-chapter-switcher';
import { PrV2DiffWorkerPool, usePrV2DiffRendererReady } from './diff/pr-v2-diff-worker-pool';
import { PrV2GroupCard } from './diff/pr-v2-group-card';
import { PrV2GroupSection } from './diff/pr-v2-group-section';
import { PrV2RepoFilePanel } from './diff/pr-v2-repo-file-panel';
import { PrV2SinceLastReviewNotice } from './diff/pr-v2-since-last-review-notice';
import { PrV2FileTree } from './file-tree-v2/pr-v2-file-tree';
import { getPrV2FileElementId, groupCodeContent, hasUsablePrV2OrderedFiles } from './file-tree-v2/pr-v2-file-tree-utils';
import { buildDraftCommentAiChatText } from './lib/draft-comment-ai-chat';
import { usePublishPrV2FileFilterSnapshot } from './pr-v2-file-filter-context';
import { PrV2ChangesContentSkeleton, PrV2ChangesPaneSkeleton } from './shared/skeletons/pr-v2-loading-sections';
import { PrV2ThreadCard } from './shared/thread/pr-v2-thread-card';
import { PrV2FileHeader } from '@/app/pr-v2/[owner]/[repo]/pull/[number]/components/diff/pr-v2-file-header/pr-v2-file-header';
import { getFileReviewCommentPreviews } from '@/app/pr-v2/[owner]/[repo]/pull/[number]/components/diff/pr-v2-file-story-metadata-utils';
import { useRegisterCommands } from '@/commands/use-commands';
import { DIFF_FILENAME_ATTRIBUTE } from '@/components/highlighted-text-popup/utils';
import { PIERRE_DIFF_PALETTE_OVERRIDES, PIERRE_DIFF_ROW_BACKGROUND_UNSAFE_CSS } from '@/components/shared/diff/pierre-diff-palette';
import { RenderErrorBoundary } from '@/components/shared/render-error-boundary';
import { useMarkdownLinkPaste } from '@/components/textarea/hooks/use-markdown-link-paste';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { useSidebarActions } from '@/components/ui/sidebar';
import { Textarea } from '@/components/ui/textarea';
import { useProfile } from '@/data/profile/use-profile.client';
import { buildPrV2CommentReadModel, buildPrV2ReadModelByGithubCommentId } from '@/data/v2/pr/comment-read-model';
import { usePrV2LocalCommentModel } from '@/data/v2/pr/local-comment-model';
import { usePendingCommentsStore } from '@/data/v2/pr/pending-comments-store';
import { CommentSubjectType } from '@/data/v2/pr/pending-comments-types';
import { prV2CodeChangesQueryKey, prV2SummaryQueryKey } from '@/data/v2/pr/query-keys';
import { prV2CodeChangesQueryOptions, usePrV2CodeChanges } from '@/data/v2/pr/use-pr-v2-code-changes';
import { usePrV2CommentActions } from '@/data/v2/pr/use-pr-v2-comment-actions';
import { usePrV2Discussion } from '@/data/v2/pr/use-pr-v2-discussion';
import { ensurePrV2FileContent, usePrV2FileContent } from '@/data/v2/pr/use-pr-v2-file-content';
import { usePrV2FileDescription } from '@/data/v2/pr/use-pr-v2-file-description';
import { usePrV2OrderedFiles } from '@/data/v2/pr/use-pr-v2-ordered-files';
import { usePrV2Reactions } from '@/data/v2/pr/use-pr-v2-reactions';
import { usePrV2ReviewThreads } from '@/data/v2/pr/use-pr-v2-review-threads';
import { usePrV2Reviews } from '@/data/v2/pr/use-pr-v2-reviews';
import { usePrV2Versions } from '@/data/v2/pr/use-pr-v2-versions';
import { useIsMobile } from '@/hooks/use-mobile';
import { trackClientEvent } from '@/lib/analytics/posthog-client';
import { clientLogger } from '@/lib/client-logger';
import { isPureRename } from '@/lib/diff/pr-diff-helpers';
import { shouldEnablePrV2CustomComparisonOrderedFiles } from '@/lib/pr-v2/code-changes/custom-comparison-ordered-files-rollout';
import { createPierreFileContents, ensurePierreFileDiffCacheKey } from '@/lib/pr-v2/code-changes/pierre-file-contents';
import { getPrV2ChangesCommentAnchorIds, PR_V2_THREAD_CARD_SELECTOR } from '@/lib/pr-v2/comment-anchors';
import { githubReviewSideToCommentSide } from '@/lib/pr-v2/comment-side';
import { buildFileReviewDecisionsByFilename, formatFileReviewDecisionLines, groupAnchoredDecisionsByLine } from '@/lib/pr-v2/file-review-decisions';
import { parsePendingGithubCommentId, toPendingGithubCommentId } from '@/lib/pr-v2/pending-comment-ids';
import { dispatchPrV2RevealComment } from '@/lib/pr-v2/reveal-comment';
import { getPrV2FileViewportTopOffset, PR_V2_BASE_FILE_VIEWPORT_TOP_OFFSET } from '@/lib/pr-v2/sticky-tabs-offset';
import { createPrV2FileFilter, filterPrV2FileGroups, getPrV2VisibleFileStats } from '@/lib/pr-v2/test-file-filter';
import { useDiffNavigationStore } from '@/lib/stores/navigation/diff-navigation-store';
import { getPrV2ActiveFileStoreKey, usePrV2ActiveFileStore } from '@/lib/stores/navigation/pr-v2-active-file-store';
import { useMarkPrV2ContentPainted, usePrV2ContentPainted } from '@/lib/stores/navigation/pr-v2-content-paint-store';
import { getPrV2DraftStoreKey, usePrV2DraftStore } from '@/lib/stores/pr-v2-draft-store';
import { getPrV2ReplyDraftStoreKey, usePrV2ReplyDraftStore } from '@/lib/stores/pr-v2-reply-draft-store';
import { normalizePrV2HunkSeparatorOption, usePrV2ViewPreferencesStore } from '@/lib/stores/pr-v2-view-preferences-store';
import { cn, metaShortcut } from '@/lib/utils';
import { useAuth } from '@/providers/auth-provider';
type PrV2DiffCommentThreadAnnotation = {
  kind: 'comments';
  filePath: string;
  side: AnnotationSide;
  lineNumber: number;
  thread?: PrV2ReviewThread;
  threadDraftKey: string;
  comments: PrV2CommentResponse[];
};
type PrV2DiffDraftAnnotation = {
  kind: 'draft';
  filePath: string;
  side: AnnotationSide;
  lineNumber: number;
  startLine: number | null;
  startSide: AnnotationSide | null;
};
type PrV2DiffCommentAnnotationMetadata = PrV2DiffCommentThreadAnnotation | PrV2DiffDraftAnnotation;
type PrV2CodeLineAnnotation = LineAnnotation<PrV2DiffCommentAnnotationMetadata>;
type PrV2DiffOptions = FileDiffOptions<PrV2DiffCommentAnnotationMetadata, undefined>;
type PrV2CodeFileOptions = FileOptions<PrV2DiffCommentAnnotationMetadata, undefined>;
function buildDecisionMarkerAiChatText({
  filename,
  lineStart,
  lineEnd,
  decisionLabel
}: PrV2DecisionGutterMarkerClickPayload): string {
  return `Review decision\nLocation: @${filename} · ${formatFileReviewDecisionLines(lineStart, lineEnd)}\n\n${decisionLabel}`.trim();
}
type PrV2InlineDraftTarget = {
  lineNumber: number;
  side: AnnotationSide;
  startLine: number | null;
  startSide: AnnotationSide | null;
};
type PrV2GroupedFileLevelThread = {
  key: string;
  threadDraftKey: string;
  thread?: PrV2ReviewThread;
  comments: PrV2CommentResponse[];
};
function buildThreadReplyDraftKey(threadDraftKey: string, commentId: string): string {
  return `${threadDraftKey}::${commentId}`;
}
function buildComparisonKey(baseSha: string | null, headSha: string | null): string | null {
  return baseSha && headSha ? `${baseSha}:${headSha}` : null;
}
function buildVisibleHeadScopeKey({
  owner,
  repo,
  number,
  baseSha
}: {
  owner: string;
  repo: string;
  number: string;
  baseSha: string | null;
}): string {
  return JSON.stringify([owner, repo, number, baseSha ?? null]);
}
function areShasEqual(left: string | null | undefined, right: string | null | undefined): boolean {
  if (!left || !right) {
    return false;
  }
  return left.toLowerCase() === right.toLowerCase();
}
function getFallbackShaLabel(sha: string | null | undefined, fallback: string): string {
  if (!sha) {
    return fallback;
  }
  return sha.slice(0, 7);
}
const PIERRE_DIFF_SELECTION_UNSAFE_CSS = `
${ADD_COMMENT_BUTTON_CSS}
${PIERRE_DIFF_ROW_BACKGROUND_UNSAFE_CSS}
${PR_V2_REPO_FILE_LINK_UNSAFE_CSS}

[data-line-type='change-addition'][data-selected-line] [data-column-content],
[data-line-type='change-deletion'][data-selected-line] [data-column-content],
[data-line-type='context-expanded'][data-selected-line] [data-column-content] {
  background-color: var(--diffs-bg-selection-background-override, var(--diffs-bg-selection));
}

[data-unified] [data-line-type='change-deletion'][data-column-number] [data-line-number-content] {
  position: relative;
  visibility: hidden;
}

[data-unified] [data-line-type='change-deletion'][data-column-number] [data-line-number-content]::after {
  content: '-';
  position: absolute;
  right: 0;
  visibility: visible;
}

:where([data-background]) [data-selected-line]:where([data-line], [data-no-newline]) {
  --diffs-line-bg: var(--diffs-bg-selection-background-override, var(--diffs-bg-selection));
  background-color: var(
    --diffs-bg-selection-background-override,
    var(--diffs-bg-selection)
  ) !important;
}

:where([data-background]) [data-selected-line]:where([data-gutter-buffer], [data-column-number]) {
  --diffs-line-bg: var(
    --diffs-bg-selection-number-background-override,
    var(--diffs-bg-selection-number)
  );
  background-color: var(
    --diffs-bg-selection-number-background-override,
    var(--diffs-bg-selection-number)
  ) !important;
}

@media (pointer: fine) {
  :where([data-background]) [data-selected-line][data-hovered]:where([data-line], [data-no-newline]) {
    --diffs-line-bg: var(--diffs-bg-selection-background-override, var(--diffs-bg-selection));
    background-color: var(
      --diffs-bg-selection-background-override,
      var(--diffs-bg-selection)
    ) !important;
  }

  :where([data-background]) [data-selected-line][data-hovered]:where([data-gutter-buffer], [data-column-number]) {
    --diffs-line-bg: var(
      --diffs-bg-selection-number-background-override,
      var(--diffs-bg-selection-number)
    );
    background-color: var(
      --diffs-bg-selection-number-background-override,
      var(--diffs-bg-selection-number)
    ) !important;
  }
}

[data-line-type='change-addition'][data-selected-line] [data-column-number],
[data-line-type='change-deletion'][data-selected-line] [data-column-number],
[data-line-type='context-expanded'][data-selected-line] [data-column-number] {
  background-color: var(
    --diffs-bg-selection-number-background-override,
    var(--diffs-bg-selection-number)
  );
}

[data-code] {
  padding-bottom: 0;
}

[data-overflow='wrap'] {
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
}

[data-overflow='scroll'] [data-code] {
  -webkit-overflow-scrolling: touch;
  touch-action: pan-x pan-y pinch-zoom;
}

[data-overflow='wrap'] [data-code] {
  overflow-x: hidden;
}

[data-overflow='wrap'] [data-line],
[data-overflow='wrap'] [data-line] span,
[data-overflow='wrap'] [data-content],
[data-overflow='wrap'] [data-column-content],
[data-overflow='wrap'] [data-annotation-content] {
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
}

/* Keep split panes evenly sized when one side renders a wide inline
   annotation card. Pierre's wrap-mode grid uses intrinsic sizing by
   default, so we clamp the content track to minmax(0, 1fr). */
[data-diff-type='split'][data-overflow='wrap'],
[data-type='split'][data-overflow='wrap'] {
  --diffs-code-grid: var(
    --diffs-grid-number-column-width,
    minmax(min-content, max-content)
  ) minmax(0, 1fr);
}

[data-diff-type='split'][data-overflow='wrap'] [data-content],
[data-diff-type='split'][data-overflow='wrap'] [data-annotation-content],
[data-type='split'][data-overflow='wrap'] [data-column-content],
[data-type='split'][data-overflow='wrap'] [data-annotation-content] {
  min-width: 0;
}

[data-diff-type='split'][data-overflow='wrap'] [data-annotation-content],
[data-type='split'][data-overflow='wrap'] [data-annotation-content] {
  width: 100%;
  max-width: 100%;
}

::slotted([data-annotation-slot]) {
  min-width: 0;
  max-width: 100%;
}

[data-separator='line-info'] {
  margin-top: 0;
  margin-bottom: 0;
}

[data-separator='line-info']:not([data-separator-first]) {
  margin-top: 6px;
}

[data-separator='line-info'][data-separator-first] {
  margin-bottom: 6px;
}

[data-separator='line-info'] [data-separator-wrapper] {
  border-radius: 0;
}

/* Note: Pierre hard-codes an extra deleted-side inset in split mode. Keep these
   overrides scoped to split diffs so the outer edges stay visually symmetric
   without changing unified mode or the hidden-line-number layout. */
[data-type='split'] [data-deletions] [data-separator='line-info'] [data-separator-wrapper] {
  left: var(--diffs-gap-inline, var(--diffs-gap-fallback));
  margin-left: var(--diffs-gap-inline, var(--diffs-gap-fallback));
}

[data-type='split']:not([data-disable-line-numbers]) [data-deletions] [data-column-number] {
  padding-left: 1ch;
}

/* Pierre expands a collapsed bar only when the click lands on this label span, so stretch it over the bar. */
[data-separator='line-info'][data-expand-index] [data-unmodified-lines] {
  flex-grow: 1;
  align-self: stretch;
  align-content: center;
}
`;
const MAX_SCROLL_RETRY_ATTEMPTS = 25;
const DJB2_INITIAL_HASH = 5381;
const DJB2_MULTIPLIER = 33;

/**
 * Generates a stable, compact cache key fragment for rendered diff content. The djb2-derived hash
 * is intentionally lightweight because it only guards React remounts for already-fetched text.
 */
function hashPrV2DiffRenderValue(value: string | null | undefined): string {
  if (!value) {
    return '0';
  }
  let hash = DJB2_INITIAL_HASH;
  for (let index = 0; index < value.length; index += 1) {
    hash = hash * DJB2_MULTIPLIER ^ value.charCodeAt(index);
  }
  return (hash >>> 0).toString(36);
}
function buildPrV2DiffRenderKey({
  filename,
  previousFilename,
  status,
  headSha,
  baseSha,
  sha,
  hasFileDiff,
  oldContent,
  newContent,
  patch,
  additions,
  deletions
}: {
  filename: string;
  previousFilename: string | null;
  status: CodeContent['status'];
  headSha: string | null;
  baseSha: string | null;
  sha: string | null;
  hasFileDiff: boolean;
  oldContent: string | null;
  newContent: string | null;
  patch: string | null;
  additions: number | null | undefined;
  deletions: number | null | undefined;
}): string {
  return [filename, previousFilename ?? '', status, headSha ?? '', baseSha ?? '', String(additions ?? ''), String(deletions ?? ''), sha ?? '', hasFileDiff ? 'fd' : 'nofd',
  // When a self-contained fileDiff exists, the blob sha + comparison shas already pin the
  // rendered content. Skipping the content hashes keeps the mounted diff stable when payloads
  // transition between full and stripped shapes or lazily fetched contents arrive.
  hasFileDiff ? '' : hashPrV2DiffRenderValue(oldContent), hasFileDiff ? '' : hashPrV2DiffRenderValue(newContent), hasFileDiff ? '' : hashPrV2DiffRenderValue(patch)].join(':');
}
function extractRenderableSingleSidedPatchContent(patch: string | null, status: CodeContent['status']): string | null {
  if (!patch || status !== 'added' && status !== 'removed') {
    return null;
  }
  const expectedPrefix = status === 'added' ? '+' : '-';
  const extractedLines: string[] = [];
  let isInHunk = false;
  for (const line of patch.split('\n')) {
    if (line.startsWith('@@')) {
      isInHunk = true;
      continue;
    }

    // File headers only precede the first hunk; inside one, `--- x` is the removed line `-- x`.
    const isFileHeader = !isInHunk && (line.startsWith('diff --git ') || line.startsWith('index ') || line.startsWith('--- ') || line.startsWith('+++ '));
    if (isFileHeader || line === '\\ No newline at end of file' || line.length === 0) {
      continue;
    }
    if (!line.startsWith(expectedPrefix)) {
      return null;
    }
    extractedLines.push(line.slice(1));
  }
  return extractedLines.length > 0 ? extractedLines.join('\n') : null;
}
function normalizePatchForDiffRender(filename: string, previousFilename: string | null, patch: string | null): string | null {
  if (!patch) {
    return null;
  }
  const patchWithoutBoundaryBlankLines = patch.replace(/^(?:\r?\n)+/, '').replace(/(?:\r?\n)+$/, '');
  if (!patchWithoutBoundaryBlankLines.trim()) {
    return null;
  }
  if (patchWithoutBoundaryBlankLines.startsWith('diff --git ')) {
    return patchWithoutBoundaryBlankLines;
  }
  const beforePath = previousFilename ?? filename;
  const normalizedPatchLines = [`diff --git a/${beforePath} b/${filename}`];
  if (!/^--- /m.test(patchWithoutBoundaryBlankLines)) {
    normalizedPatchLines.push(`--- a/${beforePath}`);
  }
  if (!/^\+\+\+ /m.test(patchWithoutBoundaryBlankLines)) {
    normalizedPatchLines.push(`+++ b/${filename}`);
  }
  normalizedPatchLines.push(patchWithoutBoundaryBlankLines);
  return normalizedPatchLines.join('\n');
}
function normalizeContentsForDiffRender(contents: string | null | undefined): string {
  if (!contents) {
    return contents ?? '';
  }
  return /\r?\n$/.test(contents) ? contents : `${contents}\n`;
}
function resolvePrV2CodeViewSource({
  filename,
  previousFilename,
  status,
  oldContent,
  newContent
}: {
  filename: string;
  previousFilename: string | null;
  status: CodeContent['status'];
  oldContent: string | null;
  newContent: string | null;
}): {
  descriptor: FileContents;
  side: AnnotationSide;
} | null {
  const preferredSide: AnnotationSide = status === 'removed' ? 'deletions' : 'additions';
  const preferredContent = preferredSide === 'deletions' ? oldContent : newContent;
  const preferredName = preferredSide === 'deletions' ? previousFilename ?? filename : filename;
  if (typeof preferredContent === 'string') {
    return {
      descriptor: createPierreFileContents({
        name: preferredName,
        contents: normalizeContentsForDiffRender(preferredContent),
        role: preferredSide === 'deletions' ? 'old' : 'new'
      }),
      side: preferredSide
    };
  }
  if (typeof newContent === 'string') {
    return {
      descriptor: createPierreFileContents({
        name: filename,
        contents: normalizeContentsForDiffRender(newContent),
        role: 'new'
      }),
      side: 'additions'
    };
  }
  if (typeof oldContent === 'string') {
    return {
      descriptor: createPierreFileContents({
        name: previousFilename ?? filename,
        contents: normalizeContentsForDiffRender(oldContent),
        role: 'old'
      }),
      side: 'deletions'
    };
  }
  return null;
}
const DEFAULT_DIFF_OPTIONS: PrV2DiffOptions = {
  diffStyle: 'unified',
  disableFileHeader: true,
  theme: {
    dark: 'pierre-dark',
    light: 'pierre-light'
  },
  themeType: 'dark',
  diffIndicators: 'none',
  lineDiffType: 'word-alt',
  overflow: 'wrap',
  expandUnchanged: false,
  unsafeCSS: PIERRE_DIFF_SELECTION_UNSAFE_CSS
};
type PrV2PierreDiffProps = {
  owner: string;
  repo: string;
  number: string;
  isReadOnly?: boolean;
  headSha: string | null;
  baseSha: string | null;
  headRef?: string | null;
  isMerged?: boolean;
  installationId?: number | null;
  comments: PrV2CommentResponse[];
  severityByCommentId?: Record<string, number>;
  /** The base branch's name, or its short SHA when the name is unknown. */
  baseLabel: string;
  /** A visible toolbar host, including in compact panes; leave null for a floating file opener. */
  fileTreeToolbarHost?: HTMLElement | null;
  fileTreeOpenFrom?: PrV2FileTreeOpenBreakpoint;
  /** A narrow host needs unified code and a file drawer, even on a desktop viewport. */
  compact?: boolean;
  /**
   * True while the discussion tab is active or deep-link/hover intent points at it. Gates the
   * discussion query so the Changes tab does not pay for the timeline payload up front; reaction
   * counters hydrate once discussion data loads via tab open or hover prefetch (see pr-v2-tabs).
   */
  discussionDataEnabled?: boolean;
};
const EMPTY_CODE_CONTENT: CodeContent[] = [];
const EMPTY_ORDERED_FILES: OrderedFiles[] = [];
const EMPTY_FILES: File[] = [];
const EMPTY_COMMENTS: PrV2CommentResponse[] = [];
const EMPTY_REVIEW_THREADS: PrV2ReviewThread[] = [];
const EMPTY_GROUPED_FILE_LEVEL_THREADS: PrV2GroupedFileLevelThread[] = [];
const EMPTY_LINE_ANNOTATIONS: DiffLineAnnotation<PrV2DiffCommentAnnotationMetadata>[] = [];
const EMPTY_CODE_LINE_ANNOTATIONS: PrV2CodeLineAnnotation[] = [];
const FILE_VIEWPORT_ACTIVATION_THRESHOLD = 100;
const PR_V2_INLINE_COMMENT_PERF_PREFIX = 'pr-v2-inline-comment';
const PR_V2_DRAFT_COMPOSER_SELECTOR = '[data-pr-v2-draft-composer]';
const VIEWED_FILE_REANCHOR_TOP_OFFSET_PX = 38;
function applyScrollOffsetForElement(element: HTMLElement, scrollOffset: number): void {
  const scrollContainer = document.getElementById('scrollable-content');
  if (scrollContainer instanceof HTMLElement && scrollContainer.contains(element)) {
    scrollContainer.scrollTop = Math.max(0, scrollContainer.scrollTop - scrollOffset);
    return;
  }
  window.scrollBy(0, -scrollOffset);
}

// Where each file section sits in the viewport, and the band below the sticky PR header that
// counts as visible. Sections that are not in the DOM are left out.
function measurePrV2FileSections(filenames: string[]) {
  return {
    viewportTopOffset: getPrV2FileViewportTopOffset(),
    viewportBottom: window.innerHeight,
    sections: filenames.flatMap(filename => {
      const element = document.getElementById(getPrV2FileElementId(filename));
      if (!element) {
        return [];
      }
      const {
        top,
        bottom
      } = element.getBoundingClientRect();
      return [{
        filename,
        top,
        bottom
      }];
    })
  };
}
type PrV2SubmitMode = 'batch' | 'immediate';
function findPrV2DraftComposerElement(filename: string): HTMLElement | null {
  const fileSection = document.getElementById(getPrV2FileElementId(filename));
  if (!fileSection) {
    return null;
  }
  const composer = fileSection.querySelector<HTMLElement>(PR_V2_DRAFT_COMPOSER_SELECTOR);
  if (composer) {
    return composer;
  }
  const diffContainers = fileSection.querySelectorAll('diffs-container');
  for (const diffContainer of diffContainers) {
    if (!(diffContainer instanceof HTMLElement) || !diffContainer.shadowRoot) {
      continue;
    }
    const shadowComposer = diffContainer.shadowRoot.querySelector<HTMLElement>(PR_V2_DRAFT_COMPOSER_SELECTOR);
    if (shadowComposer) {
      return shadowComposer;
    }
  }
  return null;
}
function getOwnFileDiffMetadata(fileDiffByFilename: ReadonlyMap<string, FileDiffMetadata>, filename: string): FileDiffMetadata | undefined {
  const value = fileDiffByFilename.get(filename);
  if (!value || typeof value !== 'object') {
    return undefined;
  }
  return value;
}
function buildFileDiffLookupByFilename(fileDiffEntries: FileDiffEntry[] | undefined, legacyFileDiffByFilename: Record<string, FileDiffMetadata> | undefined): Map<string, FileDiffMetadata> {
  const lookup = new Map<string, FileDiffMetadata>();
  if (Array.isArray(fileDiffEntries)) {
    fileDiffEntries.forEach(entry => {
      if (!entry || typeof entry !== 'object') {
        return;
      }
      const filename = 'filename' in entry ? entry.filename : null;
      const fileDiff = 'fileDiff' in entry ? entry.fileDiff : null;
      if (!filename || typeof filename !== 'string') {
        return;
      }
      if (!fileDiff || typeof fileDiff !== 'object') {
        return;
      }
      lookup.set(filename, fileDiff);
    });
    return lookup;
  }

  // Legacy fallback for older payloads that still serialize filename-keyed objects.
  // This keeps the client resilient during a rolling deploy while new responses move
  // to the safe entries-based transport shape.
  if (!legacyFileDiffByFilename || typeof legacyFileDiffByFilename !== 'object') {
    return lookup;
  }
  Object.entries(legacyFileDiffByFilename).forEach(([filename, fileDiff]) => {
    if (!fileDiff || typeof fileDiff !== 'object') {
      return;
    }
    lookup.set(filename, fileDiff);
  });
  return lookup;
}
function markPrV2InlineCommentPerf(perfId: string, phase: 'click' | 'visible' | 'focused'): void {
  if (typeof performance === 'undefined') {
    return;
  }
  try {
    performance.mark(`${PR_V2_INLINE_COMMENT_PERF_PREFIX}:${phase}:${perfId}`);
  } catch {
    // Ignore environments where Performance mark APIs are unavailable.
  }
}
function measurePrV2InlineCommentPerf(perfId: string, phase: 'visible' | 'focused'): void {
  if (typeof performance === 'undefined') {
    return;
  }
  const clickMarkName = `${PR_V2_INLINE_COMMENT_PERF_PREFIX}:click:${perfId}`;
  const endMarkName = `${PR_V2_INLINE_COMMENT_PERF_PREFIX}:${phase}:${perfId}`;
  const measureName = `${PR_V2_INLINE_COMMENT_PERF_PREFIX}:click-to-${phase}:${perfId}`;
  try {
    performance.measure(measureName, clickMarkName, endMarkName);
  } catch {
    // Ignore missing mark failures from stale or interrupted sessions.
  }
}
function commentsByFilePath(comments: PrV2CommentResponse[]): Map<string, PrV2CommentResponse[]> {
  const map = new Map<string, PrV2CommentResponse[]>();
  comments.forEach(comment => {
    if (!comment.path) {
      return;
    }
    const existing = map.get(comment.path);
    if (existing) {
      existing.push(comment);
      return;
    }
    map.set(comment.path, [comment]);
  });
  return map;
}
function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}
function asPositiveInteger(value: unknown): number | null {
  if (typeof value === 'number' && Number.isInteger(value) && value > 0) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10);
    if (Number.isInteger(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return null;
}
function formatInlineDraftRangeMetaLabel(line: unknown, startLine: unknown): string | undefined {
  const resolvedLine = asPositiveInteger(line);
  const resolvedStartLine = asPositiveInteger(startLine);
  if (!resolvedLine || !resolvedStartLine || resolvedLine === resolvedStartLine) {
    return undefined;
  }
  const rangeStart = Math.min(resolvedStartLine, resolvedLine);
  const rangeEnd = Math.max(resolvedStartLine, resolvedLine);
  return `Lines ${rangeStart}-${rangeEnd}`;
}

/**
 * The whole old or new file from self-contained diff metadata, which keeps each line's
 * terminator. Partial metadata holds only the patch's lines, so it has nothing to add, and
 * metadata that doesn't say which it is (older cached payloads) isn't trusted.
 */
function readFullFileFromDiff(fileDiff: FileDiffMetadata | null, side: 'old' | 'new'): string | null {
  if (fileDiff?.isPartial !== false) {
    return null;
  }
  return (side === 'old' ? fileDiff.deletionLines : fileDiff.additionLines).join('');
}
function normalizeAnnotationSide(value: unknown): AnnotationSide {
  return value === 'LEFT' ? 'deletions' : 'additions';
}
function annotationSideToGithubSide(side: AnnotationSide): 'LEFT' | 'RIGHT' {
  return side === 'deletions' ? 'LEFT' : 'RIGHT';
}
function githubSideToAnnotationSide(side: 'LEFT' | 'RIGHT'): AnnotationSide {
  return side === 'LEFT' ? 'deletions' : 'additions';
}
function normalizeCommentType(value: string | null | undefined): PrV2CommentType {
  return value === 'issue_comment' ? 'issue_comment' : 'review_comment';
}
function toMillis(value: string | null): number {
  if (!value) {
    return 0;
  }
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
}
function getCommentCreatedAt(comment: PrV2CommentResponse): string | null {
  return comment.createdAtGithub ?? comment.updatedAtGithub;
}
type PrV2ReviewThreadCommentContext = {
  thread: PrV2ReviewThread;
  commentNode: PrV2ReviewThread['comments']['nodes'][number];
};
function asGithubDiffSide(value: unknown): 'LEFT' | 'RIGHT' | null {
  return value === 'LEFT' || value === 'RIGHT' ? value : null;
}
function extractInlineCommentAnchor(comment: PrV2CommentResponse, context?: PrV2ReviewThreadCommentContext): {
  lineNumber: number;
  side: AnnotationSide;
} | null {
  const raw = asRecord(comment.raw);

  // Whole-file comments (subject_type: 'file') should never anchor to a diff
  // line — GitHub sets line/position to 1 as a placeholder.
  if (raw?.subject_type === 'file') {
    return null;
  }
  const lineFromRaw = asPositiveInteger(raw?.line) ?? asPositiveInteger(comment.line) ?? asPositiveInteger(raw?.original_line) ?? asPositiveInteger(raw?.start_line);
  const lineFromThread = asPositiveInteger(context?.commentNode.line) ?? asPositiveInteger(context?.commentNode.originalLine) ?? asPositiveInteger(context?.commentNode.startLine) ?? asPositiveInteger(context?.commentNode.originalStartLine) ?? asPositiveInteger(context?.thread.line) ?? asPositiveInteger(context?.thread.originalLine) ?? asPositiveInteger(context?.thread.startLine) ?? asPositiveInteger(context?.thread.originalStartLine);
  const lineNumber = lineFromRaw ?? lineFromThread;
  if (!lineNumber) {
    return null;
  }
  const contextPath = context?.commentNode.path ?? context?.thread.path ?? null;
  if (!lineFromRaw && comment.path && contextPath && contextPath !== comment.path) {
    return null;
  }
  const side = normalizeAnnotationSide(asGithubDiffSide(raw?.side ?? raw?.start_side ?? raw?.original_side) ?? context?.thread.diffSide ?? context?.thread.startDiffSide);
  return {
    lineNumber,
    side
  };
}
function extractCommentNodeId(comment: PrV2CommentResponse): string | null {
  const raw = asRecord(comment.raw);
  const nodeId = raw ? raw.node_id : null;
  return typeof nodeId === 'string' ? nodeId : null;
}
function buildCommentByGithubId(comments: PrV2CommentResponse[]): Map<string, PrV2CommentResponse> {
  return new Map(comments.map(comment => [comment.githubCommentId, comment] as const));
}
function isFileLevelComment(comment: PrV2CommentResponse, context?: PrV2ReviewThreadCommentContext): boolean {
  const raw = asRecord(comment.raw);
  const subjectType = raw?.subject_type;
  if (subjectType === 'file') {
    return true;
  }
  const hasLine = asPositiveInteger(raw?.line) ?? asPositiveInteger(comment.line) ?? asPositiveInteger(raw?.original_line) ?? asPositiveInteger(raw?.start_line) ?? asPositiveInteger(context?.commentNode.line) ?? asPositiveInteger(context?.commentNode.originalLine) ?? asPositiveInteger(context?.commentNode.startLine) ?? asPositiveInteger(context?.commentNode.originalStartLine) ?? asPositiveInteger(context?.thread.line) ?? asPositiveInteger(context?.thread.originalLine) ?? asPositiveInteger(context?.thread.startLine) ?? asPositiveInteger(context?.thread.originalStartLine);
  return hasLine == null;
}
type InlineCommentAnchor = {
  lineNumber: number;
  side: AnnotationSide;
};
const groupedCodeContentCache = new WeakMap<CodeContent[], WeakMap<OrderedFiles[], PrV2GroupedCodeContent[]>>();
const EMPTY_FILE_REVIEW_DECISIONS: FileReviewDecision[] = [];
const commentsByFileCache = new WeakMap<PrV2CommentResponse[], Map<string, PrV2CommentResponse[]>>();
const reviewThreadContextCache = new WeakMap<PrV2ReviewThread[], Map<string, PrV2ReviewThreadCommentContext>>();
const inlineAnchorByCommentIdCache = new WeakMap<PrV2CommentResponse[], WeakMap<Map<string, PrV2ReviewThreadCommentContext>, Map<string, InlineCommentAnchor>>>();
let pendingCommentSequence = 0;
function createPendingCommentId(): number {
  pendingCommentSequence += 1;
  return Date.now() * 1_000 + pendingCommentSequence % 1_000;
}
function resolveCommentSubjectType(comment: PrV2CommentResponse): 'line' | 'file' {
  const raw = asRecord(comment.raw);
  if (raw?.subject_type === CommentSubjectType.file || comment.line == null) {
    return CommentSubjectType.file;
  }
  return CommentSubjectType.line;
}
function getGroupedCodeContentCached(codeContent: CodeContent[], orderedFiles: OrderedFiles[]): PrV2GroupedCodeContent[] {
  const cacheForCodeContent = groupedCodeContentCache.get(codeContent);
  if (cacheForCodeContent) {
    const cached = cacheForCodeContent.get(orderedFiles);
    if (cached) {
      return cached;
    }
  }
  const grouped = groupCodeContent(codeContent, orderedFiles);
  const nextCache = cacheForCodeContent ?? new WeakMap<OrderedFiles[], PrV2GroupedCodeContent[]>();
  nextCache.set(orderedFiles, grouped);
  groupedCodeContentCache.set(codeContent, nextCache);
  return grouped;
}
function getCommentsByFilePathCached(comments: PrV2CommentResponse[]): Map<string, PrV2CommentResponse[]> {
  const cached = commentsByFileCache.get(comments);
  if (cached) {
    return cached;
  }
  const map = commentsByFilePath(comments);
  commentsByFileCache.set(comments, map);
  return map;
}
function buildReviewThreadContextByCommentNodeId(reviewThreads: PrV2ReviewThread[]): Map<string, PrV2ReviewThreadCommentContext> {
  const map = new Map<string, PrV2ReviewThreadCommentContext>();
  reviewThreads.forEach(thread => {
    thread.comments.nodes.forEach(commentNode => {
      map.set(commentNode.id, {
        thread,
        commentNode
      });
    });
  });
  return map;
}
function getReviewThreadContextByCommentNodeIdCached(reviewThreads: PrV2ReviewThread[]): Map<string, PrV2ReviewThreadCommentContext> {
  const cached = reviewThreadContextCache.get(reviewThreads);
  if (cached) {
    return cached;
  }
  const map = buildReviewThreadContextByCommentNodeId(reviewThreads);
  reviewThreadContextCache.set(reviewThreads, map);
  return map;
}
function buildInlineAnchorByCommentId(comments: PrV2CommentResponse[], reviewThreadContextByCommentNodeId: Map<string, PrV2ReviewThreadCommentContext>): Map<string, InlineCommentAnchor> {
  const reviewComments = comments.filter(comment => comment.commentType === 'review_comment');
  const sortedComments = [...reviewComments].sort((a, b) => toMillis(getCommentCreatedAt(a)) - toMillis(getCommentCreatedAt(b)));
  const anchorByCommentId = new Map<string, InlineCommentAnchor>();
  sortedComments.forEach(comment => {
    const commentNodeId = extractCommentNodeId(comment);
    const threadContext = commentNodeId ? reviewThreadContextByCommentNodeId.get(commentNodeId) : undefined;
    const directAnchor = extractInlineCommentAnchor(comment, threadContext);
    if (directAnchor) {
      anchorByCommentId.set(comment.githubCommentId, directAnchor);
    }
  });
  sortedComments.forEach(comment => {
    if (anchorByCommentId.has(comment.githubCommentId) || !comment.inReplyToId) {
      return;
    }
    const parentAnchor = anchorByCommentId.get(comment.inReplyToId);
    if (parentAnchor) {
      anchorByCommentId.set(comment.githubCommentId, parentAnchor);
    }
  });
  return anchorByCommentId;
}
function getInlineAnchorByCommentIdCached(comments: PrV2CommentResponse[], reviewThreadContextByCommentNodeId: Map<string, PrV2ReviewThreadCommentContext>): Map<string, InlineCommentAnchor> {
  const cacheByComments = inlineAnchorByCommentIdCache.get(comments);
  if (cacheByComments) {
    const cached = cacheByComments.get(reviewThreadContextByCommentNodeId);
    if (cached) {
      return cached;
    }
  }
  const map = buildInlineAnchorByCommentId(comments, reviewThreadContextByCommentNodeId);
  const nextCache = cacheByComments ?? new WeakMap<Map<string, PrV2ReviewThreadCommentContext>, Map<string, InlineCommentAnchor>>();
  nextCache.set(reviewThreadContextByCommentNodeId, map);
  inlineAnchorByCommentIdCache.set(comments, nextCache);
  return map;
}
type PrV2DraftComposerCardProps = {
  mode: 'file' | 'line';
  layout?: 'inline' | 'full';
  value: string;
  currentUserLogin: string;
  currentUserAvatarUrl?: string | null;
  isMutating: boolean;
  isSplitDiffStyle: boolean;
  disablePostImmediately?: boolean;
  metaLabel?: string;
  autoFocusInput?: boolean;
  focusKey?: string;
  onVisible?: () => void;
  onFocused?: () => void;
  onChange: (value: string) => void;
  onCancel: () => void;
  onAddToReview: () => void;
  onSubmitImmediately: () => void;
  /**
   * Sends the draft to the AI sidebar chat as a question instead of posting it. Absent when the
   * page has no chat sidebar, which hides the button and leaves ⌘I to the page.
   */
  onAskAi?: () => void;
};
function PrV2DraftComposerCard(t0) {
  const $ = _c(80);
  const {
    mode,
    layout: t1,
    value,
    currentUserLogin,
    currentUserAvatarUrl,
    isMutating,
    isSplitDiffStyle,
    disablePostImmediately: t2,
    metaLabel,
    autoFocusInput: t3,
    focusKey: t4,
    onVisible,
    onFocused,
    onChange,
    onCancel,
    onAddToReview,
    onSubmitImmediately,
    onAskAi
  } = t0;
  const layout = t1 === undefined ? "full" : t1;
  const disablePostImmediately = t2 === undefined ? false : t2;
  const autoFocusInput = t3 === undefined ? false : t3;
  const focusKey = t4 === undefined ? "" : t4;
  let t5;
  if ($[0] !== isMutating || $[1] !== value) {
    t5 = value.trim().length > 0 && !isMutating;
    $[0] = isMutating;
    $[1] = value;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const canSubmit = t5;
  const textareaRef = useRef(null);
  let t6;
  if ($[3] !== currentUserLogin) {
    t6 = currentUserLogin.slice(0, 1).toUpperCase() || "Y";
    $[3] = currentUserLogin;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const avatarFallback = t6;
  let t7;
  let t8;
  if ($[5] !== onVisible) {
    t7 = () => {
      onVisible?.();
    };
    t8 = [onVisible];
    $[5] = onVisible;
    $[6] = t7;
    $[7] = t8;
  } else {
    t7 = $[6];
    t8 = $[7];
  }
  useEffect(t7, t8);
  let t9;
  if ($[8] !== autoFocusInput || $[9] !== onFocused) {
    t9 = () => {
      if (!autoFocusInput) {
        return;
      }
      const focusTextarea = () => {
        const textarea = textareaRef.current;
        if (!textarea || document.activeElement === textarea) {
          onFocused?.();
          return;
        }
        try {
          textarea.focus({
            preventScroll: true
          });
        } catch {
          textarea.focus();
        }
        const caretPosition = textarea.value.length;
        textarea.setSelectionRange(caretPosition, caretPosition);
        onFocused?.();
      };
      if (typeof window.requestAnimationFrame !== "function") {
        focusTextarea();
        return;
      }
      const frameId = window.requestAnimationFrame(focusTextarea);
      return () => {
        window.cancelAnimationFrame(frameId);
      };
    };
    $[8] = autoFocusInput;
    $[9] = onFocused;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== autoFocusInput || $[12] !== focusKey || $[13] !== onFocused) {
    t10 = [autoFocusInput, focusKey, onFocused];
    $[11] = autoFocusInput;
    $[12] = focusKey;
    $[13] = onFocused;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  useEffect(t9, t10);
  let t11;
  if ($[15] !== canSubmit || $[16] !== disablePostImmediately || $[17] !== onAddToReview || $[18] !== onAskAi || $[19] !== onCancel || $[20] !== onSubmitImmediately) {
    t11 = event => {
      if (event.nativeEvent.isComposing) {
        return;
      }
      if ((event.metaKey || event.ctrlKey) && !event.shiftKey && !event.altKey && event.key.toLowerCase() === "i" && canSubmit && onAskAi) {
        event.preventDefault();
        onAskAi();
        return;
      }
      if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
        event.preventDefault();
        if (!canSubmit) {
          return;
        }
        if (event.shiftKey && !disablePostImmediately) {
          onSubmitImmediately();
          return;
        }
        onAddToReview();
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        onCancel();
      }
    };
    $[15] = canSubmit;
    $[16] = disablePostImmediately;
    $[17] = onAddToReview;
    $[18] = onAskAi;
    $[19] = onCancel;
    $[20] = onSubmitImmediately;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const handleKeyDown = t11;
  let t12;
  if ($[22] !== onChange) {
    t12 = {
      onChange,
      textareaRef
    };
    $[22] = onChange;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const handlePaste = useMarkdownLinkPaste(t12);
  const t13 = layout === "inline" ? "pr-v2-inline-draft-composer" : "pr-v2-file-draft-composer";
  let t14;
  if ($[24] !== isSplitDiffStyle || $[25] !== layout) {
    t14 = cn("overflow-hidden rounded-lg border border-zinc-700/60 bg-zinc-900/90 font-sans shadow-sm", layout === "inline" ? cn("mx-2 my-3 [width:calc(100%_-_1rem)] [max-width:calc(100%_-_1rem)]", isSplitDiffStyle ? "sm:mx-4 sm:w-auto sm:max-w-[94%]" : "sm:mx-4 sm:w-auto sm:max-w-[70%]") : "w-full");
    $[24] = isSplitDiffStyle;
    $[25] = layout;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const t15 = metaLabel ? `Add comment · ${metaLabel}` : "Add comment";
  let t16;
  if ($[27] !== t15) {
    t16 = <div className="border-b border-zinc-700/40 px-4 py-2"><p className="text-xs text-zinc-500">{t15}</p></div>;
    $[27] = t15;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  const t17 = currentUserAvatarUrl || undefined;
  const t18 = `${currentUserLogin || "user"} avatar`;
  let t19;
  if ($[29] !== t17 || $[30] !== t18) {
    t19 = <AvatarImage src={t17} alt={t18} />;
    $[29] = t17;
    $[30] = t18;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== avatarFallback) {
    t20 = <AvatarFallback className="bg-zinc-700 text-[10px] font-medium text-white">{avatarFallback}</AvatarFallback>;
    $[32] = avatarFallback;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== t19 || $[35] !== t20) {
    t21 = <Avatar className="mt-0.5 size-6 shrink-0">{t19}{t20}</Avatar>;
    $[34] = t19;
    $[35] = t20;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] !== onChange) {
    t22 = event_0 => onChange(event_0.target.value);
    $[37] = onChange;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  const t23 = mode === "file" ? "Leave a file comment" : "Leave a comment";
  const t24 = mode === "file" ? "Add file comment" : "Add line comment";
  let t25;
  if ($[39] !== handleKeyDown || $[40] !== handlePaste || $[41] !== t22 || $[42] !== t23 || $[43] !== t24 || $[44] !== value) {
    t25 = <Textarea ref={textareaRef} value={value} onChange={t22} onKeyDown={handleKeyDown} onPaste={handlePaste} placeholder={t23} aria-label={t24} className="min-h-[72px] border-zinc-700/60 bg-zinc-950/40 font-sans text-[13px] text-zinc-100 placeholder:text-zinc-500 focus-visible:ring-zinc-500" />;
    $[39] = handleKeyDown;
    $[40] = handlePaste;
    $[41] = t22;
    $[42] = t23;
    $[43] = t24;
    $[44] = value;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  let t26;
  if ($[46] !== canSubmit || $[47] !== mode || $[48] !== onAskAi) {
    t26 = onAskAi ? <Button type="button" size="sm" variant="ghost" aria-label={mode === "file" ? "Ask AI about this file" : "Ask AI about this line"} title={`Ask AI in the sidebar (${metaShortcut("I")})`} disabled={!canSubmit} onClick={onAskAi} className="mr-auto h-7 gap-1.5 px-2 text-xs text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-200 [&_svg]:size-3.5"><Bot />Ask AI</Button> : null;
    $[46] = canSubmit;
    $[47] = mode;
    $[48] = onAskAi;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <X className="size-3.5" />;
    $[50] = t27;
  } else {
    t27 = $[50];
  }
  let t28;
  if ($[51] !== onCancel) {
    t28 = <Button type="button" size="icon" variant="ghost" aria-label="Cancel draft comment" onClick={onCancel} className="size-7 text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-200 [&_svg]:size-3.5">{t27}</Button>;
    $[51] = onCancel;
    $[52] = t28;
  } else {
    t28 = $[52];
  }
  const t29 = mode === "file" ? "Post file comment immediately" : "Post line comment immediately";
  const t30 = disablePostImmediately ? "Post immediately is disabled once a review has started" : "Post immediately";
  const t31 = !canSubmit || disablePostImmediately;
  let t32;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <Send className="size-3.5" />;
    $[53] = t32;
  } else {
    t32 = $[53];
  }
  let t33;
  if ($[54] !== onSubmitImmediately || $[55] !== t29 || $[56] !== t30 || $[57] !== t31) {
    t33 = <Button type="button" size="icon" variant="ghost" className="size-7 text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-200 [&_svg]:size-3.5" aria-label={t29} title={t30} disabled={t31} onClick={onSubmitImmediately}>{t32}</Button>;
    $[54] = onSubmitImmediately;
    $[55] = t29;
    $[56] = t30;
    $[57] = t31;
    $[58] = t33;
  } else {
    t33 = $[58];
  }
  const t34 = mode === "file" ? "Add file comment to review" : "Add line comment to review";
  const t35 = !canSubmit;
  let t36;
  if ($[59] !== onAddToReview || $[60] !== t34 || $[61] !== t35) {
    t36 = <Button type="button" size="sm" className="h-7 text-xs" aria-label={t34} disabled={t35} onClick={onAddToReview}>Add to review</Button>;
    $[59] = onAddToReview;
    $[60] = t34;
    $[61] = t35;
    $[62] = t36;
  } else {
    t36 = $[62];
  }
  let t37;
  if ($[63] !== t26 || $[64] !== t28 || $[65] !== t33 || $[66] !== t36) {
    t37 = <div className="mt-2.5 flex items-center justify-end gap-2">{t26}{t28}{t33}{t36}</div>;
    $[63] = t26;
    $[64] = t28;
    $[65] = t33;
    $[66] = t36;
    $[67] = t37;
  } else {
    t37 = $[67];
  }
  let t38;
  if ($[68] !== t25 || $[69] !== t37) {
    t38 = <div className="min-w-0 flex-1">{t25}{t37}</div>;
    $[68] = t25;
    $[69] = t37;
    $[70] = t38;
  } else {
    t38 = $[70];
  }
  let t39;
  if ($[71] !== t21 || $[72] !== t38) {
    t39 = <div className="px-4 py-3"><div className="flex items-start gap-2.5">{t21}{t38}</div></div>;
    $[71] = t21;
    $[72] = t38;
    $[73] = t39;
  } else {
    t39 = $[73];
  }
  let t40;
  if ($[74] !== mode || $[75] !== t13 || $[76] !== t14 || $[77] !== t16 || $[78] !== t39) {
    t40 = <div data-testid={t13} data-pr-v2-draft-composer={mode} className={t14}>{t16}{t39}</div>;
    $[74] = mode;
    $[75] = t13;
    $[76] = t14;
    $[77] = t16;
    $[78] = t39;
    $[79] = t40;
  } else {
    t40 = $[79];
  }
  return t40;
}
type PrV2InlineDraftComposerProps = {
  prDraftKey: string;
  filename: string;
  currentUserLogin: string;
  currentUserAvatarUrl?: string | null;
  isMutating: boolean;
  isSplitDiffStyle: boolean;
  disablePostImmediately: boolean;
  focusKey: string;
  onVisible?: () => void;
  onFocused?: () => void;
  onCancel: () => void;
  onAddToReview: () => void;
  onSubmitImmediately: () => void;
  onAskAi?: () => void;
};
const PrV2InlineDraftComposer = memo(t0 => {
  const $ = _c(32);
  const {
    prDraftKey,
    filename,
    currentUserLogin,
    currentUserAvatarUrl,
    isMutating,
    isSplitDiffStyle,
    disablePostImmediately,
    focusKey,
    onVisible,
    onFocused,
    onCancel,
    onAddToReview,
    onSubmitImmediately,
    onAskAi
  } = t0;
  let t1;
  if ($[0] !== filename || $[1] !== prDraftKey) {
    t1 = state => {
      const draft = state.draftsByPr[prDraftKey]?.[filename];
      return draft?.comment ?? "";
    };
    $[0] = filename;
    $[1] = prDraftKey;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const draftComment = usePrV2DraftStore(t1);
  let t2;
  if ($[3] !== filename || $[4] !== prDraftKey) {
    t2 = state_0 => {
      const draft_0 = state_0.draftsByPr[prDraftKey]?.[filename];
      return draft_0?.line ?? "";
    };
    $[3] = filename;
    $[4] = prDraftKey;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const draftLine = usePrV2DraftStore(t2);
  let t3;
  if ($[6] !== filename || $[7] !== prDraftKey) {
    t3 = state_1 => {
      const draft_1 = state_1.draftsByPr[prDraftKey]?.[filename];
      return draft_1?.startLine ?? "";
    };
    $[6] = filename;
    $[7] = prDraftKey;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const draftStartLine = usePrV2DraftStore(t3);
  const setDraftComment = usePrV2DraftStore(_temp);
  let t4;
  if ($[9] !== draftLine || $[10] !== draftStartLine) {
    t4 = formatInlineDraftRangeMetaLabel(draftLine, draftStartLine);
    $[9] = draftLine;
    $[10] = draftStartLine;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const metaLabel = t4;
  let t5;
  if ($[12] !== filename || $[13] !== prDraftKey || $[14] !== setDraftComment) {
    t5 = value => {
      setDraftComment(prDraftKey, filename, value);
    };
    $[12] = filename;
    $[13] = prDraftKey;
    $[14] = setDraftComment;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const handleDraftCommentChange = t5;
  let t6;
  if ($[16] !== currentUserAvatarUrl || $[17] !== currentUserLogin || $[18] !== disablePostImmediately || $[19] !== draftComment || $[20] !== focusKey || $[21] !== handleDraftCommentChange || $[22] !== isMutating || $[23] !== isSplitDiffStyle || $[24] !== metaLabel || $[25] !== onAddToReview || $[26] !== onAskAi || $[27] !== onCancel || $[28] !== onFocused || $[29] !== onSubmitImmediately || $[30] !== onVisible) {
    t6 = <PrV2DraftComposerCard mode="line" layout="inline" value={draftComment} currentUserLogin={currentUserLogin} currentUserAvatarUrl={currentUserAvatarUrl} isMutating={isMutating} isSplitDiffStyle={isSplitDiffStyle} disablePostImmediately={disablePostImmediately} metaLabel={metaLabel} autoFocusInput={true} focusKey={focusKey} onVisible={onVisible} onFocused={onFocused} onChange={handleDraftCommentChange} onCancel={onCancel} onAddToReview={onAddToReview} onSubmitImmediately={onSubmitImmediately} onAskAi={onAskAi} />;
    $[16] = currentUserAvatarUrl;
    $[17] = currentUserLogin;
    $[18] = disablePostImmediately;
    $[19] = draftComment;
    $[20] = focusKey;
    $[21] = handleDraftCommentChange;
    $[22] = isMutating;
    $[23] = isSplitDiffStyle;
    $[24] = metaLabel;
    $[25] = onAddToReview;
    $[26] = onAskAi;
    $[27] = onCancel;
    $[28] = onFocused;
    $[29] = onSubmitImmediately;
    $[30] = onVisible;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  return t6;
});
type PrV2ThreadCardWithReplyDraftProps = {
  owner: string;
  repo: string;
  pullNumber: string;
  isReadOnly?: boolean;
  comments: PrV2CommentResponse[];
  thread?: PrV2ReviewThread;
  severityByCommentId?: Record<string, number>;
  replyDraftKey: string;
  prReplyDraftKey: string;
  anchorKind: PrV2CommentAnchorKind;
  layout: 'inline' | 'full';
  isSplitDiffStyle: boolean;
  resolvedPresentation?: 'none' | 'hybrid';
  isMutating: boolean;
  mutationState: PrV2ThreadCardMutationState;
  reactions?: PrV2ThreadReactionsProps;
  reactionAffordancePlacement?: 'footer-row' | 'hover-strip';
  onReplySubmit: (githubCommentId: string, body: string, threadDraftKey: string) => Promise<void> | void;
  onResolveToggle: (comment: PrV2CommentResponse, resolve: boolean) => Promise<void> | void;
  onEditComment: (comment: PrV2CommentResponse, body: string) => Promise<void>;
  onDeleteComment: (comment: PrV2CommentResponse) => Promise<void>;
  onEditPendingComment?: (comment: PrV2CommentResponse, body: string) => Promise<void>;
  onDeletePendingComment?: (comment: PrV2CommentResponse) => Promise<void>;
  onApplySuggestion?: (comment: PrV2CommentResponse, payload: PrV2ApplySuggestionPayload) => Promise<void>;
  suggestionApplyDisabledReason?: string;
  overflowUtilityActions?: boolean;
  aiPromptContext?: PrV2ThreadCardAiPromptContext;
};
const PrV2ThreadCardWithReplyDraft = memo(t0 => {
  const $ = _c(59);
  const {
    owner,
    repo,
    pullNumber,
    isReadOnly: t1,
    comments,
    thread,
    severityByCommentId,
    replyDraftKey,
    prReplyDraftKey,
    anchorKind,
    layout,
    isSplitDiffStyle,
    resolvedPresentation,
    isMutating,
    mutationState,
    reactions,
    reactionAffordancePlacement,
    onReplySubmit,
    onResolveToggle,
    onEditComment,
    onDeleteComment,
    onEditPendingComment,
    onDeletePendingComment,
    onApplySuggestion,
    suggestionApplyDisabledReason,
    overflowUtilityActions: t2,
    aiPromptContext
  } = t0;
  const isReadOnly = t1 === undefined ? false : t1;
  const overflowUtilityActions = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== prReplyDraftKey || $[1] !== replyDraftKey) {
    t3 = state => state.activeReplyTargetsByPr[prReplyDraftKey]?.[replyDraftKey] ?? null;
    $[0] = prReplyDraftKey;
    $[1] = replyDraftKey;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const activeReplyCommentId = usePrV2ReplyDraftStore(t3);
  const rootReplyCommentId = comments.find(_temp2)?.githubCommentId ?? null;
  let t4;
  if ($[3] !== prReplyDraftKey || $[4] !== replyDraftKey) {
    t4 = state_0 => state_0.replyDraftsByPr[prReplyDraftKey]?.[replyDraftKey] ?? "";
    $[3] = prReplyDraftKey;
    $[4] = replyDraftKey;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const legacyReplyDraft = usePrV2ReplyDraftStore(t4);
  let t5;
  if ($[6] !== activeReplyCommentId || $[7] !== replyDraftKey) {
    t5 = activeReplyCommentId ? buildThreadReplyDraftKey(replyDraftKey, activeReplyCommentId) : replyDraftKey;
    $[6] = activeReplyCommentId;
    $[7] = replyDraftKey;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const activeReplyDraftKey = t5;
  let t6;
  if ($[9] !== activeReplyDraftKey || $[10] !== prReplyDraftKey) {
    t6 = state_1 => state_1.replyDraftsByPr[prReplyDraftKey]?.[activeReplyDraftKey] ?? "";
    $[9] = activeReplyDraftKey;
    $[10] = prReplyDraftKey;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const scopedReplyDraft = usePrV2ReplyDraftStore(t6);
  const replyDraft = activeReplyCommentId && activeReplyCommentId === rootReplyCommentId && !scopedReplyDraft ? legacyReplyDraft : scopedReplyDraft;
  const setReplyDraft = usePrV2ReplyDraftStore(_temp3);
  const setActiveReplyTarget = usePrV2ReplyDraftStore(_temp4);
  let t7;
  if ($[12] !== activeReplyDraftKey || $[13] !== prReplyDraftKey || $[14] !== setReplyDraft) {
    t7 = value => {
      setReplyDraft(prReplyDraftKey, activeReplyDraftKey, value);
    };
    $[12] = activeReplyDraftKey;
    $[13] = prReplyDraftKey;
    $[14] = setReplyDraft;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handleReplyDraftChange = t7;
  let t8;
  if ($[16] !== legacyReplyDraft || $[17] !== prReplyDraftKey || $[18] !== replyDraftKey || $[19] !== rootReplyCommentId || $[20] !== setActiveReplyTarget || $[21] !== setReplyDraft) {
    t8 = comment_0 => {
      const nextReplyDraftKey = buildThreadReplyDraftKey(replyDraftKey, comment_0.githubCommentId);
      if (comment_0.githubCommentId === rootReplyCommentId && legacyReplyDraft.trim()) {
        const existingRootDraft = usePrV2ReplyDraftStore.getState().replyDraftsByPr[prReplyDraftKey]?.[nextReplyDraftKey] ?? "";
        if (!existingRootDraft) {
          setReplyDraft(prReplyDraftKey, nextReplyDraftKey, legacyReplyDraft);
          setReplyDraft(prReplyDraftKey, replyDraftKey, "");
        }
      }
      setActiveReplyTarget(prReplyDraftKey, replyDraftKey, comment_0.githubCommentId);
    };
    $[16] = legacyReplyDraft;
    $[17] = prReplyDraftKey;
    $[18] = replyDraftKey;
    $[19] = rootReplyCommentId;
    $[20] = setActiveReplyTarget;
    $[21] = setReplyDraft;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const handleReplySelect = t8;
  let t9;
  if ($[23] !== prReplyDraftKey || $[24] !== replyDraftKey || $[25] !== setActiveReplyTarget) {
    t9 = () => {
      setActiveReplyTarget(prReplyDraftKey, replyDraftKey, null);
    };
    $[23] = prReplyDraftKey;
    $[24] = replyDraftKey;
    $[25] = setActiveReplyTarget;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const handleReplyCancel = t9;
  let t10;
  if ($[27] !== activeReplyCommentId || $[28] !== activeReplyDraftKey || $[29] !== aiPromptContext || $[30] !== anchorKind || $[31] !== comments || $[32] !== handleReplyCancel || $[33] !== handleReplyDraftChange || $[34] !== handleReplySelect || $[35] !== isMutating || $[36] !== isReadOnly || $[37] !== isSplitDiffStyle || $[38] !== layout || $[39] !== mutationState || $[40] !== onApplySuggestion || $[41] !== onDeleteComment || $[42] !== onDeletePendingComment || $[43] !== onEditComment || $[44] !== onEditPendingComment || $[45] !== onReplySubmit || $[46] !== onResolveToggle || $[47] !== overflowUtilityActions || $[48] !== owner || $[49] !== pullNumber || $[50] !== reactionAffordancePlacement || $[51] !== reactions || $[52] !== replyDraft || $[53] !== repo || $[54] !== resolvedPresentation || $[55] !== severityByCommentId || $[56] !== suggestionApplyDisabledReason || $[57] !== thread) {
    t10 = <PrV2ThreadCard owner={owner} repo={repo} pullNumber={pullNumber} isReadOnly={isReadOnly} comments={comments} thread={thread} severityByCommentId={severityByCommentId} replyDraftKey={activeReplyDraftKey} activeReplyCommentId={activeReplyCommentId} anchorKind={anchorKind} layout={layout} isSplitDiffStyle={isSplitDiffStyle} replyComposerPlacement="thread-footer" resolvedPresentation={resolvedPresentation} isMutating={isMutating} mutationState={mutationState} replyDraft={replyDraft} onReplyDraftChange={handleReplyDraftChange} onReplySubmit={onReplySubmit} onReplySelect={handleReplySelect} onReplyCancel={handleReplyCancel} canReplyToComment={_temp5} allowPendingReplyTargets={true} onResolveToggle={onResolveToggle} onEditComment={onEditComment} onDeleteComment={onDeleteComment} onEditPendingComment={onEditPendingComment} onDeletePendingComment={onDeletePendingComment} onApplySuggestion={onApplySuggestion} suggestionApplyDisabledReason={suggestionApplyDisabledReason} reactions={reactions} reactionAffordancePlacement={reactionAffordancePlacement} overflowUtilityActions={overflowUtilityActions} aiPromptContext={aiPromptContext} />;
    $[27] = activeReplyCommentId;
    $[28] = activeReplyDraftKey;
    $[29] = aiPromptContext;
    $[30] = anchorKind;
    $[31] = comments;
    $[32] = handleReplyCancel;
    $[33] = handleReplyDraftChange;
    $[34] = handleReplySelect;
    $[35] = isMutating;
    $[36] = isReadOnly;
    $[37] = isSplitDiffStyle;
    $[38] = layout;
    $[39] = mutationState;
    $[40] = onApplySuggestion;
    $[41] = onDeleteComment;
    $[42] = onDeletePendingComment;
    $[43] = onEditComment;
    $[44] = onEditPendingComment;
    $[45] = onReplySubmit;
    $[46] = onResolveToggle;
    $[47] = overflowUtilityActions;
    $[48] = owner;
    $[49] = pullNumber;
    $[50] = reactionAffordancePlacement;
    $[51] = reactions;
    $[52] = replyDraft;
    $[53] = repo;
    $[54] = resolvedPresentation;
    $[55] = severityByCommentId;
    $[56] = suggestionApplyDisabledReason;
    $[57] = thread;
    $[58] = t10;
  } else {
    t10 = $[58];
  }
  return t10;
});
type PrV2FileSectionProps = {
  owner: string;
  repo: string;
  number: string;
  isReadOnly: boolean;
  prDraftKey: string;
  prReplyDraftKey: string;
  currentUserLogin: string;
  currentUserAvatarUrl?: string | null;
  file: CodeContent;
  fileDiff?: FileDiffMetadata;
  /** Every file the pull request changes, searched for a definition a cmd-clicked name lacks. */
  changedFiles: CodeContent[];
  headSha: string | null;
  baseSha: string | null;
  diffOptions: PrV2DiffOptions;
  isCodeView: boolean;
  onToggleCodeView: (filename: string) => void;
  commentsByFile: Map<string, PrV2CommentResponse[]>;
  severityByCommentId?: Record<string, number>;
  readModelCommentByGithubId: Map<string, PrV2ReadModelComment>;
  reviewThreadContextByCommentNodeId: Map<string, PrV2ReviewThreadCommentContext>;
  reviewThreadByCommentNodeId: Map<string, PrV2ReviewThread>;
  inlineAnchorByCommentId: Map<string, InlineCommentAnchor>;
  fileIsViewed: boolean;
  /** Marked viewed before a push that changed it. */
  fileIsChangedSinceLastView: boolean;
  fileIsCollapsed: boolean;
  fileReviewDecisions?: FileReviewDecision[];
  onDecisionMarkerHover?: (filename: string, lineStart: number, decisionCount: number) => void;
  onDecisionMarkerClick?: (payload: PrV2DecisionGutterMarkerClickPayload) => void;
  isActiveFile: boolean;
  isMutating: boolean;
  mutationState: PrV2ThreadCardMutationState;
  disablePostImmediately: boolean;
  onToggleCollapsed: (filename: string, isCurrentlyCollapsed: boolean) => void;
  onToggleViewed: (filename: string, isCurrentlyViewed: boolean) => void;
  onToggleFileDraft: (filename: string) => void;
  onPostFileComment: (filename: string, mode: PrV2SubmitMode) => void;
  /** Absent when the page has no chat sidebar, which hides "Ask AI" in the composers. */
  onAskDraftInChat?: (filename: string, codeSource: DraftCommentCodeSource) => void;
  onReplySubmit: (githubCommentId: string, body: string, threadDraftKey: string) => Promise<void> | void;
  onResolveToggle: (comment: PrV2CommentResponse, resolve: boolean) => Promise<void> | void;
  onEditComment: (comment: PrV2CommentResponse, body: string) => Promise<void>;
  onDeleteComment: (comment: PrV2CommentResponse) => Promise<void>;
  onEditPendingComment: (comment: PrV2CommentResponse, body: string) => Promise<void>;
  onDeletePendingComment: (comment: PrV2CommentResponse) => Promise<void>;
  reactions: PrV2ThreadReactionsProps;
  onApplySuggestion: (comment: PrV2CommentResponse, payload: PrV2ApplySuggestionPayload) => Promise<void>;
  suggestionApplyDisabledReason?: string;
};
type PrV2StableTreeState = {
  files: File[];
  orderedFiles: OrderedFiles[];
  groupedCodeContent: PrV2GroupedCodeContent[];
};
function areFilesEqual(previous: File[], next: File[]): boolean {
  if (previous === next) {
    return true;
  }
  if (previous.length !== next.length) {
    return false;
  }
  return previous.every((file, index) => {
    const nextFile = next[index];
    return file.filename === nextFile?.filename && file.previous_filename === nextFile?.previous_filename && file.status === nextFile?.status;
  });
}
function areOrderedFilesEqual(previous: OrderedFiles[], next: OrderedFiles[]): boolean {
  if (previous === next) {
    return true;
  }
  if (previous.length !== next.length) {
    return false;
  }
  return previous.every((file, index) => {
    const nextFile = next[index];
    return file.filename === nextFile?.filename && file.orderNumber === nextFile?.orderNumber && file.groupName === nextFile?.groupName && file.groupOrder === nextFile?.groupOrder && file.reviewPriority === nextFile?.reviewPriority;
  });
}
function areGroupedCodeContentEqual(previous: PrV2GroupedCodeContent[], next: PrV2GroupedCodeContent[]): boolean {
  if (previous === next) {
    return true;
  }
  if (previous.length !== next.length) {
    return false;
  }
  return previous.every((group, index) => {
    const nextGroup = next[index];
    if (group.groupName !== nextGroup?.groupName || group.groupOrder !== nextGroup?.groupOrder || group.groupNameReason !== nextGroup?.groupNameReason || group.files.length !== nextGroup?.files.length) {
      return false;
    }
    return group.files.every((file, fileIndex) => file.filename === nextGroup.files[fileIndex]?.filename);
  });
}
function areStableTreeStatesEqual(previous: PrV2StableTreeState | null, next: PrV2StableTreeState | null): boolean {
  if (previous === next) {
    return true;
  }
  if (!previous || !next) {
    return false;
  }
  return areFilesEqual(previous.files, next.files) && areOrderedFilesEqual(previous.orderedFiles, next.orderedFiles) && areGroupedCodeContentEqual(previous.groupedCodeContent, next.groupedCodeContent);
}
function createSnapshotStore<T>(initialSnapshot: T, isEqual: (previous: T, next: T) => boolean = Object.is) {
  let snapshot = initialSnapshot;
  const listeners = new Set<() => void>();
  return {
    getSnapshot: () => snapshot,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    setSnapshot: (nextSnapshot: T) => {
      if (isEqual(snapshot, nextSnapshot)) {
        return;
      }
      snapshot = nextSnapshot;
      listeners.forEach(listener => listener());
    }
  };
}
function createStableTreeStateStore(initialSnapshot: PrV2StableTreeState | null = null) {
  return createSnapshotStore(initialSnapshot, areStableTreeStatesEqual);
}
function observePrV2DiffHost(diffHost: HTMLElement, syncShadowRoot: (root: ShadowRoot) => void) {
  let observedDiffContainer: HTMLElement | null = null;
  const shadowRootObserver = new MutationObserver(() => {
    const shadowRoot = observedDiffContainer?.shadowRoot;
    if (!shadowRoot) {
      return;
    }
    shadowRootObserver.disconnect();
    syncShadowRoot(shadowRoot);
    shadowRootObserver.observe(shadowRoot, {
      childList: true,
      subtree: true
    });
  });
  const attachToDiffContainer = (): void => {
    const diffContainer = diffHost.querySelector('diffs-container');
    if (!(diffContainer instanceof HTMLElement) || !diffContainer.shadowRoot) {
      return;
    }
    observedDiffContainer = diffContainer;
    shadowRootObserver.disconnect();
    syncShadowRoot(diffContainer.shadowRoot);
    shadowRootObserver.observe(diffContainer.shadowRoot, {
      childList: true,
      subtree: true
    });
  };
  attachToDiffContainer();
  const diffHostObserver = new MutationObserver(attachToDiffContainer);
  diffHostObserver.observe(diffHost, {
    childList: true
  });
  return () => {
    diffHostObserver.disconnect();
    shadowRootObserver.disconnect();
  };
}
const PrV2FileSection = memo(
// eslint-disable-next-line complexity
t0 => {
  const $ = _c(374);
  const {
    owner,
    repo,
    number,
    isReadOnly,
    prDraftKey,
    prReplyDraftKey,
    currentUserLogin,
    currentUserAvatarUrl,
    file,
    fileDiff,
    changedFiles,
    headSha,
    baseSha,
    diffOptions,
    isCodeView,
    onToggleCodeView,
    commentsByFile,
    severityByCommentId,
    readModelCommentByGithubId,
    reviewThreadContextByCommentNodeId,
    reviewThreadByCommentNodeId,
    inlineAnchorByCommentId,
    fileIsViewed,
    fileIsChangedSinceLastView,
    fileIsCollapsed,
    fileReviewDecisions,
    onDecisionMarkerHover,
    onDecisionMarkerClick,
    isActiveFile,
    isMutating,
    mutationState,
    disablePostImmediately,
    onToggleCollapsed,
    onToggleViewed,
    onToggleFileDraft,
    onPostFileComment,
    onAskDraftInChat,
    onReplySubmit,
    onResolveToggle,
    onEditComment,
    onDeleteComment,
    onEditPendingComment,
    onDeletePendingComment,
    reactions,
    onApplySuggestion,
    suggestionApplyDisabledReason
  } = t0;
  const filename = file.filename;
  const previousFilename = file.previous_filename ?? null;
  const patch = typeof file.patch === "string" && file.patch.trim().length > 0 ? file.patch : null;
  let t1;
  if ($[0] !== file.status || $[1] !== patch) {
    t1 = extractRenderableSingleSidedPatchContent(patch, file.status);
    $[0] = file.status;
    $[1] = patch;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const singleSidedPatchContent = t1;
  const queryClient = useQueryClient();
  const hasRawTextContents = typeof file.oldContent === "string" || typeof file.newContent === "string";
  const shouldFetchOmittedContents = isCodeView && !hasRawTextContents && file.contentsOmitted === true;
  let t2;
  if ($[3] !== baseSha || $[4] !== file.sha || $[5] !== file.status || $[6] !== filename || $[7] !== headSha || $[8] !== previousFilename) {
    t2 = file.sha && headSha && baseSha ? {
      filename,
      previousFilename,
      sha: file.sha,
      status: file.status,
      headSha,
      baseSha
    } : null;
    $[3] = baseSha;
    $[4] = file.sha;
    $[5] = file.status;
    $[6] = filename;
    $[7] = headSha;
    $[8] = previousFilename;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const fileContentParams = t2;
  let t3;
  if ($[10] !== fileContentParams || $[11] !== shouldFetchOmittedContents) {
    t3 = {
      ...fileContentParams,
      enabled: shouldFetchOmittedContents
    };
    $[10] = fileContentParams;
    $[11] = shouldFetchOmittedContents;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const lazyFileContentQuery = usePrV2FileContent(owner, repo, number, t3);
  const lazyFileContents = file.contentsOmitted === true ? lazyFileContentQuery.data : undefined;
  const oldContent = file.oldContent ?? lazyFileContents?.oldContent ?? (file.status === "removed" && singleSidedPatchContent !== null ? singleSidedPatchContent : null);
  const newContent = file.newContent ?? lazyFileContents?.newContent ?? (file.status === "added" && singleSidedPatchContent !== null ? singleSidedPatchContent : null);
  let t4;
  if ($[13] !== filename || $[14] !== patch || $[15] !== previousFilename) {
    t4 = normalizePatchForDiffRender(filename, previousFilename, patch);
    $[13] = filename;
    $[14] = patch;
    $[15] = previousFilename;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const normalizedPatch = t4;
  const t5 = file.status;
  const t6 = file.sha ?? null;
  const t7 = Boolean(fileDiff);
  let t8;
  if ($[17] !== baseSha || $[18] !== file.additions || $[19] !== file.deletions || $[20] !== file.status || $[21] !== filename || $[22] !== headSha || $[23] !== newContent || $[24] !== normalizedPatch || $[25] !== oldContent || $[26] !== previousFilename || $[27] !== t6 || $[28] !== t7) {
    t8 = buildPrV2DiffRenderKey({
      filename,
      previousFilename,
      status: t5,
      headSha,
      baseSha,
      sha: t6,
      hasFileDiff: t7,
      oldContent,
      newContent,
      patch: normalizedPatch,
      additions: file.additions,
      deletions: file.deletions
    });
    $[17] = baseSha;
    $[18] = file.additions;
    $[19] = file.deletions;
    $[20] = file.status;
    $[21] = filename;
    $[22] = headSha;
    $[23] = newContent;
    $[24] = normalizedPatch;
    $[25] = oldContent;
    $[26] = previousFilename;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  const diffRenderKey = t8;
  let t9;
  if ($[30] !== file.contentsOmitted || $[31] !== fileContentParams || $[32] !== newContent || $[33] !== number || $[34] !== oldContent || $[35] !== owner || $[36] !== queryClient || $[37] !== repo) {
    t9 = async () => {
      const hasLoadedTextContents = typeof oldContent === "string" || typeof newContent === "string";
      if (hasLoadedTextContents || file.contentsOmitted !== true || !fileContentParams) {
        return {
          oldFileContent: oldContent,
          newFileContent: newContent
        };
      }
      const fileContents = await ensurePrV2FileContent(queryClient, owner, repo, number, fileContentParams);
      return {
        oldFileContent: fileContents.oldContent,
        newFileContent: fileContents.newContent
      };
    };
    $[30] = file.contentsOmitted;
    $[31] = fileContentParams;
    $[32] = newContent;
    $[33] = number;
    $[34] = oldContent;
    $[35] = owner;
    $[36] = queryClient;
    $[37] = repo;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  const loadThreadAiPromptFileContents = t9;
  const hasImmediateThreadAiPromptFileContents = typeof oldContent === "string" || typeof newContent === "string" || file.contentsOmitted !== true || !fileContentParams;
  let t10;
  if ($[39] !== hasImmediateThreadAiPromptFileContents || $[40] !== loadThreadAiPromptFileContents || $[41] !== newContent || $[42] !== oldContent) {
    t10 = {
      oldFileContent: oldContent,
      newFileContent: newContent,
      loadFileContents: loadThreadAiPromptFileContents,
      hasImmediateFileContents: hasImmediateThreadAiPromptFileContents
    };
    $[39] = hasImmediateThreadAiPromptFileContents;
    $[40] = loadThreadAiPromptFileContents;
    $[41] = newContent;
    $[42] = oldContent;
    $[43] = t10;
  } else {
    t10 = $[43];
  }
  const threadAiPromptContext = t10;
  const hasTextContents = typeof oldContent === "string" || typeof newContent === "string";
  const sectionRef = useRef(null);
  const diffHostRef = useRef(null);
  let t11;
  if ($[44] !== filename || $[45] !== prDraftKey) {
    t11 = state => state.draftsByPr[prDraftKey]?.[filename];
    $[44] = filename;
    $[45] = prDraftKey;
    $[46] = t11;
  } else {
    t11 = $[46];
  }
  const draft = usePrV2DraftStore(t11);
  const toggleLineDraft = usePrV2DraftStore(_temp6);
  const setLineSelection = usePrV2DraftStore(_temp7);
  const setDraftComment = usePrV2DraftStore(_temp8);
  const clearDraftForFile = usePrV2DraftStore(_temp9);
  const pendingInlinePerfIdRef = useRef(null);
  const awaitingInlineFocusPerfIdRef = useRef(null);
  const ignoredSelectionRef = useRef(false);
  const inlinePerfSequenceRef = useRef(0);
  const draftComment = draft?.comment ?? "";
  const draftLineValue = draft?.line ?? "";
  const draftStartLineValue = draft?.startLine ?? "";
  const draftSide = draft?.side ?? "RIGHT";
  const draftStartSide = draft?.startSide ?? draftSide;
  const selectedLines = draft?.selectedLines ?? null;
  const isSelectionOnlyDraft = Boolean(draft?.selectionOnly);
  const t12 = draftLineValue || null;
  let t13;
  if ($[47] !== t12) {
    t13 = asPositiveInteger(t12);
    $[47] = t12;
    $[48] = t13;
  } else {
    t13 = $[48];
  }
  const draftLine = t13;
  const t14 = draftStartLineValue || null;
  let t15;
  if ($[49] !== t14) {
    t15 = asPositiveInteger(t14);
    $[49] = t14;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  const draftStartLine = t15;
  let t16;
  if ($[51] !== draftSide) {
    t16 = githubSideToAnnotationSide(draftSide);
    $[51] = draftSide;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  const draftAnnotationSide = t16;
  let t17;
  if ($[53] !== draftStartLine || $[54] !== draftStartSide) {
    t17 = draftStartLine ? githubSideToAnnotationSide(draftStartSide) : null;
    $[53] = draftStartLine;
    $[54] = draftStartSide;
    $[55] = t17;
  } else {
    t17 = $[55];
  }
  const draftStartAnnotationSide = t17;
  let t18;
  bb0: {
    if (!draftLine || isSelectionOnlyDraft) {
      t18 = null;
      break bb0;
    }
    let t19;
    if ($[56] !== draftAnnotationSide || $[57] !== draftLine || $[58] !== draftStartAnnotationSide || $[59] !== draftStartLine) {
      t19 = {
        lineNumber: draftLine,
        side: draftAnnotationSide,
        startLine: draftStartLine,
        startSide: draftStartAnnotationSide
      };
      $[56] = draftAnnotationSide;
      $[57] = draftLine;
      $[58] = draftStartAnnotationSide;
      $[59] = draftStartLine;
      $[60] = t19;
    } else {
      t19 = $[60];
    }
    t18 = t19;
  }
  const inlineDraftTarget = t18;
  const draftFocusKey = inlineDraftTarget ? `${inlineDraftTarget.lineNumber}:${inlineDraftTarget.side}:${inlineDraftTarget.startLine ?? ""}:${inlineDraftTarget.startSide ?? ""}` : `${draftLineValue}:${draftSide}:${draftStartLineValue}:${draftStartSide}`;
  const isFileLevelDraftOpen = Boolean(draft && !inlineDraftTarget && selectedLines == null);
  let t19;
  if ($[61] !== file.additions || $[62] !== file.deletions || $[63] !== file.status) {
    t19 = isPureRename(file.status, file.additions, file.deletions);
    $[61] = file.additions;
    $[62] = file.deletions;
    $[63] = file.status;
    $[64] = t19;
  } else {
    t19 = $[64];
  }
  const pureRename = t19;
  let t20;
  if ($[65] !== filename || $[66] !== oldContent) {
    t20 = createPierreFileContents({
      name: filename,
      contents: normalizeContentsForDiffRender(oldContent),
      role: "old"
    });
    $[65] = filename;
    $[66] = oldContent;
    $[67] = t20;
  } else {
    t20 = $[67];
  }
  const oldFileDescriptor = t20;
  let t21;
  if ($[68] !== filename || $[69] !== newContent) {
    t21 = createPierreFileContents({
      name: filename,
      contents: normalizeContentsForDiffRender(newContent),
      role: "new"
    });
    $[68] = filename;
    $[69] = newContent;
    $[70] = t21;
  } else {
    t21 = $[70];
  }
  const newFileDescriptor = t21;
  let t22;
  bb1: {
    if (!fileDiff) {
      t22 = null;
      break bb1;
    }
    let t23;
    if ($[71] !== fileDiff) {
      const keyed = ensurePierreFileDiffCacheKey(fileDiff);
      t23 = keyed.isPartial ? cloneFileDiffMetadata(keyed) : keyed;
      $[71] = fileDiff;
      $[72] = t23;
    } else {
      t23 = $[72];
    }
    t22 = t23;
  }
  const keyedFileDiff = t22;
  let t23;
  bb2: {
    if (!fileDiff || !fileContentParams) {
      t23 = undefined;
      break bb2;
    }
    let t24;
    if ($[73] !== fileContentParams || $[74] !== filename || $[75] !== number || $[76] !== owner || $[77] !== previousFilename || $[78] !== queryClient || $[79] !== repo) {
      t24 = async () => {
        const fileContents_0 = await ensurePrV2FileContent(queryClient, owner, repo, number, fileContentParams);
        if (fileContents_0.oldContent == null || fileContents_0.newContent == null) {
          throw new Error(`Full contents of ${filename} are unavailable`);
        }
        return {
          oldFile: createPierreFileContents({
            name: previousFilename ?? filename,
            contents: fileContents_0.oldContent,
            role: "old"
          }),
          newFile: createPierreFileContents({
            name: filename,
            contents: fileContents_0.newContent,
            role: "new"
          })
        };
      };
      $[73] = fileContentParams;
      $[74] = filename;
      $[75] = number;
      $[76] = owner;
      $[77] = previousFilename;
      $[78] = queryClient;
      $[79] = repo;
      $[80] = t24;
    } else {
      t24 = $[80];
    }
    t23 = t24;
  }
  const loadDiffFiles = t23;
  let t24;
  if ($[81] !== file.status || $[82] !== filename || $[83] !== newContent || $[84] !== oldContent || $[85] !== previousFilename) {
    t24 = resolvePrV2CodeViewSource({
      filename,
      previousFilename,
      status: file.status,
      oldContent,
      newContent
    });
    $[81] = file.status;
    $[82] = filename;
    $[83] = newContent;
    $[84] = oldContent;
    $[85] = previousFilename;
    $[86] = t24;
  } else {
    t24 = $[86];
  }
  const codeViewSource = t24;
  const canToggleCodeView = !pureRename && (hasTextContents || singleSidedPatchContent !== null || file.contentsOmitted === true && file.isBinaryFile !== true);
  const shouldRenderCodeView = isCodeView && canToggleCodeView;
  const renderedCodeView = shouldRenderCodeView ? codeViewSource : null;
  const isCodeViewContentLoading = shouldFetchOmittedContents && lazyFileContentQuery.isLoading;
  const codeViewSide = codeViewSource?.side ?? (file.status === "removed" ? "deletions" : "additions");
  const codeViewToggleLabel = codeViewSide === "deletions" ? "View full old file" : "View full new file";
  let t25;
  if ($[87] !== clearDraftForFile || $[88] !== filename || $[89] !== prDraftKey) {
    t25 = () => {
      pendingInlinePerfIdRef.current = null;
      awaitingInlineFocusPerfIdRef.current = null;
      clearDraftForFile(prDraftKey, filename);
    };
    $[87] = clearDraftForFile;
    $[88] = filename;
    $[89] = prDraftKey;
    $[90] = t25;
  } else {
    t25 = $[90];
  }
  const dismissDraft = t25;
  let t26;
  if ($[91] !== filename || $[92] !== keyedFileDiff || $[93] !== newContent || $[94] !== oldContent || $[95] !== onAskDraftInChat || $[96] !== patch) {
    t26 = onAskDraftInChat ? () => onAskDraftInChat(filename, {
      patch,
      oldContent: oldContent ?? readFullFileFromDiff(keyedFileDiff, "old"),
      newContent: newContent ?? readFullFileFromDiff(keyedFileDiff, "new")
    }) : undefined;
    $[91] = filename;
    $[92] = keyedFileDiff;
    $[93] = newContent;
    $[94] = oldContent;
    $[95] = onAskDraftInChat;
    $[96] = patch;
    $[97] = t26;
  } else {
    t26 = $[97];
  }
  const handleAskDraftInChat = t26;
  let t27;
  if ($[98] !== commentsByFile || $[99] !== filename) {
    t27 = commentsByFile.get(filename) ?? EMPTY_COMMENTS;
    $[98] = commentsByFile;
    $[99] = filename;
    $[100] = t27;
  } else {
    t27 = $[100];
  }
  const unsortedFileComments = t27;
  let t28;
  if ($[101] !== reviewThreadByCommentNodeId || $[102] !== unsortedFileComments) {
    t28 = getFileReviewCommentPreviews({
      comments: unsortedFileComments,
      reviewThreadByCommentNodeId,
      getCommentNodeId: extractCommentNodeId
    });
    $[101] = reviewThreadByCommentNodeId;
    $[102] = unsortedFileComments;
    $[103] = t28;
  } else {
    t28 = $[103];
  }
  const fileCommentPreviews = t28;
  let t29;
  bb3: {
    if (fileIsCollapsed) {
      t29 = EMPTY_COMMENTS;
      break bb3;
    }
    let t30;
    if ($[104] !== unsortedFileComments) {
      t30 = [...unsortedFileComments].sort(_temp10);
      $[104] = unsortedFileComments;
      $[105] = t30;
    } else {
      t30 = $[105];
    }
    t29 = t30;
  }
  const fileComments = t29;
  let t30;
  if ($[106] !== fileComments || $[107] !== inlineAnchorByCommentId) {
    let t31;
    if ($[109] !== inlineAnchorByCommentId) {
      t31 = comment => inlineAnchorByCommentId.has(comment.githubCommentId);
      $[109] = inlineAnchorByCommentId;
      $[110] = t31;
    } else {
      t31 = $[110];
    }
    t30 = fileComments.filter(t31);
    $[106] = fileComments;
    $[107] = inlineAnchorByCommentId;
    $[108] = t30;
  } else {
    t30 = $[108];
  }
  const inlineComments = t30;
  let t31;
  if ($[111] !== fileComments || $[112] !== inlineAnchorByCommentId || $[113] !== reviewThreadContextByCommentNodeId) {
    let t32;
    if ($[115] !== inlineAnchorByCommentId || $[116] !== reviewThreadContextByCommentNodeId) {
      t32 = comment_0 => {
        if (inlineAnchorByCommentId.has(comment_0.githubCommentId)) {
          return false;
        }
        const nodeId = extractCommentNodeId(comment_0);
        const context = nodeId ? reviewThreadContextByCommentNodeId.get(nodeId) : undefined;
        return isFileLevelComment(comment_0, context);
      };
      $[115] = inlineAnchorByCommentId;
      $[116] = reviewThreadContextByCommentNodeId;
      $[117] = t32;
    } else {
      t32 = $[117];
    }
    t31 = fileComments.filter(t32);
    $[111] = fileComments;
    $[112] = inlineAnchorByCommentId;
    $[113] = reviewThreadContextByCommentNodeId;
    $[114] = t31;
  } else {
    t31 = $[114];
  }
  const fileLevelComments = t31;
  let t32;
  bb4: {
    if (fileIsCollapsed) {
      t32 = EMPTY_GROUPED_FILE_LEVEL_THREADS;
      break bb4;
    }
    let t33;
    if ($[118] !== fileLevelComments || $[119] !== readModelCommentByGithubId || $[120] !== reviewThreadByCommentNodeId) {
      const groupedFileLevelThreadMap = new Map();
      fileLevelComments.forEach(comment_1 => {
        const nodeId_0 = extractCommentNodeId(comment_1);
        const thread = nodeId_0 ? reviewThreadByCommentNodeId.get(nodeId_0) : undefined;
        const threadDraftKey = readModelCommentByGithubId.get(comment_1.githubCommentId)?.threadId ?? `comment:${comment_1.githubCommentId}`;
        const key = threadDraftKey;
        const existing = groupedFileLevelThreadMap.get(key);
        if (existing) {
          existing.comments.push(comment_1);
          return;
        }
        groupedFileLevelThreadMap.set(key, {
          key,
          threadDraftKey,
          thread,
          comments: [comment_1]
        });
      });
      t33 = Array.from(groupedFileLevelThreadMap.values()).map(_temp12);
      $[118] = fileLevelComments;
      $[119] = readModelCommentByGithubId;
      $[120] = reviewThreadByCommentNodeId;
      $[121] = t33;
    } else {
      t33 = $[121];
    }
    t32 = t33;
  }
  const groupedFileLevelThreads = t32;
  let t33;
  bb5: {
    if (fileIsCollapsed) {
      t33 = EMPTY_LINE_ANNOTATIONS;
      break bb5;
    }
    let t34;
    if ($[122] !== filename || $[123] !== inlineAnchorByCommentId || $[124] !== inlineComments || $[125] !== readModelCommentByGithubId || $[126] !== reviewThreadByCommentNodeId) {
      const annotationBuckets = new Map();
      inlineComments.forEach(comment_2 => {
        const anchor = inlineAnchorByCommentId.get(comment_2.githubCommentId);
        if (!anchor) {
          return;
        }
        const nodeId_1 = extractCommentNodeId(comment_2);
        const thread_0 = nodeId_1 ? reviewThreadByCommentNodeId.get(nodeId_1) : undefined;
        const threadDraftKey_0 = readModelCommentByGithubId.get(comment_2.githubCommentId)?.threadId ?? `comment:${comment_2.githubCommentId}`;
        const key_0 = `${anchor.side}:${anchor.lineNumber}:${threadDraftKey_0}`;
        const bucket = annotationBuckets.get(key_0);
        if (bucket) {
          bucket.comments.push(comment_2);
          if (!bucket.thread && thread_0) {
            bucket.thread = thread_0;
          }
          return;
        }
        annotationBuckets.set(key_0, {
          kind: "comments",
          filePath: filename,
          side: anchor.side,
          lineNumber: anchor.lineNumber,
          thread: thread_0,
          threadDraftKey: threadDraftKey_0,
          comments: [comment_2]
        });
      });
      t34 = Array.from(annotationBuckets.values()).sort(_temp13).map(_temp14);
      $[122] = filename;
      $[123] = inlineAnchorByCommentId;
      $[124] = inlineComments;
      $[125] = readModelCommentByGithubId;
      $[126] = reviewThreadByCommentNodeId;
      $[127] = t34;
    } else {
      t34 = $[127];
    }
    t33 = t34;
  }
  const baseLineAnnotations = t33;
  const shouldRenderDecisionGutterMarkers = !fileIsCollapsed && (!shouldRenderCodeView || codeViewSide === "additions");
  let t34;
  if ($[128] !== fileReviewDecisions || $[129] !== shouldRenderDecisionGutterMarkers) {
    t34 = shouldRenderDecisionGutterMarkers ? groupAnchoredDecisionsByLine(fileReviewDecisions) : [];
    $[128] = fileReviewDecisions;
    $[129] = shouldRenderDecisionGutterMarkers;
    $[130] = t34;
  } else {
    t34 = $[130];
  }
  const decisionLineGroups = t34;
  let t35;
  bb6: {
    if (!inlineDraftTarget) {
      t35 = baseLineAnnotations;
      break bb6;
    }
    let t36;
    if ($[131] !== filename || $[132] !== inlineDraftTarget.lineNumber || $[133] !== inlineDraftTarget.side || $[134] !== inlineDraftTarget.startLine || $[135] !== inlineDraftTarget.startSide) {
      t36 = {
        kind: "draft",
        filePath: filename,
        side: inlineDraftTarget.side,
        lineNumber: inlineDraftTarget.lineNumber,
        startLine: inlineDraftTarget.startLine,
        startSide: inlineDraftTarget.startSide
      };
      $[131] = filename;
      $[132] = inlineDraftTarget.lineNumber;
      $[133] = inlineDraftTarget.side;
      $[134] = inlineDraftTarget.startLine;
      $[135] = inlineDraftTarget.startSide;
      $[136] = t36;
    } else {
      t36 = $[136];
    }
    const t37 = t36 satisfies PrV2DiffDraftAnnotation;
    let t38;
    if ($[137] !== inlineDraftTarget.lineNumber || $[138] !== inlineDraftTarget.side || $[139] !== t37) {
      t38 = {
        side: inlineDraftTarget.side,
        lineNumber: inlineDraftTarget.lineNumber,
        metadata: t37
      };
      $[137] = inlineDraftTarget.lineNumber;
      $[138] = inlineDraftTarget.side;
      $[139] = t37;
      $[140] = t38;
    } else {
      t38 = $[140];
    }
    const t39 = t38 satisfies DiffLineAnnotation<PrV2DiffCommentAnnotationMetadata>;
    let t40;
    if ($[141] !== baseLineAnnotations || $[142] !== t39) {
      t40 = [...baseLineAnnotations, t39];
      $[141] = baseLineAnnotations;
      $[142] = t39;
      $[143] = t40;
    } else {
      t40 = $[143];
    }
    t35 = t40;
  }
  const lineAnnotations = t35;
  let t36;
  bb7: {
    if (!shouldRenderCodeView) {
      t36 = EMPTY_CODE_LINE_ANNOTATIONS;
      break bb7;
    }
    let annotations;
    if ($[144] !== codeViewSide || $[145] !== lineAnnotations) {
      annotations = [];
      lineAnnotations.forEach(annotation => {
        if (annotation.side !== codeViewSide) {
          return;
        }
        if (annotation.metadata.kind === "draft") {
          annotations.push({
            lineNumber: annotation.lineNumber,
            metadata: annotation.metadata
          } satisfies LineAnnotation<PrV2DiffDraftAnnotation>);
          return;
        }
        annotations.push({
          lineNumber: annotation.lineNumber,
          metadata: annotation.metadata
        } satisfies LineAnnotation<PrV2DiffCommentThreadAnnotation>);
      });
      $[144] = codeViewSide;
      $[145] = lineAnnotations;
      $[146] = annotations;
    } else {
      annotations = $[146];
    }
    t36 = annotations.length > 0 ? annotations : EMPTY_CODE_LINE_ANNOTATIONS;
  }
  const codeLineAnnotations = t36;
  let t37;
  bb8: {
    if (!selectedLines || !shouldRenderCodeView) {
      t37 = null;
      break bb8;
    }
    const selectedSide = selectedLines.side ?? codeViewSide;
    const selectedEndSide = selectedLines.endSide ?? selectedSide;
    if (selectedSide !== codeViewSide || selectedEndSide !== codeViewSide) {
      t37 = null;
      break bb8;
    }
    let t38;
    if ($[147] !== selectedLines.end || $[148] !== selectedLines.start) {
      t38 = {
        start: selectedLines.start,
        end: selectedLines.end
      };
      $[147] = selectedLines.end;
      $[148] = selectedLines.start;
      $[149] = t38;
    } else {
      t38 = $[149];
    }
    t37 = t38;
  }
  const codeSelectedLines = t37;
  let t38;
  if ($[150] !== fileDiff || $[151] !== hasTextContents || $[152] !== newFileDescriptor || $[153] !== oldFileDescriptor) {
    t38 = (annotationSide, lineNumber) => {
      let hunkFileDiff = fileDiff;
      if (!hunkFileDiff && hasTextContents) {
        try {
          hunkFileDiff = parseDiffFromFile(oldFileDescriptor, newFileDescriptor);
        } catch {
          hunkFileDiff = undefined;
        }
      }
      return isLineInDiffHunks(hunkFileDiff, annotationSide, lineNumber);
    };
    $[150] = fileDiff;
    $[151] = hasTextContents;
    $[152] = newFileDescriptor;
    $[153] = oldFileDescriptor;
    $[154] = t38;
  } else {
    t38 = $[154];
  }
  const isCommentableLine = t38;
  let t39;
  if ($[155] !== draftAnnotationSide || $[156] !== draftLine || $[157] !== filename || $[158] !== isCommentableLine || $[159] !== isReadOnly || $[160] !== prDraftKey || $[161] !== toggleLineDraft) {
    t39 = t40 => {
      const {
        lineNumber: lineNumber_0,
        annotationSide: annotationSide_0
      } = t40;
      if (isReadOnly || !isCommentableLine(annotationSide_0, lineNumber_0)) {
        return;
      }
      const isSameInlineTarget = draftLine === lineNumber_0 && draftAnnotationSide === annotationSide_0;
      if (isSameInlineTarget) {
        pendingInlinePerfIdRef.current = null;
        awaitingInlineFocusPerfIdRef.current = null;
        toggleLineDraft(prDraftKey, filename, lineNumber_0, annotationSideToGithubSide(annotationSide_0));
        return;
      }
      const perfId = `${filename}:${lineNumber_0}:${annotationSide_0}:${inlinePerfSequenceRef.current = inlinePerfSequenceRef.current + 1}`;
      pendingInlinePerfIdRef.current = perfId;
      awaitingInlineFocusPerfIdRef.current = null;
      markPrV2InlineCommentPerf(perfId, "click");
      toggleLineDraft(prDraftKey, filename, lineNumber_0, annotationSideToGithubSide(annotationSide_0));
    };
    $[155] = draftAnnotationSide;
    $[156] = draftLine;
    $[157] = filename;
    $[158] = isCommentableLine;
    $[159] = isReadOnly;
    $[160] = prDraftKey;
    $[161] = toggleLineDraft;
    $[162] = t39;
  } else {
    t39 = $[162];
  }
  const openOrToggleInlineDraft = t39;
  let t40;
  if ($[163] !== openOrToggleInlineDraft) {
    t40 = t41 => {
      const {
        lineNumber: lineNumber_1,
        annotationSide: annotationSide_1
      } = t41;
      openOrToggleInlineDraft({
        lineNumber: lineNumber_1,
        annotationSide: annotationSide_1
      });
    };
    $[163] = openOrToggleInlineDraft;
    $[164] = t40;
  } else {
    t40 = $[164];
  }
  const handleLineClick = t40;
  let t41;
  if ($[165] !== openOrToggleInlineDraft) {
    t41 = range => {
      openOrToggleInlineDraft({
        lineNumber: range.start,
        annotationSide: range.side ?? "additions"
      });
    };
    $[165] = openOrToggleInlineDraft;
    $[166] = t41;
  } else {
    t41 = $[166];
  }
  const handleAddCommentButtonClick = t41;
  let t42;
  if ($[167] !== draftAnnotationSide || $[168] !== draftLine || $[169] !== filename || $[170] !== isCommentableLine || $[171] !== isReadOnly || $[172] !== openOrToggleInlineDraft || $[173] !== prDraftKey || $[174] !== setLineSelection) {
    t42 = range_0 => {
      if (isReadOnly) {
        return;
      }
      if (range_0 && range_0.start === range_0.end && !isCommentableLine(range_0.side ?? "additions", range_0.start)) {
        ignoredSelectionRef.current = true;
        return;
      }
      const followsIgnoredSelection = ignoredSelectionRef.current;
      ignoredSelectionRef.current = false;
      if (!range_0 && followsIgnoredSelection) {
        return;
      }
      setLineSelection(prDraftKey, filename, range_0);
      pendingInlinePerfIdRef.current = null;
      awaitingInlineFocusPerfIdRef.current = null;
      if (range_0 && range_0.start === range_0.end) {
        const annotationSide_2 = range_0.side ?? "additions";
        openOrToggleInlineDraft({
          lineNumber: range_0.start,
          annotationSide: annotationSide_2
        });
      } else {
        if (range_0) {
          const selectedDraft = usePrV2DraftStore.getState().getDraft(prDraftKey, filename);
          const selectedLine = asPositiveInteger(selectedDraft?.line);
          const selectedSide_0 = selectedDraft?.side;
          if (selectedLine != null && selectedSide_0 != null) {
            openOrToggleInlineDraft({
              lineNumber: selectedLine,
              annotationSide: githubSideToAnnotationSide(selectedSide_0)
            });
          }
        } else {
          if (!range_0 && draftLine != null && draftAnnotationSide != null) {
            openOrToggleInlineDraft({
              lineNumber: draftLine,
              annotationSide: draftAnnotationSide
            });
          }
        }
      }
    };
    $[167] = draftAnnotationSide;
    $[168] = draftLine;
    $[169] = filename;
    $[170] = isCommentableLine;
    $[171] = isReadOnly;
    $[172] = openOrToggleInlineDraft;
    $[173] = prDraftKey;
    $[174] = setLineSelection;
    $[175] = t42;
  } else {
    t42 = $[175];
  }
  const handleLineSelectionEnd = t42;
  const resolvedHunkSeparators = file.status === "added" || file.status === "removed" ? "simple" : diffOptions.hunkSeparators;
  const t43 = !isReadOnly;
  const t44 = isReadOnly ? undefined : handleLineClick;
  const t45 = isReadOnly ? undefined : handleLineSelectionEnd;
  const t46 = isReadOnly ? undefined : handleAddCommentButtonClick;
  let t47;
  if ($[176] !== diffOptions || $[177] !== loadDiffFiles || $[178] !== resolvedHunkSeparators || $[179] !== t43 || $[180] !== t44 || $[181] !== t45 || $[182] !== t46) {
    t47 = {
      ...diffOptions,
      hunkSeparators: resolvedHunkSeparators,
      enableLineSelection: t43,
      onLineNumberClick: t44,
      onLineSelectionEnd: t45,
      onGutterUtilityClick: t46,
      loadDiffFiles
    };
    $[176] = diffOptions;
    $[177] = loadDiffFiles;
    $[178] = resolvedHunkSeparators;
    $[179] = t43;
    $[180] = t44;
    $[181] = t45;
    $[182] = t46;
    $[183] = t47;
  } else {
    t47 = $[183];
  }
  const diffOptionsForFile = t47;
  let t48;
  if ($[184] !== codeViewSide || $[185] !== openOrToggleInlineDraft) {
    t48 = t49 => {
      const {
        lineNumber: lineNumber_2
      } = t49;
      openOrToggleInlineDraft({
        lineNumber: lineNumber_2,
        annotationSide: codeViewSide
      });
    };
    $[184] = codeViewSide;
    $[185] = openOrToggleInlineDraft;
    $[186] = t48;
  } else {
    t48 = $[186];
  }
  const handleCodeLineClick = t48;
  let t49;
  if ($[187] !== codeViewSide || $[188] !== handleLineSelectionEnd) {
    t49 = range_1 => {
      if (!range_1) {
        handleLineSelectionEnd(null);
        return;
      }
      handleLineSelectionEnd({
        ...range_1,
        side: codeViewSide,
        endSide: codeViewSide
      });
    };
    $[187] = codeViewSide;
    $[188] = handleLineSelectionEnd;
    $[189] = t49;
  } else {
    t49 = $[189];
  }
  const handleCodeLineSelectionEnd = t49;
  let t50;
  if ($[190] !== codeViewSide || $[191] !== openOrToggleInlineDraft) {
    t50 = range_2 => {
      openOrToggleInlineDraft({
        lineNumber: range_2.start,
        annotationSide: codeViewSide
      });
    };
    $[190] = codeViewSide;
    $[191] = openOrToggleInlineDraft;
    $[192] = t50;
  } else {
    t50 = $[192];
  }
  const handleCodeGutterUtilityClick = t50;
  const t51 = !isReadOnly;
  const t52 = isReadOnly ? undefined : handleCodeLineClick;
  const t53 = isReadOnly ? undefined : handleCodeLineSelectionEnd;
  const t54 = isReadOnly ? undefined : handleCodeGutterUtilityClick;
  let t55;
  if ($[193] !== diffOptions.disableLineNumbers || $[194] !== diffOptions.overflow || $[195] !== diffOptions.theme || $[196] !== diffOptions.themeType || $[197] !== diffOptions.unsafeCSS || $[198] !== t51 || $[199] !== t52 || $[200] !== t53 || $[201] !== t54) {
    t55 = {
      disableFileHeader: true,
      theme: diffOptions.theme,
      themeType: diffOptions.themeType,
      overflow: diffOptions.overflow,
      disableLineNumbers: diffOptions.disableLineNumbers,
      unsafeCSS: diffOptions.unsafeCSS,
      enableGutterUtility: true,
      enableLineSelection: t51,
      onLineNumberClick: t52,
      onLineSelectionEnd: t53,
      onGutterUtilityClick: t54
    };
    $[193] = diffOptions.disableLineNumbers;
    $[194] = diffOptions.overflow;
    $[195] = diffOptions.theme;
    $[196] = diffOptions.themeType;
    $[197] = diffOptions.unsafeCSS;
    $[198] = t51;
    $[199] = t52;
    $[200] = t53;
    $[201] = t54;
    $[202] = t55;
  } else {
    t55 = $[202];
  }
  const codeOptionsForFile = t55;
  let t56;
  let t57;
  if ($[203] !== decisionLineGroups || $[204] !== fileIsCollapsed || $[205] !== filename || $[206] !== onDecisionMarkerClick || $[207] !== onDecisionMarkerHover) {
    t56 = () => {
      if (fileIsCollapsed) {
        return;
      }
      const diffHost = diffHostRef.current;
      if (!diffHost) {
        return;
      }
      let syncedShadowRoot = null;
      const syncShadowRoot = shadowRoot => {
        if (syncedShadowRoot && syncedShadowRoot !== shadowRoot) {
          disposePrV2DecisionGutterMarkers(syncedShadowRoot);
        }
        syncedShadowRoot = shadowRoot;
        syncPrV2DecisionGutterMarkers({
          shadowRoot,
          filename,
          groups: decisionLineGroups,
          onFirstHover: onDecisionMarkerHover,
          onClick: onDecisionMarkerClick
        });
      };
      const stopObservingDiffHost = observePrV2DiffHost(diffHost, syncShadowRoot);
      return () => {
        stopObservingDiffHost();
        if (syncedShadowRoot) {
          disposePrV2DecisionGutterMarkers(syncedShadowRoot);
        }
      };
    };
    t57 = [decisionLineGroups, fileIsCollapsed, filename, onDecisionMarkerClick, onDecisionMarkerHover];
    $[203] = decisionLineGroups;
    $[204] = fileIsCollapsed;
    $[205] = filename;
    $[206] = onDecisionMarkerClick;
    $[207] = onDecisionMarkerHover;
    $[208] = t56;
    $[209] = t57;
  } else {
    t56 = $[208];
    t57 = $[209];
  }
  useEffect(t56, t57);
  let t58;
  if ($[210] !== newContent || $[211] !== oldContent) {
    t58 = {
      base: oldContent,
      head: newContent
    };
    $[210] = newContent;
    $[211] = oldContent;
    $[212] = t58;
  } else {
    t58 = $[212];
  }
  const t59 = renderedCodeView?.side ?? null;
  let t60;
  if ($[213] !== baseSha || $[214] !== changedFiles || $[215] !== filename || $[216] !== headSha || $[217] !== number || $[218] !== owner || $[219] !== previousFilename || $[220] !== repo || $[221] !== t58 || $[222] !== t59) {
    t60 = {
      owner,
      repo,
      number,
      headSha,
      baseSha,
      changedFiles,
      fileTexts: t58,
      filename,
      previousFilename,
      codeViewSide: t59
    };
    $[213] = baseSha;
    $[214] = changedFiles;
    $[215] = filename;
    $[216] = headSha;
    $[217] = number;
    $[218] = owner;
    $[219] = previousFilename;
    $[220] = repo;
    $[221] = t58;
    $[222] = t59;
    $[223] = t60;
  } else {
    t60 = $[223];
  }
  usePrV2RepoFileLinks(sectionRef, t60);
  let t61;
  if ($[224] === Symbol.for("react.memo_cache_sentinel")) {
    t61 = () => {
      const pendingPerfId = pendingInlinePerfIdRef.current;
      if (!pendingPerfId) {
        return;
      }
      markPrV2InlineCommentPerf(pendingPerfId, "visible");
      measurePrV2InlineCommentPerf(pendingPerfId, "visible");
      awaitingInlineFocusPerfIdRef.current = pendingPerfId;
      pendingInlinePerfIdRef.current = null;
    };
    $[224] = t61;
  } else {
    t61 = $[224];
  }
  const handleInlineDraftComposerVisible = t61;
  let t62;
  if ($[225] === Symbol.for("react.memo_cache_sentinel")) {
    t62 = () => {
      const perfId_0 = awaitingInlineFocusPerfIdRef.current;
      if (!perfId_0) {
        return;
      }
      markPrV2InlineCommentPerf(perfId_0, "focused");
      measurePrV2InlineCommentPerf(perfId_0, "focused");
      awaitingInlineFocusPerfIdRef.current = null;
    };
    $[225] = t62;
  } else {
    t62 = $[225];
  }
  const handleInlineDraftComposerFocused = t62;
  const isSplitDiffStyle = diffOptions.diffStyle === "split";
  let t63;
  if ($[226] !== currentUserAvatarUrl || $[227] !== currentUserLogin || $[228] !== disablePostImmediately || $[229] !== dismissDraft || $[230] !== draftFocusKey || $[231] !== filename || $[232] !== handleAskDraftInChat || $[233] !== isMutating || $[234] !== isReadOnly || $[235] !== isSplitDiffStyle || $[236] !== mutationState || $[237] !== number || $[238] !== onApplySuggestion || $[239] !== onDeleteComment || $[240] !== onDeletePendingComment || $[241] !== onEditComment || $[242] !== onEditPendingComment || $[243] !== onPostFileComment || $[244] !== onReplySubmit || $[245] !== onResolveToggle || $[246] !== owner || $[247] !== prDraftKey || $[248] !== prReplyDraftKey || $[249] !== reactions || $[250] !== repo || $[251] !== severityByCommentId || $[252] !== suggestionApplyDisabledReason || $[253] !== threadAiPromptContext) {
    t63 = annotation_0 => {
      const metadata = annotation_0.metadata;
      if (!metadata) {
        return null;
      }
      if (metadata.kind === "draft") {
        if (isReadOnly) {
          return null;
        }
        return <PrV2InlineDraftComposer prDraftKey={prDraftKey} filename={filename} currentUserLogin={currentUserLogin} currentUserAvatarUrl={currentUserAvatarUrl} isMutating={isMutating} isSplitDiffStyle={isSplitDiffStyle} disablePostImmediately={disablePostImmediately} focusKey={draftFocusKey} onVisible={handleInlineDraftComposerVisible} onFocused={handleInlineDraftComposerFocused} onCancel={dismissDraft} onAddToReview={() => onPostFileComment(filename, "batch")} onSubmitImmediately={() => onPostFileComment(filename, "immediate")} onAskAi={handleAskDraftInChat} />;
      }
      return <PrV2ThreadCardWithReplyDraft owner={owner} repo={repo} pullNumber={number} isReadOnly={isReadOnly} comments={metadata.comments} thread={metadata.thread} severityByCommentId={severityByCommentId} prReplyDraftKey={prReplyDraftKey} replyDraftKey={metadata.threadDraftKey} anchorKind="inline" layout="inline" isSplitDiffStyle={isSplitDiffStyle} resolvedPresentation="hybrid" reactionAffordancePlacement="hover-strip" isMutating={mutationState.any} mutationState={mutationState} onReplySubmit={onReplySubmit} onResolveToggle={onResolveToggle} onEditComment={onEditComment} onDeleteComment={onDeleteComment} onEditPendingComment={onEditPendingComment} onDeletePendingComment={onDeletePendingComment} onApplySuggestion={onApplySuggestion} suggestionApplyDisabledReason={suggestionApplyDisabledReason} reactions={reactions} overflowUtilityActions={true} aiPromptContext={threadAiPromptContext} />;
    };
    $[226] = currentUserAvatarUrl;
    $[227] = currentUserLogin;
    $[228] = disablePostImmediately;
    $[229] = dismissDraft;
    $[230] = draftFocusKey;
    $[231] = filename;
    $[232] = handleAskDraftInChat;
    $[233] = isMutating;
    $[234] = isReadOnly;
    $[235] = isSplitDiffStyle;
    $[236] = mutationState;
    $[237] = number;
    $[238] = onApplySuggestion;
    $[239] = onDeleteComment;
    $[240] = onDeletePendingComment;
    $[241] = onEditComment;
    $[242] = onEditPendingComment;
    $[243] = onPostFileComment;
    $[244] = onReplySubmit;
    $[245] = onResolveToggle;
    $[246] = owner;
    $[247] = prDraftKey;
    $[248] = prReplyDraftKey;
    $[249] = reactions;
    $[250] = repo;
    $[251] = severityByCommentId;
    $[252] = suggestionApplyDisabledReason;
    $[253] = threadAiPromptContext;
    $[254] = t63;
  } else {
    t63 = $[254];
  }
  const renderAnnotation = t63;
  let t64;
  if ($[255] !== fileIsCollapsed || $[256] !== filename || $[257] !== isFileLevelDraftOpen || $[258] !== isReadOnly || $[259] !== onToggleCollapsed || $[260] !== onToggleFileDraft) {
    t64 = () => {
      if (isReadOnly) {
        return;
      }
      pendingInlinePerfIdRef.current = null;
      awaitingInlineFocusPerfIdRef.current = null;
      if (isFileLevelDraftOpen) {
        onToggleFileDraft(filename);
        return;
      }
      if (fileIsCollapsed) {
        onToggleCollapsed(filename, fileIsCollapsed);
      }
      if (typeof window === "undefined") {
        onToggleFileDraft(filename);
        return;
      }
      const scheduleToggle = window.requestAnimationFrame ? window.requestAnimationFrame.bind(window) : _temp15;
      scheduleToggle(() => {
        onToggleFileDraft(filename);
      });
    };
    $[255] = fileIsCollapsed;
    $[256] = filename;
    $[257] = isFileLevelDraftOpen;
    $[258] = isReadOnly;
    $[259] = onToggleCollapsed;
    $[260] = onToggleFileDraft;
    $[261] = t64;
  } else {
    t64 = $[261];
  }
  const handleFileDraftToggleClick = t64;
  let t65;
  if ($[262] !== canToggleCodeView || $[263] !== fileIsCollapsed || $[264] !== filename || $[265] !== isCodeView || $[266] !== onToggleCodeView || $[267] !== onToggleCollapsed) {
    t65 = () => {
      if (!canToggleCodeView) {
        return;
      }
      onToggleCodeView(filename);
      if (!isCodeView && fileIsCollapsed) {
        onToggleCollapsed(filename, fileIsCollapsed);
      }
    };
    $[262] = canToggleCodeView;
    $[263] = fileIsCollapsed;
    $[264] = filename;
    $[265] = isCodeView;
    $[266] = onToggleCodeView;
    $[267] = onToggleCollapsed;
    $[268] = t65;
  } else {
    t65 = $[268];
  }
  const handleCodeViewToggleClick = t65;
  const showFileBody = !fileIsCollapsed && (!pureRename || isFileLevelDraftOpen || groupedFileLevelThreads.length > 0);
  let t66;
  if ($[269] !== number || $[270] !== owner || $[271] !== repo) {
    t66 = {
      owner,
      repo,
      number
    };
    $[269] = number;
    $[270] = owner;
    $[271] = repo;
    $[272] = t66;
  } else {
    t66 = $[272];
  }
  const fileHeaderPullRequest = t66;
  let t67;
  if ($[273] !== canToggleCodeView || $[274] !== codeViewToggleLabel || $[275] !== fileIsChangedSinceLastView || $[276] !== fileIsCollapsed || $[277] !== fileIsViewed || $[278] !== isActiveFile || $[279] !== isCodeView || $[280] !== isFileLevelDraftOpen) {
    t67 = {
      isActiveFile,
      isViewed: fileIsViewed,
      isChangedSinceLastView: fileIsChangedSinceLastView,
      isCollapsed: fileIsCollapsed,
      isFileLevelDraftOpen,
      isCodeView,
      canToggleCodeView,
      codeViewToggleLabel
    };
    $[273] = canToggleCodeView;
    $[274] = codeViewToggleLabel;
    $[275] = fileIsChangedSinceLastView;
    $[276] = fileIsCollapsed;
    $[277] = fileIsViewed;
    $[278] = isActiveFile;
    $[279] = isCodeView;
    $[280] = isFileLevelDraftOpen;
    $[281] = t67;
  } else {
    t67 = $[281];
  }
  const fileHeaderState = t67;
  const t68 = Boolean(headSha) && !isMutating;
  let t69;
  if ($[282] !== isReadOnly || $[283] !== t68) {
    t69 = {
      isReadOnly,
      canComment: t68
    };
    $[282] = isReadOnly;
    $[283] = t68;
    $[284] = t69;
  } else {
    t69 = $[284];
  }
  const fileHeaderPermissions = t69;
  let t70;
  if ($[285] !== fileIsCollapsed || $[286] !== filename || $[287] !== onToggleCollapsed) {
    t70 = () => onToggleCollapsed(filename, fileIsCollapsed);
    $[285] = fileIsCollapsed;
    $[286] = filename;
    $[287] = onToggleCollapsed;
    $[288] = t70;
  } else {
    t70 = $[288];
  }
  let t71;
  if ($[289] !== fileIsViewed || $[290] !== filename || $[291] !== onToggleViewed) {
    t71 = () => onToggleViewed(filename, fileIsViewed);
    $[289] = fileIsViewed;
    $[290] = filename;
    $[291] = onToggleViewed;
    $[292] = t71;
  } else {
    t71 = $[292];
  }
  let t72;
  if ($[293] !== handleCodeViewToggleClick || $[294] !== handleFileDraftToggleClick || $[295] !== t70 || $[296] !== t71) {
    t72 = {
      onToggleCollapsed: t70,
      onToggleViewed: t71,
      onToggleFileDraft: handleFileDraftToggleClick,
      onToggleCodeView: handleCodeViewToggleClick
    };
    $[293] = handleCodeViewToggleClick;
    $[294] = handleFileDraftToggleClick;
    $[295] = t70;
    $[296] = t71;
    $[297] = t72;
  } else {
    t72 = $[297];
  }
  const fileHeaderActions = t72;
  let t73;
  if ($[298] !== filename) {
    t73 = getPrV2FileElementId(filename);
    $[298] = filename;
    $[299] = t73;
  } else {
    t73 = $[299];
  }
  let t74;
  if ($[300] !== filename) {
    t74 = {
      [DIFF_FILENAME_ATTRIBUTE]: filename
    };
    $[300] = filename;
    $[301] = t74;
  } else {
    t74 = $[301];
  }
  const t75 = !fileIsCollapsed && "[content-visibility:auto] [contain-intrinsic-size:auto_600px]";
  const t76 = isActiveFile && "border-white/35 ring-1 ring-white/10";
  let t77;
  if ($[302] !== t75 || $[303] !== t76) {
    t77 = cn("rounded-md border border-zinc-800 overflow-clip", t75, t76);
    $[302] = t75;
    $[303] = t76;
    $[304] = t77;
  } else {
    t77 = $[304];
  }
  let t78;
  if ($[305] !== baseSha || $[306] !== file || $[307] !== fileCommentPreviews || $[308] !== fileHeaderActions || $[309] !== fileHeaderPermissions || $[310] !== fileHeaderPullRequest || $[311] !== fileHeaderState || $[312] !== headSha || $[313] !== showFileBody) {
    t78 = <PrV2FileHeader pullRequest={fileHeaderPullRequest} file={file} headSha={headSha} baseSha={baseSha} commentPreviews={fileCommentPreviews} showFileBody={showFileBody} state={fileHeaderState} permissions={fileHeaderPermissions} actions={fileHeaderActions} />;
    $[305] = baseSha;
    $[306] = file;
    $[307] = fileCommentPreviews;
    $[308] = fileHeaderActions;
    $[309] = fileHeaderPermissions;
    $[310] = fileHeaderPullRequest;
    $[311] = fileHeaderState;
    $[312] = headSha;
    $[313] = showFileBody;
    $[314] = t78;
  } else {
    t78 = $[314];
  }
  let t79;
  if ($[315] !== codeLineAnnotations || $[316] !== codeOptionsForFile || $[317] !== codeSelectedLines || $[318] !== currentUserAvatarUrl || $[319] !== currentUserLogin || $[320] !== diffOptions.overflow || $[321] !== diffOptionsForFile || $[322] !== diffRenderKey || $[323] !== disablePostImmediately || $[324] !== dismissDraft || $[325] !== draftComment || $[326] !== file.isLargeFile || $[327] !== filename || $[328] !== groupedFileLevelThreads || $[329] !== handleAskDraftInChat || $[330] !== hasTextContents || $[331] !== isCodeViewContentLoading || $[332] !== isFileLevelDraftOpen || $[333] !== isMutating || $[334] !== isReadOnly || $[335] !== isSplitDiffStyle || $[336] !== keyedFileDiff || $[337] !== lineAnnotations || $[338] !== mutationState || $[339] !== newFileDescriptor || $[340] !== normalizedPatch || $[341] !== number || $[342] !== oldFileDescriptor || $[343] !== onApplySuggestion || $[344] !== onDeleteComment || $[345] !== onDeletePendingComment || $[346] !== onEditComment || $[347] !== onEditPendingComment || $[348] !== onPostFileComment || $[349] !== onReplySubmit || $[350] !== onResolveToggle || $[351] !== owner || $[352] !== patch || $[353] !== prDraftKey || $[354] !== prReplyDraftKey || $[355] !== pureRename || $[356] !== reactions || $[357] !== renderAnnotation || $[358] !== renderedCodeView || $[359] !== repo || $[360] !== selectedLines || $[361] !== setDraftComment || $[362] !== severityByCommentId || $[363] !== shouldRenderCodeView || $[364] !== showFileBody || $[365] !== suggestionApplyDisabledReason || $[366] !== threadAiPromptContext) {
    t79 = showFileBody ? <div className="overflow-hidden rounded-b-md">{isFileLevelDraftOpen && !isReadOnly ? <div className="border-b border-zinc-700/40 px-4 py-3"><PrV2DraftComposerCard mode="file" layout="full" value={draftComment} currentUserLogin={currentUserLogin} currentUserAvatarUrl={currentUserAvatarUrl} isMutating={isMutating} isSplitDiffStyle={false} disablePostImmediately={disablePostImmediately} autoFocusInput={true} onChange={value => setDraftComment(prDraftKey, filename, value)} onCancel={dismissDraft} onAddToReview={() => onPostFileComment(filename, "batch")} onSubmitImmediately={() => onPostFileComment(filename, "immediate")} onAskAi={handleAskDraftInChat} /></div> : null}{groupedFileLevelThreads.length > 0 ? <div className="border-b border-zinc-700/40 px-4 py-3"><p className="mb-2 text-[11px] text-zinc-500">File comments</p><div className="space-y-2">{groupedFileLevelThreads.map(groupedFileLevelThread_0 => <PrV2ThreadCardWithReplyDraft key={`file-thread-${groupedFileLevelThread_0.key}`} owner={owner} repo={repo} pullNumber={number} isReadOnly={isReadOnly} comments={groupedFileLevelThread_0.comments} thread={groupedFileLevelThread_0.thread} severityByCommentId={severityByCommentId} prReplyDraftKey={prReplyDraftKey} replyDraftKey={groupedFileLevelThread_0.threadDraftKey} anchorKind="file" layout="full" isSplitDiffStyle={isSplitDiffStyle} resolvedPresentation="hybrid" isMutating={mutationState.any} mutationState={mutationState} onReplySubmit={onReplySubmit} onResolveToggle={onResolveToggle} onEditComment={onEditComment} onDeleteComment={onDeleteComment} onEditPendingComment={onEditPendingComment} onDeletePendingComment={onDeletePendingComment} onApplySuggestion={onApplySuggestion} suggestionApplyDisabledReason={suggestionApplyDisabledReason} reactions={reactions} overflowUtilityActions={true} aiPromptContext={threadAiPromptContext} />)}</div></div> : null}{!pureRename ? <div className={cn("min-w-0 max-w-full bg-black", diffOptions.overflow === "wrap" ? "overflow-x-hidden" : "overflow-x-auto")} ref={diffHostRef}>{renderedCodeView ? <CodeFile key={diffRenderKey} className="block w-full" file={renderedCodeView.descriptor} options={codeOptionsForFile} lineAnnotations={codeLineAnnotations} selectedLines={codeSelectedLines} style={PIERRE_DIFF_PALETTE_OVERRIDES} renderAnnotation={renderAnnotation} /> : shouldRenderCodeView && isCodeViewContentLoading ? <div data-testid={`pr-v2-code-view-loading-${filename}`} className="px-4 py-3 text-sm text-zinc-400">Loading file contents…</div> : keyedFileDiff ? <FileDiff key={diffRenderKey} className="block w-full" fileDiff={keyedFileDiff} options={diffOptionsForFile} lineAnnotations={lineAnnotations} selectedLines={selectedLines} style={PIERRE_DIFF_PALETTE_OVERRIDES} renderAnnotation={renderAnnotation} /> : hasTextContents ? <MultiFileDiff key={diffRenderKey} className="block w-full" oldFile={oldFileDescriptor} newFile={newFileDescriptor} options={diffOptionsForFile} lineAnnotations={lineAnnotations} selectedLines={selectedLines} style={PIERRE_DIFF_PALETTE_OVERRIDES} renderAnnotation={renderAnnotation} /> : normalizedPatch ? <RenderErrorBoundary key={diffRenderKey} fallback={<pre className="overflow-x-auto px-4 py-3 text-xs text-zinc-300">{patch}</pre>}><PatchDiff className="block w-full" patch={normalizedPatch} options={diffOptionsForFile} lineAnnotations={lineAnnotations} selectedLines={selectedLines} style={PIERRE_DIFF_PALETTE_OVERRIDES} renderAnnotation={renderAnnotation} /></RenderErrorBoundary> : file.isLargeFile ? <div className="px-4 py-3 text-sm text-zinc-400">Large file - content not loaded</div> : <div className="px-4 py-3 text-sm text-zinc-400">File content unavailable</div>}</div> : null}</div> : null;
    $[315] = codeLineAnnotations;
    $[316] = codeOptionsForFile;
    $[317] = codeSelectedLines;
    $[318] = currentUserAvatarUrl;
    $[319] = currentUserLogin;
    $[320] = diffOptions.overflow;
    $[321] = diffOptionsForFile;
    $[322] = diffRenderKey;
    $[323] = disablePostImmediately;
    $[324] = dismissDraft;
    $[325] = draftComment;
    $[326] = file.isLargeFile;
    $[327] = filename;
    $[328] = groupedFileLevelThreads;
    $[329] = handleAskDraftInChat;
    $[330] = hasTextContents;
    $[331] = isCodeViewContentLoading;
    $[332] = isFileLevelDraftOpen;
    $[333] = isMutating;
    $[334] = isReadOnly;
    $[335] = isSplitDiffStyle;
    $[336] = keyedFileDiff;
    $[337] = lineAnnotations;
    $[338] = mutationState;
    $[339] = newFileDescriptor;
    $[340] = normalizedPatch;
    $[341] = number;
    $[342] = oldFileDescriptor;
    $[343] = onApplySuggestion;
    $[344] = onDeleteComment;
    $[345] = onDeletePendingComment;
    $[346] = onEditComment;
    $[347] = onEditPendingComment;
    $[348] = onPostFileComment;
    $[349] = onReplySubmit;
    $[350] = onResolveToggle;
    $[351] = owner;
    $[352] = patch;
    $[353] = prDraftKey;
    $[354] = prReplyDraftKey;
    $[355] = pureRename;
    $[356] = reactions;
    $[357] = renderAnnotation;
    $[358] = renderedCodeView;
    $[359] = repo;
    $[360] = selectedLines;
    $[361] = setDraftComment;
    $[362] = severityByCommentId;
    $[363] = shouldRenderCodeView;
    $[364] = showFileBody;
    $[365] = suggestionApplyDisabledReason;
    $[366] = threadAiPromptContext;
    $[367] = t79;
  } else {
    t79 = $[367];
  }
  let t80;
  if ($[368] !== t73 || $[369] !== t74 || $[370] !== t77 || $[371] !== t78 || $[372] !== t79) {
    t80 = <section ref={sectionRef} id={t73} {...t74} className={t77}>{t78}{t79}</section>;
    $[368] = t73;
    $[369] = t74;
    $[370] = t77;
    $[371] = t78;
    $[372] = t79;
    $[373] = t80;
  } else {
    t80 = $[373];
  }
  return t80;
});
export function PrV2PierreDiff(t0) {
  const $ = _c(20);
  let baseSha;
  let headSha;
  let number;
  let owner;
  let props;
  let repo;
  if ($[0] !== t0) {
    ({
      owner,
      repo,
      number,
      baseSha,
      headSha,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = baseSha;
    $[2] = headSha;
    $[3] = number;
    $[4] = owner;
    $[5] = props;
    $[6] = repo;
  } else {
    baseSha = $[1];
    headSha = $[2];
    number = $[3];
    owner = $[4];
    props = $[5];
    repo = $[6];
  }
  let t1;
  if ($[7] !== baseSha || $[8] !== number || $[9] !== owner || $[10] !== repo) {
    t1 = buildVisibleHeadScopeKey({
      owner,
      repo,
      number,
      baseSha
    });
    $[7] = baseSha;
    $[8] = number;
    $[9] = owner;
    $[10] = repo;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  const visibleHeadScopeKey = t1;
  const visibleHeadComponentKey = headSha ? visibleHeadScopeKey : `${visibleHeadScopeKey}:pending-head`;
  let t2;
  if ($[12] !== baseSha || $[13] !== headSha || $[14] !== number || $[15] !== owner || $[16] !== props || $[17] !== repo || $[18] !== visibleHeadComponentKey) {
    t2 = <PrV2DiffWorkerPool><PrV2PierreDiffInner key={visibleHeadComponentKey} owner={owner} repo={repo} number={number} baseSha={baseSha} headSha={headSha} {...props} /></PrV2DiffWorkerPool>;
    $[12] = baseSha;
    $[13] = headSha;
    $[14] = number;
    $[15] = owner;
    $[16] = props;
    $[17] = repo;
    $[18] = visibleHeadComponentKey;
    $[19] = t2;
  } else {
    t2 = $[19];
  }
  return t2;
}

// eslint-disable-next-line complexity
function PrV2PierreDiffInner(t0) {
  const $ = _c(726);
  const {
    owner,
    repo,
    number,
    isReadOnly: t1,
    headSha,
    baseSha,
    headRef: t2,
    isMerged: t3,
    installationId: t4,
    comments,
    severityByCommentId,
    baseLabel,
    fileTreeToolbarHost: t5,
    fileTreeOpenFrom: t6,
    compact: t7,
    discussionDataEnabled: t8
  } = t0;
  const isReadOnly = t1 === undefined ? false : t1;
  const headRef = t2 === undefined ? null : t2;
  const isMerged = t3 === undefined ? false : t3;
  const installationId = t4 === undefined ? null : t4;
  const fileTreeToolbarHost = t5 === undefined ? null : t5;
  const fileTreeOpenFrom = t6 === undefined ? "xl" : t6;
  const compact = t7 === undefined ? false : t7;
  const discussionDataEnabled = t8 === undefined ? false : t8;
  const latestHeadSha = headSha;
  const [visibleHeadSha, setVisibleHeadSha] = useState(headSha);
  let t9;
  if ($[0] !== latestHeadSha) {
    t9 = () => {
      setVisibleHeadSha(latestHeadSha);
    };
    $[0] = latestHeadSha;
    $[1] = t9;
  } else {
    t9 = $[1];
  }
  const reviewLatestHead = t9;
  const {
    user
  } = useAuth();
  const profileQuery = useProfile();
  const t10 = Boolean(visibleHeadSha && baseSha);
  let t11;
  if ($[2] !== t10) {
    t11 = {
      enabled: t10
    };
    $[2] = t10;
    $[3] = t11;
  } else {
    t11 = $[3];
  }
  const versionsQuery = usePrV2Versions(owner, repo, number, t11);
  const t12 = Boolean(visibleHeadSha && baseSha);
  let t13;
  if ($[4] !== t12) {
    t13 = {
      enabled: t12
    };
    $[4] = t12;
    $[5] = t13;
  } else {
    t13 = $[5];
  }
  const reviewsQuery = usePrV2Reviews(owner, repo, number, t13);
  const authMetadata = asRecord(user?.user_metadata);
  const comparisonUserLogin = profileQuery.data?.userName?.trim() || typeof authMetadata?.user_name === "string" && authMetadata.user_name.trim() || null;
  const defaultComparison = calculatePrV2DefaultComparison({
    defaultBaseSha: baseSha,
    defaultHeadSha: visibleHeadSha,
    defaultBaseLabel: baseLabel,
    versions: versionsQuery.data,
    reviews: reviewsQuery.data,
    currentUserLogin: comparisonUserLogin
  });
  const [suppressedAutoComparisonKey, setSuppressedAutoComparisonKey] = useState(null);
  const requestedAutoComparisonKey = buildComparisonKey(defaultComparison.baseSha, defaultComparison.headSha);
  const activeAutoComparison = requestedAutoComparisonKey !== null && requestedAutoComparisonKey === suppressedAutoComparisonKey ? null : defaultComparison;
  const {
    versionOptions,
    effectiveBaseSha,
    effectiveHeadSha,
    isCustomComparison,
    isAutoComparisonActive,
    hasUserComparisonOverride,
    hasMeaningfulComparison,
    setComparison,
    resetComparison
  } = usePrV2VersionComparison({
    defaultBaseSha: baseSha,
    defaultHeadSha: visibleHeadSha,
    defaultBaseLabel: baseLabel,
    autoComparison: activeAutoComparison,
    versions: versionsQuery.data
  });
  const isShowingChangesSinceLastReview = defaultComparison.isAutoComparedToLastReview && isAutoComparisonActive;
  let t14;
  if ($[6] !== versionOptions || $[7] !== visibleHeadSha) {
    let t15;
    if ($[9] !== visibleHeadSha) {
      t15 = versionOption => areShasEqual(versionOption.id, visibleHeadSha);
      $[9] = visibleHeadSha;
      $[10] = t15;
    } else {
      t15 = $[10];
    }
    const option = versionOptions.find(t15);
    t14 = getCompactVersionLabel(option?.label, getFallbackShaLabel(visibleHeadSha, "Current"));
    $[6] = versionOptions;
    $[7] = visibleHeadSha;
    $[8] = t14;
  } else {
    t14 = $[8];
  }
  const visibleHeadLabel = t14;
  let t15;
  if ($[11] !== latestHeadSha || $[12] !== versionOptions) {
    let t16;
    if ($[14] !== latestHeadSha) {
      t16 = versionOption_0 => areShasEqual(versionOption_0.id, latestHeadSha);
      $[14] = latestHeadSha;
      $[15] = t16;
    } else {
      t16 = $[15];
    }
    const option_0 = versionOptions.find(t16);
    t15 = getCompactVersionLabel(option_0?.label, getFallbackShaLabel(latestHeadSha, "Latest"));
    $[11] = latestHeadSha;
    $[12] = versionOptions;
    $[13] = t15;
  } else {
    t15 = $[13];
  }
  const latestHeadLabel = t15;
  let t16;
  if ($[16] !== hasUserComparisonOverride || $[17] !== latestHeadLabel || $[18] !== latestHeadSha || $[19] !== reviewLatestHead || $[20] !== visibleHeadLabel || $[21] !== visibleHeadSha) {
    t16 = latestHeadSha && visibleHeadSha && !areShasEqual(latestHeadSha, visibleHeadSha) && !hasUserComparisonOverride ? {
      currentLabel: visibleHeadLabel,
      latestLabel: latestHeadLabel,
      onReviewLatest: reviewLatestHead
    } : undefined;
    $[16] = hasUserComparisonOverride;
    $[17] = latestHeadLabel;
    $[18] = latestHeadSha;
    $[19] = reviewLatestHead;
    $[20] = visibleHeadLabel;
    $[21] = visibleHeadSha;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  const versionUpdate = t16;
  const t17 = Boolean(effectiveHeadSha && effectiveBaseSha);
  let t18;
  if ($[23] !== effectiveBaseSha || $[24] !== effectiveHeadSha || $[25] !== t17) {
    t18 = {
      headSha: effectiveHeadSha,
      baseSha: effectiveBaseSha,
      enabled: t17
    };
    $[23] = effectiveBaseSha;
    $[24] = effectiveHeadSha;
    $[25] = t17;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  const query = usePrV2CodeChanges(owner, repo, number, t18);
  const mergedSuggestionApplyDisabledReason = isMerged ? "Cannot apply suggestion on a merged pull request" : undefined;
  const queryClient = useQueryClient();
  let t19;
  if ($[27] !== effectiveBaseSha || $[28] !== effectiveHeadSha) {
    t19 = buildComparisonKey(effectiveBaseSha, effectiveHeadSha);
    $[27] = effectiveBaseSha;
    $[28] = effectiveHeadSha;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  const comparisonKey = t19;
  const previousComparisonKeyRef = useRef(comparisonKey);
  let t20;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = new Set();
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  const hoveredDecisionMarkerKeysRef = useRef(t20);
  let t21;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = createSnapshotStore(null);
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  const pendingComparisonKeyStore = t21;
  const pendingComparisonKey = useSyncExternalStore(pendingComparisonKeyStore.subscribe, pendingComparisonKeyStore.getSnapshot, pendingComparisonKeyStore.getSnapshot);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const {
    addComment,
    replyToComment,
    resolveCommentThread,
    editComment,
    deleteComment,
    pending: commentActionPendingRaw,
    isMutating
  } = usePrV2CommentActions(owner, repo, number);
  let t22;
  if ($[32] !== commentActionPendingRaw || $[33] !== isMutating) {
    t22 = commentActionPendingRaw ?? {
      addComment: isMutating,
      reply: isMutating,
      resolve: isMutating,
      edit: isMutating,
      delete: isMutating,
      any: isMutating
    };
    $[32] = commentActionPendingRaw;
    $[33] = isMutating;
    $[34] = t22;
  } else {
    t22 = $[34];
  }
  const commentActionPending = t22;
  let t23;
  if ($[35] !== number || $[36] !== owner || $[37] !== repo) {
    t23 = getPrV2ActiveFileStoreKey(owner, repo, number);
    $[35] = number;
    $[36] = owner;
    $[37] = repo;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  const prV2ActiveFileStoreKey = t23;
  const hasContentPainted = usePrV2ContentPainted(prV2ActiveFileStoreKey);
  useEffect(() => {
    if (!hasContentPainted || !visibleHeadSha || !baseSha || versionOptions.length < 3) {
      return;
    }
    const comparisonsToPrefetch = new Map();
    const previousPushComparison = findPrV2PreviousPushComparison(versionOptions);
    if (previousPushComparison) {
      comparisonsToPrefetch.set(`${previousPushComparison.baseSha}:${previousPushComparison.headSha}`, previousPushComparison);
    }
    if (activeAutoComparison?.baseSha && activeAutoComparison.headSha) {
      comparisonsToPrefetch.set(`${activeAutoComparison.baseSha}:${activeAutoComparison.headSha}`, {
        baseSha: activeAutoComparison.baseSha,
        headSha: activeAutoComparison.headSha
      });
    }
    comparisonsToPrefetch.forEach(comparison => {
      if (comparison.baseSha === effectiveBaseSha && comparison.headSha === effectiveHeadSha) {
        return;
      }
      queryClient.prefetchQuery(prV2CodeChangesQueryOptions(owner, repo, number, comparison));
    });
  }, [activeAutoComparison?.baseSha, activeAutoComparison?.headSha, baseSha, effectiveBaseSha, effectiveHeadSha, hasContentPainted, number, owner, queryClient, repo, visibleHeadSha, versionOptions]);
  const t24 = commentActionPending.reply || commentActionPending.edit || commentActionPending.delete;
  let t25;
  if ($[39] !== commentActionPending.delete || $[40] !== commentActionPending.edit || $[41] !== commentActionPending.reply || $[42] !== commentActionPending.resolve || $[43] !== t24) {
    t25 = {
      any: t24,
      reply: commentActionPending.reply,
      edit: commentActionPending.edit,
      delete: commentActionPending.delete,
      resolve: commentActionPending.resolve
    };
    $[39] = commentActionPending.delete;
    $[40] = commentActionPending.edit;
    $[41] = commentActionPending.reply;
    $[42] = commentActionPending.resolve;
    $[43] = t24;
    $[44] = t25;
  } else {
    t25 = $[44];
  }
  const threadMutationState = t25;
  const reactions = usePrV2Reactions(owner, repo, number);
  let t26;
  if ($[45] !== comments) {
    t26 = comments.some(_temp16);
    $[45] = comments;
    $[46] = t26;
  } else {
    t26 = $[46];
  }
  const hasReviewComments = t26;
  const t27 = discussionDataEnabled || hasReviewComments;
  let t28;
  if ($[47] !== t27) {
    t28 = {
      enabled: t27
    };
    $[47] = t27;
    $[48] = t28;
  } else {
    t28 = $[48];
  }
  usePrV2Discussion(owner, repo, number, t28);
  const pendingComments = usePendingCommentsStore(_temp17);
  const addPendingComment = usePendingCommentsStore(_temp18);
  const removePendingComment = usePendingCommentsStore(_temp19);
  const editPendingComment = usePendingCommentsStore(_temp20);
  let t29;
  if ($[49] !== owner) {
    t29 = owner.toLowerCase();
    $[49] = owner;
    $[50] = t29;
  } else {
    t29 = $[50];
  }
  const normalizedOwner = t29;
  let t30;
  if ($[51] !== repo) {
    t30 = repo.toLowerCase();
    $[51] = repo;
    $[52] = t30;
  } else {
    t30 = $[52];
  }
  const normalizedRepo = t30;
  let t31;
  if ($[53] !== normalizedOwner || $[54] !== normalizedRepo || $[55] !== number || $[56] !== pendingComments) {
    let t32;
    if ($[58] !== normalizedOwner || $[59] !== normalizedRepo || $[60] !== number) {
      t32 = pendingComment => pendingComment.owner.toLowerCase() === normalizedOwner && pendingComment.repo.toLowerCase() === normalizedRepo && pendingComment.pullNumber === Number(number);
      $[58] = normalizedOwner;
      $[59] = normalizedRepo;
      $[60] = number;
      $[61] = t32;
    } else {
      t32 = $[61];
    }
    t31 = pendingComments.filter(t32);
    $[53] = normalizedOwner;
    $[54] = normalizedRepo;
    $[55] = number;
    $[56] = pendingComments;
    $[57] = t31;
  } else {
    t31 = $[57];
  }
  const prPendingComments = t31;
  let t32;
  if ($[62] !== hasReviewComments) {
    t32 = {
      enabled: hasReviewComments
    };
    $[62] = hasReviewComments;
    $[63] = t32;
  } else {
    t32 = $[63];
  }
  const reviewThreadsQuery = usePrV2ReviewThreads(owner, repo, number, t32);
  let t33;
  if ($[64] !== comments || $[65] !== number || $[66] !== owner || $[67] !== prPendingComments || $[68] !== repo || $[69] !== reviewThreadsQuery.data) {
    t33 = {
      owner,
      repo,
      number,
      comments,
      reviewThreads: reviewThreadsQuery.data,
      pendingComments: prPendingComments
    };
    $[64] = comments;
    $[65] = number;
    $[66] = owner;
    $[67] = prPendingComments;
    $[68] = repo;
    $[69] = reviewThreadsQuery.data;
    $[70] = t33;
  } else {
    t33 = $[70];
  }
  const {
    comments: localComments,
    reviewThreads: localReviewThreads,
    reviewThreadByCommentNodeId
  } = usePrV2LocalCommentModel(t33);
  let t34;
  if ($[71] !== comments) {
    t34 = new Set(comments.map(_temp21));
    $[71] = comments;
    $[72] = t34;
  } else {
    t34 = $[72];
  }
  const rawCommentIds = t34;
  let t35;
  if ($[73] !== prPendingComments) {
    t35 = new Set(prPendingComments.map(_temp22));
    $[73] = prPendingComments;
    $[74] = t35;
  } else {
    t35 = $[74];
  }
  const pendingCommentIds = t35;
  let t36;
  if ($[75] !== localComments || $[76] !== pendingCommentIds || $[77] !== rawCommentIds) {
    let t37;
    if ($[79] !== pendingCommentIds || $[80] !== rawCommentIds) {
      t37 = comment_1 => !rawCommentIds.has(comment_1.githubCommentId) && !pendingCommentIds.has(comment_1.githubCommentId);
      $[79] = pendingCommentIds;
      $[80] = rawCommentIds;
      $[81] = t37;
    } else {
      t37 = $[81];
    }
    t36 = localComments.filter(t37);
    $[75] = localComments;
    $[76] = pendingCommentIds;
    $[77] = rawCommentIds;
    $[78] = t36;
  } else {
    t36 = $[78];
  }
  const overlayComments = t36;
  let t37;
  if ($[82] !== comments || $[83] !== overlayComments || $[84] !== prPendingComments) {
    t37 = buildPrV2CommentReadModel({
      comments: [...comments, ...overlayComments],
      pendingComments: prPendingComments
    });
    $[82] = comments;
    $[83] = overlayComments;
    $[84] = prPendingComments;
    $[85] = t37;
  } else {
    t37 = $[85];
  }
  const commentReadModel = t37;
  let t38;
  if ($[86] !== commentReadModel) {
    t38 = commentReadModel.map(_temp23);
    $[86] = commentReadModel;
    $[87] = t38;
  } else {
    t38 = $[87];
  }
  const allComments = t38;
  const currentUserLogin = typeof authMetadata?.user_name === "string" && authMetadata.user_name.trim() || typeof user?.email === "string" && user.email.trim() || "you";
  const currentUserAvatarUrl = typeof authMetadata?.avatar_url === "string" ? authMetadata.avatar_url : "";
  const disablePostImmediately = prPendingComments.length > 0;
  const clearDraftForFile = usePrV2DraftStore(_temp24);
  const setDraftForFile = usePrV2DraftStore(_temp25);
  const openFileDraft = usePrV2DraftStore(_temp26);
  const pruneDraftsToExistingFiles = usePrV2DraftStore(_temp27);
  const clearDraftsForPr = usePrV2DraftStore(_temp28);
  const setReplyDraft = usePrV2ReplyDraftStore(_temp29);
  const pruneReplyDraftsToExistingKeys = usePrV2ReplyDraftStore(_temp30);
  const pruneActiveReplyTargetsToExistingKeys = usePrV2ReplyDraftStore(_temp31);
  const clearReplyDraftsForPr = usePrV2ReplyDraftStore(_temp32);
  const clearActiveReplyTargetsForPr = usePrV2ReplyDraftStore(_temp33);
  const [manualActiveFilename, setManualActiveFilename] = useState(null);
  const [chapterModeState, setChapterModeState] = useState(_temp34);
  const [codeViewFilenames, setCodeViewFilenames] = useState(_temp35);
  const scrollSyncFrameRef = useRef(null);
  const viewedFileScrollFrameRef = useRef(null);
  const viewedFileScrollTimeoutRef = useRef(null);
  const suppressNextViewportActiveFilenameSyncRef = useRef(false);
  const [pendingScrollTarget, setPendingScrollTarget] = useState(null);
  const {
    toggleSidebar,
    sendToSidebarChat
  } = useSidebarActions();
  const setDiffNavigationHandlers = useDiffNavigationStore(_temp36);
  const clearDiffNavigationHandlers = useDiffNavigationStore(_temp37);
  const setPrV2ActiveFilename = usePrV2ActiveFileStore(_temp38);
  const clearPrV2ActiveFilename = usePrV2ActiveFileStore(_temp39);
  let t39;
  if ($[88] !== number || $[89] !== owner || $[90] !== repo) {
    t39 = getPrV2DraftStoreKey(owner, repo, number);
    $[88] = number;
    $[89] = owner;
    $[90] = repo;
    $[91] = t39;
  } else {
    t39 = $[91];
  }
  const prDraftKey = t39;
  let t40;
  if ($[92] !== number || $[93] !== owner || $[94] !== repo) {
    t40 = getPrV2ReplyDraftStoreKey(owner, repo, number);
    $[92] = number;
    $[93] = owner;
    $[94] = repo;
    $[95] = t40;
  } else {
    t40 = $[95];
  }
  const prReplyDraftKey = t40;
  const isMobile = useIsMobile();
  const fileTreeLayout = compact ? "right-drawer" : isMobile ? "left-drawer" : "sidebar";
  const isCompactLayout = fileTreeLayout !== "sidebar";
  const groupCardSlot = usePrV2GroupCardSlot();
  const groupGuide = groupCardSlot ? "card" : "inline";
  const diffStyle = usePrV2ViewPreferencesStore(_temp40);
  const overflow = usePrV2ViewPreferencesStore(_temp41);
  const diffIndicators = usePrV2ViewPreferencesStore(_temp42);
  const lineDiffType = usePrV2ViewPreferencesStore(_temp43);
  const hunkSeparators = usePrV2ViewPreferencesStore(_temp44);
  const highlightTheme = usePrV2ViewPreferencesStore(_temp45);
  const showLineNumbers = usePrV2ViewPreferencesStore(_temp46);
  const expandUnchanged = usePrV2ViewPreferencesStore(_temp47);
  let t41;
  if ($[96] !== hunkSeparators) {
    t41 = normalizePrV2HunkSeparatorOption(hunkSeparators);
    $[96] = hunkSeparators;
    $[97] = t41;
  } else {
    t41 = $[97];
  }
  const effectiveHunkSeparators = t41;
  const effectiveOverflow = isCompactLayout ? "scroll" : overflow;
  const effectiveDiffStyle = isCompactLayout ? "unified" : diffStyle;
  let t42;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = filename => {
      setCodeViewFilenames(current => {
        const next = new Set(current);
        if (next.has(filename)) {
          next.delete(filename);
        } else {
          next.add(filename);
        }
        return next;
      });
    };
    $[98] = t42;
  } else {
    t42 = $[98];
  }
  const handleToggleCodeView = t42;
  let t43;
  if ($[99] !== toggleSidebar) {
    t43 = payload => {
      toggleSidebar(buildDecisionMarkerAiChatText(payload), "append");
    };
    $[99] = toggleSidebar;
    $[100] = t43;
  } else {
    t43 = $[100];
  }
  const handleDecisionMarkerClick = t43;
  const canPaintDiffs = usePrV2DiffRendererReady();
  const t44 = !showLineNumbers;
  let t45;
  if ($[101] !== diffIndicators || $[102] !== effectiveDiffStyle || $[103] !== effectiveHunkSeparators || $[104] !== effectiveOverflow || $[105] !== expandUnchanged || $[106] !== highlightTheme || $[107] !== lineDiffType || $[108] !== t44) {
    t45 = {
      ...DEFAULT_DIFF_OPTIONS,
      diffStyle: effectiveDiffStyle,
      overflow: effectiveOverflow,
      diffIndicators,
      lineDiffType,
      hunkSeparators: effectiveHunkSeparators,
      theme: highlightTheme,
      expandUnchanged,
      disableLineNumbers: t44,
      enableGutterUtility: true
    };
    $[101] = diffIndicators;
    $[102] = effectiveDiffStyle;
    $[103] = effectiveHunkSeparators;
    $[104] = effectiveOverflow;
    $[105] = expandUnchanged;
    $[106] = highlightTheme;
    $[107] = lineDiffType;
    $[108] = t44;
    $[109] = t45;
  } else {
    t45 = $[109];
  }
  const diffOptions = t45;
  const hideTestFiles = usePrV2ViewPreferencesStore(_temp48);
  const applyViewPreferences = usePrV2ViewPreferencesStore(_temp49);
  let t46;
  if ($[110] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = [];
    $[110] = t46;
  } else {
    t46 = $[110];
  }
  let t47;
  if ($[111] !== hideTestFiles) {
    t47 = {
      preference: hideTestFiles,
      filenames: t46 as string[]
    };
    $[111] = hideTestFiles;
    $[112] = t47;
  } else {
    t47 = $[112];
  }
  const [revealedTests, setRevealedTests] = useState(t47);
  if (revealedTests.preference !== hideTestFiles) {
    setRevealedTests({
      preference: hideTestFiles,
      filenames: []
    });
  }
  let t48;
  if ($[113] !== hideTestFiles || $[114] !== revealedTests.filenames) {
    t48 = createPrV2FileFilter(hideTestFiles, revealedTests.filenames);
    $[113] = hideTestFiles;
    $[114] = revealedTests.filenames;
    $[115] = t48;
  } else {
    t48 = $[115];
  }
  const isFileVisible = t48;
  let t49;
  if ($[116] !== isFileVisible) {
    t49 = filename_0 => {
      if (isFileVisible({
        filename: filename_0
      })) {
        return false;
      }
      setRevealedTests(current_0 => ({
        ...current_0,
        filenames: current_0.filenames.includes(filename_0) ? current_0.filenames : [...current_0.filenames, filename_0]
      }));
      return true;
    };
    $[116] = isFileVisible;
    $[117] = t49;
  } else {
    t49 = $[117];
  }
  const revealTestFile = t49;
  const codeContent = query.data?.codeContent ?? EMPTY_CODE_CONTENT;
  let t50;
  if ($[118] !== codeContent || $[119] !== isFileVisible) {
    t50 = codeContent.filter(isFileVisible);
    $[118] = codeContent;
    $[119] = isFileVisible;
    $[120] = t50;
  } else {
    t50 = $[120];
  }
  const visibleCodeContent = t50;
  const comparisonResolution = query.data?.comparisonResolution ?? null;
  if (comparisonResolution?.status === "diverged" && !hasUserComparisonOverride && latestHeadSha && visibleHeadSha && !areShasEqual(latestHeadSha, visibleHeadSha)) {
    setVisibleHeadSha(latestHeadSha);
  }
  let t51;
  if ($[121] !== isCustomComparison || $[122] !== owner) {
    t51 = !isCustomComparison || shouldEnablePrV2CustomComparisonOrderedFiles(owner);
    $[121] = isCustomComparison;
    $[122] = owner;
    $[123] = t51;
  } else {
    t51 = $[123];
  }
  const shouldUseOrderedFilesForComparison = t51;
  let t52;
  if ($[124] !== codeContent) {
    t52 = codeContent.map(_temp50);
    $[124] = codeContent;
    $[125] = t52;
  } else {
    t52 = $[125];
  }
  const codeContentFilenames = t52;
  const t53 = Boolean(query.data) && codeContent.length > 0 && !query.data?.orderedFiles && shouldUseOrderedFilesForComparison;
  let t54;
  if ($[126] !== effectiveBaseSha || $[127] !== effectiveHeadSha || $[128] !== t53) {
    t54 = {
      headSha: effectiveHeadSha,
      baseSha: effectiveBaseSha,
      enabled: t53
    };
    $[126] = effectiveBaseSha;
    $[127] = effectiveHeadSha;
    $[128] = t53;
    $[129] = t54;
  } else {
    t54 = $[129];
  }
  const orderedFilesQuery = usePrV2OrderedFiles(owner, repo, number, t54);
  const t55 = Boolean(query.data) && codeContent.length > 0 && !isReadOnly;
  let t56;
  if ($[130] !== effectiveBaseSha || $[131] !== effectiveHeadSha || $[132] !== t55) {
    t56 = {
      headSha: effectiveHeadSha,
      baseSha: effectiveBaseSha,
      enabled: t55
    };
    $[130] = effectiveBaseSha;
    $[131] = effectiveHeadSha;
    $[132] = t55;
    $[133] = t56;
  } else {
    t56 = $[133];
  }
  const fileDescriptionQuery = usePrV2FileDescription(owner, repo, number, t56);
  const hasPendingOrderedFilesLoading = Boolean(query.data) && codeContent.length > 0 && !query.data?.orderedFiles && shouldUseOrderedFilesForComparison && (orderedFilesQuery.isLoading || orderedFilesQuery.data?.status === "pending");
  const isComparisonLoading = comparisonKey !== null && pendingComparisonKey === comparisonKey && (query.isLoading || query.isFetching);
  const shouldSilentlyFallbackToCanonicalComparison = isAutoComparisonActive && !hasUserComparisonOverride && (comparisonResolution?.status === "diverged" || comparisonResolution?.status === "merge_only");
  const showComparisonLoadingPanel = isComparisonLoading || shouldSilentlyFallbackToCanonicalComparison;
  const mergeComparisonNotice = isCustomComparison && comparisonResolution?.status === "resolved" && comparisonResolution.strategy === "post_merge" ? "Showing only changes after the merge commit to avoid merged-branch noise." : null;
  const emptyComparisonMessage = isCustomComparison && comparisonResolution?.status === "merge_only" ? "This version range only contains merge-only changes." : isCustomComparison && comparisonResolution?.status === "diverged" ? "This comparison is no longer valid because the branch history was rewritten. Reset the comparison to continue." : "No renderable file diffs available.";
  let t57;
  if ($[134] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = createStableTreeStateStore();
    $[134] = t57;
  } else {
    t57 = $[134];
  }
  const stableTreeStateStore = t57;
  const previousStableTreeState = useSyncExternalStore(stableTreeStateStore.subscribe, stableTreeStateStore.getSnapshot, stableTreeStateStore.getSnapshot);
  const orderedFiles = orderedFilesQuery.data?.status === "ready" ? orderedFilesQuery.data.orderedFiles : query.data?.orderedFiles ?? EMPTY_ORDERED_FILES;
  const decisions = fileDescriptionQuery.data?.status === "ready" ? fileDescriptionQuery.data.decisions : EMPTY_FILE_REVIEW_DECISIONS;
  let t58;
  if ($[135] !== decisions) {
    t58 = buildFileReviewDecisionsByFilename(decisions);
    $[135] = decisions;
    $[136] = t58;
  } else {
    t58 = $[136];
  }
  const fileReviewDecisionsByFilename = t58;
  let t59;
  if ($[137] !== codeContent) {
    t59 = getDefaultCollapsedPrV2Filenames(codeContent);
    $[137] = codeContent;
    $[138] = t59;
  } else {
    t59 = $[138];
  }
  const defaultCollapsedFilenames = t59;
  let t60;
  if ($[139] !== effectiveHeadSha || $[140] !== latestHeadSha) {
    t60 = areShasEqual(effectiveHeadSha, latestHeadSha);
    $[139] = effectiveHeadSha;
    $[140] = latestHeadSha;
    $[141] = t60;
  } else {
    t60 = $[141];
  }
  let t61;
  if ($[142] !== codeContent || $[143] !== defaultCollapsedFilenames || $[144] !== effectiveHeadSha || $[145] !== number || $[146] !== owner || $[147] !== repo || $[148] !== t60) {
    t61 = {
      owner,
      repo,
      number,
      headSha: effectiveHeadSha,
      isLatestHead: t60,
      files: codeContent,
      defaultCollapsedFilenames
    };
    $[142] = codeContent;
    $[143] = defaultCollapsedFilenames;
    $[144] = effectiveHeadSha;
    $[145] = number;
    $[146] = owner;
    $[147] = repo;
    $[148] = t60;
    $[149] = t61;
  } else {
    t61 = $[149];
  }
  const {
    viewedFiles,
    changedSinceLastViewFiles,
    isViewed,
    isCollapsed,
    toggleViewed,
    setFileCollapsed,
    setFilesCollapsed,
    setFilesViewed: setFilesViewedState
  } = usePrV2FileVisibilityState(t61);
  let t62;
  if ($[150] !== installationId || $[151] !== number || $[152] !== owner || $[153] !== repo || $[154] !== setFileCollapsed) {
    t62 = (filename_1, isCurrentlyCollapsed) => {
      setFileCollapsed(filename_1, !isCurrentlyCollapsed);
      trackClientEvent("v2_pr_file_toggled", installationId?.toString() ?? null, {
        owner,
        repo,
        pr_number: number,
        file_path: filename_1,
        action: isCurrentlyCollapsed ? "expand" : "collapse",
        is_story_mode: true
      });
    };
    $[150] = installationId;
    $[151] = number;
    $[152] = owner;
    $[153] = repo;
    $[154] = setFileCollapsed;
    $[155] = t62;
  } else {
    t62 = $[155];
  }
  const handleToggleCollapsed = t62;
  let t63;
  if ($[156] !== comparisonKey || $[157] !== installationId || $[158] !== number || $[159] !== owner || $[160] !== repo) {
    t63 = (markerFilename, lineStart, decisionCount) => {
      const markerKey = `${comparisonKey ?? "pending"}:${markerFilename}:${lineStart}`;
      if (hoveredDecisionMarkerKeysRef.current.has(markerKey)) {
        return;
      }
      hoveredDecisionMarkerKeysRef.current.add(markerKey);
      trackClientEvent("v2_decision_marker_hovered", installationId?.toString() ?? null, {
        owner,
        repo,
        pr_number: number,
        file_path: markerFilename,
        line_start: lineStart,
        decision_count: decisionCount
      });
    };
    $[156] = comparisonKey;
    $[157] = installationId;
    $[158] = number;
    $[159] = owner;
    $[160] = repo;
    $[161] = t63;
  } else {
    t63 = $[161];
  }
  const handleDecisionMarkerHover = t63;
  let t64;
  if ($[162] !== installationId || $[163] !== number || $[164] !== owner || $[165] !== repo || $[166] !== setFilesViewedState) {
    t64 = t65 => {
      const {
        groupName,
        groupOrder,
        filenames,
        isGroupReviewed: currentlyReviewed
      } = t65;
      if (filenames.length === 0) {
        return;
      }
      const nextReviewed = !currentlyReviewed;
      setFilesViewedState(filenames, nextReviewed);
      trackClientEvent("v2_pr_group_reviewed_toggled", installationId?.toString() ?? null, {
        owner,
        repo,
        pr_number: number,
        group_name: groupName,
        group_order: groupOrder,
        action: nextReviewed ? "reviewed" : "unreviewed",
        file_count: filenames.length,
        is_story_mode: true
      });
    };
    $[162] = installationId;
    $[163] = number;
    $[164] = owner;
    $[165] = repo;
    $[166] = setFilesViewedState;
    $[167] = t64;
  } else {
    t64 = $[167];
  }
  const handleToggleGroupReviewed = t64;
  const files = query.data?.files ?? EMPTY_FILES;
  let t65;
  if ($[168] !== files || $[169] !== isFileVisible) {
    t65 = getPrV2VisibleFileStats(files, isFileVisible);
    $[168] = files;
    $[169] = isFileVisible;
    $[170] = t65;
  } else {
    t65 = $[170];
  }
  const filteredStats = t65;
  const diffStatus = query.error ? "error" : !query.data || showComparisonLoadingPanel ? "loading" : "ready";
  let t66;
  if ($[171] !== diffStatus || $[172] !== files || $[173] !== revealedTests.filenames) {
    t66 = {
      files,
      revealedFilenames: revealedTests.filenames,
      status: diffStatus
    };
    $[171] = diffStatus;
    $[172] = files;
    $[173] = revealedTests.filenames;
    $[174] = t66;
  } else {
    t66 = $[174];
  }
  usePublishPrV2FileFilterSnapshot(t66);
  query.data?.fileDiffByFilename;
  query.data?.fileDiffEntries;
  const t67 = query.data?.fileDiffEntries;
  const t68 = query.data?.fileDiffByFilename;
  let t69;
  if ($[175] !== t67 || $[176] !== t68) {
    t69 = buildFileDiffLookupByFilename(t67, t68);
    $[175] = t67;
    $[176] = t68;
    $[177] = t69;
  } else {
    t69 = $[177];
  }
  const fileDiffByFilename = t69;
  let t70;
  if ($[178] !== codeContent || $[179] !== orderedFiles) {
    t70 = getGroupedCodeContentCached(codeContent, orderedFiles);
    $[178] = codeContent;
    $[179] = orderedFiles;
    $[180] = t70;
  } else {
    t70 = $[180];
  }
  const allGroupedCodeContent = t70;
  let t71;
  if ($[181] !== allGroupedCodeContent || $[182] !== isFileVisible) {
    t71 = filterPrV2FileGroups(allGroupedCodeContent, isFileVisible);
    $[181] = allGroupedCodeContent;
    $[182] = isFileVisible;
    $[183] = t71;
  } else {
    t71 = $[183];
  }
  const groupedCodeContent = t71;
  let t72;
  if ($[184] !== orderedFiles) {
    t72 = hasUsablePrV2OrderedFiles(orderedFiles);
    $[184] = orderedFiles;
    $[185] = t72;
  } else {
    t72 = $[185];
  }
  const hasAiFileGrouping = t72;
  let t73;
  if ($[186] !== groupedCodeContent) {
    t73 = buildPrV2ChapterModeModel(groupedCodeContent);
    $[186] = groupedCodeContent;
    $[187] = t73;
  } else {
    t73 = $[187];
  }
  const chapterModeModel = t73;
  const {
    chapterEntries,
    chapterEntryByKey,
    chapterKeyByFilename,
    chapterKeyByGroupId
  } = chapterModeModel;
  let t74;
  if ($[188] !== allGroupedCodeContent.length || $[189] !== codeContent.length) {
    t74 = isPrV2LargePrChapterMode(codeContent.length, allGroupedCodeContent.length);
    $[188] = allGroupedCodeContent.length;
    $[189] = codeContent.length;
    $[190] = t74;
  } else {
    t74 = $[190];
  }
  const isLargePrChapterMode = t74;
  const showChapterSwitcher = isLargePrChapterMode && chapterEntries.length > 1;
  const chapterComparisonKey = `${effectiveBaseSha ?? ""}:${effectiveHeadSha ?? ""}`;
  let t75;
  if ($[191] !== chapterComparisonKey || $[192] !== chapterEntries || $[193] !== chapterEntryByKey || $[194] !== chapterKeyByFilename || $[195] !== chapterModeState || $[196] !== isLargePrChapterMode) {
    t75 = resolvePrV2ActiveChapterEntry({
      chapterComparisonKey,
      chapterEntries,
      chapterEntryByKey,
      chapterKeyByFilename,
      chapterModeState,
      isLargePrChapterMode
    });
    $[191] = chapterComparisonKey;
    $[192] = chapterEntries;
    $[193] = chapterEntryByKey;
    $[194] = chapterKeyByFilename;
    $[195] = chapterModeState;
    $[196] = isLargePrChapterMode;
    $[197] = t75;
  } else {
    t75 = $[197];
  }
  const activeChapterEntry = t75;
  const resolvedActiveChapterKey = activeChapterEntry?.key ?? null;
  let t76;
  bb0: {
    if (isLargePrChapterMode) {
      let t77;
      if ($[198] !== activeChapterEntry) {
        t77 = activeChapterEntry ? [activeChapterEntry] : [];
        $[198] = activeChapterEntry;
        $[199] = t77;
      } else {
        t77 = $[199];
      }
      t76 = t77;
      break bb0;
    }
    t76 = chapterEntries;
  }
  const visibleChapterEntries = t76;
  let t77;
  if ($[200] !== visibleChapterEntries) {
    t77 = visibleChapterEntries.map(_temp51);
    $[200] = visibleChapterEntries;
    $[201] = t77;
  } else {
    t77 = $[201];
  }
  const visibleGroupedCodeContent = t77;
  let t78;
  if ($[202] !== visibleGroupedCodeContent) {
    t78 = visibleGroupedCodeContent.flatMap(_temp53);
    $[202] = visibleGroupedCodeContent;
    $[203] = t78;
  } else {
    t78 = $[203];
  }
  const renderedFilenames = t78;
  let t79;
  if ($[204] !== renderedFilenames) {
    t79 = new Set(renderedFilenames);
    $[204] = renderedFilenames;
    $[205] = t79;
  } else {
    t79 = $[205];
  }
  const renderedFilenameSet = t79;
  let t80;
  if ($[206] !== renderedFilenames || $[207] !== setFilesCollapsed) {
    t80 = collapsed => {
      if (renderedFilenames.length === 0) {
        return;
      }
      setFilesCollapsed(renderedFilenames, collapsed);
      toast.success(collapsed ? "All files collapsed" : "All files expanded");
    };
    $[206] = renderedFilenames;
    $[207] = setFilesCollapsed;
    $[208] = t80;
  } else {
    t80 = $[208];
  }
  const setAllRenderedFilesCollapsed = t80;
  let t81;
  if ($[209] !== manualActiveFilename || $[210] !== renderedFilenames) {
    t81 = resolveActiveFilename(manualActiveFilename, renderedFilenames);
    $[209] = manualActiveFilename;
    $[210] = renderedFilenames;
    $[211] = t81;
  } else {
    t81 = $[211];
  }
  const activeFilename = t81;
  let t82;
  if ($[212] !== activeFilename || $[213] !== comparisonKey || $[214] !== renderedFilenames || $[215] !== visibleChapterEntries) {
    t82 = {
      entries: visibleChapterEntries,
      activeFilename,
      comparisonKey,
      renderedFilenames
    };
    $[212] = activeFilename;
    $[213] = comparisonKey;
    $[214] = renderedFilenames;
    $[215] = visibleChapterEntries;
    $[216] = t82;
  } else {
    t82 = $[216];
  }
  const {
    currentGroupEntry,
    markGroupJump
  } = usePrV2CurrentGroup(t82);
  const unfilteredTreeDisplayFiles = showComparisonLoadingPanel && files.length === 0 ? previousStableTreeState?.files ?? files : files;
  const treeDisplayOrderedFiles = showComparisonLoadingPanel && orderedFiles.length === 0 ? previousStableTreeState?.orderedFiles ?? orderedFiles : orderedFiles;
  const unfilteredTreeDisplayGroupedCodeContent = showComparisonLoadingPanel && codeContent.length === 0 ? previousStableTreeState?.groupedCodeContent ?? allGroupedCodeContent : allGroupedCodeContent;
  let t83;
  if ($[217] !== isFileVisible || $[218] !== unfilteredTreeDisplayFiles) {
    t83 = unfilteredTreeDisplayFiles.filter(isFileVisible);
    $[217] = isFileVisible;
    $[218] = unfilteredTreeDisplayFiles;
    $[219] = t83;
  } else {
    t83 = $[219];
  }
  const treeDisplayFiles = t83;
  let t84;
  if ($[220] !== isFileVisible || $[221] !== unfilteredTreeDisplayGroupedCodeContent) {
    t84 = filterPrV2FileGroups(unfilteredTreeDisplayGroupedCodeContent, isFileVisible);
    $[220] = isFileVisible;
    $[221] = unfilteredTreeDisplayGroupedCodeContent;
    $[222] = t84;
  } else {
    t84 = $[222];
  }
  const treeDisplayGroupedCodeContent = t84;
  let t85;
  let t86;
  if ($[223] !== comparisonKey || $[224] !== pendingComparisonKeyStore || $[225] !== query.isFetching || $[226] !== query.isLoading) {
    t85 = () => {
      if (!comparisonKey) {
        previousComparisonKeyRef.current = null;
        pendingComparisonKeyStore.setSnapshot(null);
        return;
      }
      if (previousComparisonKeyRef.current === comparisonKey) {
        return;
      }
      previousComparisonKeyRef.current = comparisonKey;
      pendingComparisonKeyStore.setSnapshot(query.isLoading || query.isFetching ? comparisonKey : null);
    };
    t86 = [comparisonKey, pendingComparisonKeyStore, query.isFetching, query.isLoading];
    $[223] = comparisonKey;
    $[224] = pendingComparisonKeyStore;
    $[225] = query.isFetching;
    $[226] = query.isLoading;
    $[227] = t85;
    $[228] = t86;
  } else {
    t85 = $[227];
    t86 = $[228];
  }
  useEffect(t85, t86);
  let t87;
  let t88;
  if ($[229] !== comparisonKey || $[230] !== pendingComparisonKey || $[231] !== pendingComparisonKeyStore || $[232] !== query.isFetching || $[233] !== query.isLoading) {
    t87 = () => {
      if (!comparisonKey || pendingComparisonKey !== comparisonKey) {
        return;
      }
      if (!query.isLoading && !query.isFetching) {
        pendingComparisonKeyStore.setSnapshot(null);
      }
    };
    t88 = [comparisonKey, pendingComparisonKey, pendingComparisonKeyStore, query.isFetching, query.isLoading];
    $[229] = comparisonKey;
    $[230] = pendingComparisonKey;
    $[231] = pendingComparisonKeyStore;
    $[232] = query.isFetching;
    $[233] = query.isLoading;
    $[234] = t87;
    $[235] = t88;
  } else {
    t87 = $[234];
    t88 = $[235];
  }
  useEffect(t87, t88);
  let t89;
  if ($[236] !== comparisonKey || $[237] !== setSuppressedAutoComparisonKey || $[238] !== shouldSilentlyFallbackToCanonicalComparison) {
    t89 = () => {
      if (!shouldSilentlyFallbackToCanonicalComparison || !comparisonKey) {
        return;
      }
      let isCancelled = false;
      queueMicrotask(() => {
        if (isCancelled) {
          return;
        }
        setSuppressedAutoComparisonKey(currentComparisonKey => currentComparisonKey === comparisonKey ? currentComparisonKey : comparisonKey);
      });
      return () => {
        isCancelled = true;
      };
    };
    $[236] = comparisonKey;
    $[237] = setSuppressedAutoComparisonKey;
    $[238] = shouldSilentlyFallbackToCanonicalComparison;
    $[239] = t89;
  } else {
    t89 = $[239];
  }
  let t90;
  if ($[240] !== comparisonKey || $[241] !== shouldSilentlyFallbackToCanonicalComparison) {
    t90 = [comparisonKey, shouldSilentlyFallbackToCanonicalComparison];
    $[240] = comparisonKey;
    $[241] = shouldSilentlyFallbackToCanonicalComparison;
    $[242] = t90;
  } else {
    t90 = $[242];
  }
  useEffect(t89, t90);
  let t91;
  let t92;
  if ($[243] !== allGroupedCodeContent || $[244] !== codeContent.length || $[245] !== files || $[246] !== orderedFiles || $[247] !== showComparisonLoadingPanel) {
    t91 = () => {
      if (showComparisonLoadingPanel || files.length === 0 && codeContent.length === 0) {
        return;
      }
      stableTreeStateStore.setSnapshot({
        files,
        orderedFiles,
        groupedCodeContent: allGroupedCodeContent
      });
    };
    t92 = [files, codeContent.length, allGroupedCodeContent, orderedFiles, showComparisonLoadingPanel, stableTreeStateStore];
    $[243] = allGroupedCodeContent;
    $[244] = codeContent.length;
    $[245] = files;
    $[246] = orderedFiles;
    $[247] = showComparisonLoadingPanel;
    $[248] = t91;
    $[249] = t92;
  } else {
    t91 = $[248];
    t92 = $[249];
  }
  useEffect(t91, t92);
  let t93;
  let t94;
  if ($[250] !== activeFilename || $[251] !== prV2ActiveFileStoreKey || $[252] !== query.isFetching || $[253] !== query.isLoading || $[254] !== setPrV2ActiveFilename || $[255] !== showComparisonLoadingPanel) {
    t93 = () => {
      if (activeFilename === null && (showComparisonLoadingPanel || query.isLoading || query.isFetching)) {
        return;
      }
      setPrV2ActiveFilename(prV2ActiveFileStoreKey, activeFilename);
    };
    t94 = [activeFilename, prV2ActiveFileStoreKey, query.isLoading, query.isFetching, setPrV2ActiveFilename, showComparisonLoadingPanel];
    $[250] = activeFilename;
    $[251] = prV2ActiveFileStoreKey;
    $[252] = query.isFetching;
    $[253] = query.isLoading;
    $[254] = setPrV2ActiveFilename;
    $[255] = showComparisonLoadingPanel;
    $[256] = t93;
    $[257] = t94;
  } else {
    t93 = $[256];
    t94 = $[257];
  }
  useEffect(t93, t94);
  let t95;
  let t96;
  if ($[258] !== clearPrV2ActiveFilename || $[259] !== prV2ActiveFileStoreKey) {
    t95 = () => () => clearPrV2ActiveFilename(prV2ActiveFileStoreKey);
    t96 = [clearPrV2ActiveFilename, prV2ActiveFileStoreKey];
    $[258] = clearPrV2ActiveFilename;
    $[259] = prV2ActiveFileStoreKey;
    $[260] = t95;
    $[261] = t96;
  } else {
    t95 = $[260];
    t96 = $[261];
  }
  useEffect(t95, t96);
  let t97;
  if ($[262] !== allComments) {
    t97 = getCommentsByFilePathCached(allComments);
    $[262] = allComments;
    $[263] = t97;
  } else {
    t97 = $[263];
  }
  const commentsByFile = t97;
  const reviewThreads = localReviewThreads.length > 0 || (reviewThreadsQuery.data?.length ?? 0) === 0 ? localReviewThreads : reviewThreadsQuery.data ?? EMPTY_REVIEW_THREADS;
  let commentByGithubId;
  let readModelCommentByGithubId;
  let reviewThreadContextByCommentNodeId;
  let t98;
  if ($[264] !== allComments || $[265] !== commentReadModel || $[266] !== reviewThreads) {
    reviewThreadContextByCommentNodeId = getReviewThreadContextByCommentNodeIdCached(reviewThreads);
    let t99;
    if ($[271] !== allComments) {
      t99 = buildCommentByGithubId(allComments);
      $[271] = allComments;
      $[272] = t99;
    } else {
      t99 = $[272];
    }
    commentByGithubId = t99;
    let t100;
    if ($[273] !== commentReadModel) {
      t100 = buildPrV2ReadModelByGithubCommentId(commentReadModel);
      $[273] = commentReadModel;
      $[274] = t100;
    } else {
      t100 = $[274];
    }
    readModelCommentByGithubId = t100;
    t98 = getInlineAnchorByCommentIdCached(allComments, reviewThreadContextByCommentNodeId);
    $[264] = allComments;
    $[265] = commentReadModel;
    $[266] = reviewThreads;
    $[267] = commentByGithubId;
    $[268] = readModelCommentByGithubId;
    $[269] = reviewThreadContextByCommentNodeId;
    $[270] = t98;
  } else {
    commentByGithubId = $[267];
    readModelCommentByGithubId = $[268];
    reviewThreadContextByCommentNodeId = $[269];
    t98 = $[270];
  }
  const inlineAnchorByCommentId = t98;
  let draftKeys;
  let targetKeys;
  if ($[275] !== allComments || $[276] !== readModelCommentByGithubId) {
    draftKeys = new Set();
    targetKeys = new Set();
    allComments.forEach(comment_2 => {
      const threadDraftKey = readModelCommentByGithubId.get(comment_2.githubCommentId)?.threadId ?? `comment:${comment_2.githubCommentId}`;
      targetKeys.add(threadDraftKey);
      draftKeys.add(threadDraftKey);
      draftKeys.add(buildThreadReplyDraftKey(threadDraftKey, comment_2.githubCommentId));
    });
    $[275] = allComments;
    $[276] = readModelCommentByGithubId;
    $[277] = draftKeys;
    $[278] = targetKeys;
  } else {
    draftKeys = $[277];
    targetKeys = $[278];
  }
  let t99;
  if ($[279] !== draftKeys || $[280] !== targetKeys) {
    t99 = {
      activeReplyDraftKeys: draftKeys,
      activeReplyTargetKeys: targetKeys
    };
    $[279] = draftKeys;
    $[280] = targetKeys;
    $[281] = t99;
  } else {
    t99 = $[281];
  }
  const {
    activeReplyDraftKeys,
    activeReplyTargetKeys
  } = t99;
  let t100;
  let t101;
  if ($[282] !== codeContentFilenames || $[283] !== prDraftKey || $[284] !== pruneDraftsToExistingFiles || $[285] !== query.data) {
    t100 = () => {
      if (!query.data) {
        return;
      }
      pruneDraftsToExistingFiles(prDraftKey, codeContentFilenames);
    };
    t101 = [codeContentFilenames, pruneDraftsToExistingFiles, prDraftKey, query.data];
    $[282] = codeContentFilenames;
    $[283] = prDraftKey;
    $[284] = pruneDraftsToExistingFiles;
    $[285] = query.data;
    $[286] = t100;
    $[287] = t101;
  } else {
    t100 = $[286];
    t101 = $[287];
  }
  useEffect(t100, t101);
  let t102;
  let t103;
  if ($[288] !== clearActiveReplyTargetsForPr || $[289] !== clearDraftsForPr || $[290] !== clearReplyDraftsForPr || $[291] !== prDraftKey || $[292] !== prReplyDraftKey) {
    t102 = () => () => {
      clearDraftsForPr(prDraftKey);
      clearReplyDraftsForPr(prReplyDraftKey);
      clearActiveReplyTargetsForPr(prReplyDraftKey);
    };
    t103 = [clearActiveReplyTargetsForPr, clearDraftsForPr, clearReplyDraftsForPr, prDraftKey, prReplyDraftKey];
    $[288] = clearActiveReplyTargetsForPr;
    $[289] = clearDraftsForPr;
    $[290] = clearReplyDraftsForPr;
    $[291] = prDraftKey;
    $[292] = prReplyDraftKey;
    $[293] = t102;
    $[294] = t103;
  } else {
    t102 = $[293];
    t103 = $[294];
  }
  useEffect(t102, t103);
  let t104;
  let t105;
  if ($[295] !== activeReplyDraftKeys || $[296] !== prReplyDraftKey || $[297] !== pruneReplyDraftsToExistingKeys) {
    t104 = () => {
      pruneReplyDraftsToExistingKeys(prReplyDraftKey, Array.from(activeReplyDraftKeys));
    };
    t105 = [activeReplyDraftKeys, prReplyDraftKey, pruneReplyDraftsToExistingKeys];
    $[295] = activeReplyDraftKeys;
    $[296] = prReplyDraftKey;
    $[297] = pruneReplyDraftsToExistingKeys;
    $[298] = t104;
    $[299] = t105;
  } else {
    t104 = $[298];
    t105 = $[299];
  }
  useEffect(t104, t105);
  let t106;
  let t107;
  if ($[300] !== activeReplyTargetKeys || $[301] !== prReplyDraftKey || $[302] !== pruneActiveReplyTargetsToExistingKeys) {
    t106 = () => {
      pruneActiveReplyTargetsToExistingKeys(prReplyDraftKey, Array.from(activeReplyTargetKeys));
    };
    t107 = [activeReplyTargetKeys, prReplyDraftKey, pruneActiveReplyTargetsToExistingKeys];
    $[300] = activeReplyTargetKeys;
    $[301] = prReplyDraftKey;
    $[302] = pruneActiveReplyTargetsToExistingKeys;
    $[303] = t106;
    $[304] = t107;
  } else {
    t106 = $[303];
    t107 = $[304];
  }
  useEffect(t106, t107);
  let t108;
  if ($[305] === Symbol.for("react.memo_cache_sentinel")) {
    t108 = filename_2 => {
      setManualActiveFilename(filename_2);
    };
    $[305] = t108;
  } else {
    t108 = $[305];
  }
  const setManualActiveFilenameOverride = t108;
  let t109;
  if ($[306] === Symbol.for("react.memo_cache_sentinel")) {
    t109 = () => {
      if (scrollSyncFrameRef.current == null) {
        return;
      }
      window.cancelAnimationFrame(scrollSyncFrameRef.current);
      scrollSyncFrameRef.current = null;
    };
    $[306] = t109;
  } else {
    t109 = $[306];
  }
  const cancelPendingViewportSync = t109;
  let t110;
  if ($[307] === Symbol.for("react.memo_cache_sentinel")) {
    t110 = () => {
      if (typeof window === "undefined") {
        return;
      }
      if (viewedFileScrollFrameRef.current != null) {
        window.cancelAnimationFrame(viewedFileScrollFrameRef.current);
        viewedFileScrollFrameRef.current = null;
      }
      if (viewedFileScrollTimeoutRef.current != null) {
        window.clearTimeout(viewedFileScrollTimeoutRef.current);
        viewedFileScrollTimeoutRef.current = null;
      }
    };
    $[307] = t110;
  } else {
    t110 = $[307];
  }
  const cancelPendingViewedFileScroll = t110;
  let t111;
  let t112;
  if ($[308] === Symbol.for("react.memo_cache_sentinel")) {
    t111 = () => () => {
      cancelPendingViewedFileScroll();
    };
    t112 = [cancelPendingViewedFileScroll];
    $[308] = t111;
    $[309] = t112;
  } else {
    t111 = $[308];
    t112 = $[309];
  }
  useEffect(t111, t112);
  let map;
  if ($[310] !== allComments) {
    map = new Map();
    allComments.forEach(comment_3 => {
      if (comment_3.path) {
        map.set(comment_3.githubCommentId, comment_3.path);
      }
    });
    $[310] = allComments;
    $[311] = map;
  } else {
    map = $[311];
  }
  const commentIdToFilename = map;
  const scrollToElementWithOffset = _temp54;
  let t113;
  if ($[312] === Symbol.for("react.memo_cache_sentinel")) {
    t113 = {
      viewportTopOffset: PR_V2_BASE_FILE_VIEWPORT_TOP_OFFSET
    };
    $[312] = t113;
  } else {
    t113 = $[312];
  }
  const scrollToCommentWithOffset = usePrV2ScrollToCommentWithOffset(t113);
  let t114;
  if ($[313] === Symbol.for("react.memo_cache_sentinel")) {
    t114 = (filename_3, t115) => {
      const options = t115 === undefined ? {} : t115;
      const element_0 = document.getElementById(getPrV2FileElementId(filename_3));
      if (!element_0) {
        return false;
      }
      scrollToElementWithOffset(element_0, options.applyOffset ?? true);
      return true;
    };
    $[313] = t114;
  } else {
    t114 = $[313];
  }
  const scrollToFileElement = t114;
  let t115;
  if ($[314] === Symbol.for("react.memo_cache_sentinel")) {
    t115 = filename_4 => {
      if (typeof window === "undefined") {
        return;
      }
      cancelPendingViewedFileScroll();
      const scrollToFile = () => {
        viewedFileScrollFrameRef.current = null;
        viewedFileScrollTimeoutRef.current = null;
        if (!scrollToFileElement(filename_4, {
          applyOffset: false
        })) {
          return;
        }
        const element_1 = document.getElementById(getPrV2FileElementId(filename_4));
        if (element_1 instanceof HTMLElement) {
          applyScrollOffsetForElement(element_1, VIEWED_FILE_REANCHOR_TOP_OFFSET_PX);
        }
      };
      if (typeof window.requestAnimationFrame === "function") {
        viewedFileScrollFrameRef.current = window.requestAnimationFrame(scrollToFile);
        return;
      }
      viewedFileScrollTimeoutRef.current = window.setTimeout(scrollToFile, 0);
    };
    $[314] = t115;
  } else {
    t115 = $[314];
  }
  const scrollToFileElementAfterNextFrame = t115;
  let t116;
  if ($[315] === Symbol.for("react.memo_cache_sentinel")) {
    t116 = (groupId, t117) => {
      const options_0 = t117 === undefined ? {} : t117;
      const element_2 = document.getElementById(groupId);
      if (!element_2) {
        return false;
      }
      if (options_0.alignHeaderToTop) {
        element_2.scrollIntoView({
          behavior: "instant",
          block: "start"
        });
        return true;
      }
      scrollToElementWithOffset(element_2);
      return true;
    };
    $[315] = t116;
  } else {
    t116 = $[315];
  }
  const scrollToGroupElement = t116;
  let t117;
  if ($[316] !== scrollToCommentWithOffset) {
    t117 = commentId => {
      for (const elementId of getPrV2ChangesCommentAnchorIds(commentId)) {
        const element_3 = document.getElementById(elementId);
        if (!element_3) {
          continue;
        }
        if (element_3.closest(".hidden")) {
          continue;
        }
        const threadCard = element_3.closest(PR_V2_THREAD_CARD_SELECTOR);
        scrollToCommentWithOffset(threadCard instanceof HTMLElement ? threadCard : element_3);
        return true;
      }
      return false;
    };
    $[316] = scrollToCommentWithOffset;
    $[317] = t117;
  } else {
    t117 = $[317];
  }
  const scrollToCommentElement = t117;
  let t118;
  if ($[318] !== scrollToCommentWithOffset) {
    t118 = filename_5 => {
      const element_4 = findPrV2DraftComposerElement(filename_5);
      if (!element_4) {
        return false;
      }
      scrollToCommentWithOffset(element_4);
      return true;
    };
    $[318] = scrollToCommentWithOffset;
    $[319] = t118;
  } else {
    t118 = $[319];
  }
  const scrollToDraftComposerElement = t118;
  let t119;
  if ($[320] !== chapterComparisonKey || $[321] !== chapterEntryByKey || $[322] !== isLargePrChapterMode || $[323] !== resolvedActiveChapterKey) {
    t119 = (chapterKey, anchorFilename) => {
      if (!isLargePrChapterMode) {
        return false;
      }
      const entry_0 = chapterEntryByKey.get(chapterKey);
      if (!entry_0) {
        return false;
      }
      const didSwitch = resolvedActiveChapterKey !== entry_0.key;
      setChapterModeState({
        activeChapterKey: entry_0.key,
        anchorFilename: anchorFilename ?? entry_0.firstFilename,
        comparisonKey: chapterComparisonKey
      });
      return didSwitch;
    };
    $[320] = chapterComparisonKey;
    $[321] = chapterEntryByKey;
    $[322] = isLargePrChapterMode;
    $[323] = resolvedActiveChapterKey;
    $[324] = t119;
  } else {
    t119 = $[324];
  }
  const selectChapterByKey = t119;
  let t120;
  if ($[325] !== chapterKeyByFilename || $[326] !== isLargePrChapterMode || $[327] !== revealTestFile || $[328] !== selectChapterByKey) {
    t120 = filename_6 => {
      if (revealTestFile(filename_6)) {
        return true;
      }
      if (!isLargePrChapterMode) {
        return false;
      }
      const chapterKey_0 = chapterKeyByFilename.get(filename_6);
      if (!chapterKey_0) {
        return false;
      }
      return selectChapterByKey(chapterKey_0, filename_6);
    };
    $[325] = chapterKeyByFilename;
    $[326] = isLargePrChapterMode;
    $[327] = revealTestFile;
    $[328] = selectChapterByKey;
    $[329] = t120;
  } else {
    t120 = $[329];
  }
  const selectChapterForFilename = t120;
  let t121;
  if ($[330] !== chapterKeyByFilename) {
    t121 = filename_7 => chapterKeyByFilename.get(filename_7) ?? null;
    $[330] = chapterKeyByFilename;
    $[331] = t121;
  } else {
    t121 = $[331];
  }
  const getChapterKeyForFilename = t121;
  let t122;
  if ($[332] !== scrollToDraftComposerElement || $[333] !== selectChapterForFilename || $[334] !== setFileCollapsed) {
    t122 = (filename_8, context) => {
      if (!filename_8) {
        return false;
      }
      if (context?.expandFile) {
        setFileCollapsed(filename_8, false);
      }
      cancelPendingViewportSync();
      setManualActiveFilenameOverride(filename_8);
      const didSwitchChapter = selectChapterForFilename(filename_8);
      if (context?.scrollToDraft) {
        if (!didSwitchChapter && scrollToDraftComposerElement(filename_8)) {
          return true;
        }
        if (!didSwitchChapter) {
          scrollToFileElement(filename_8);
        }
        setPendingScrollTarget({
          type: "draft",
          filename: filename_8
        });
        return true;
      }
      if (context?.suppressScroll) {
        return true;
      }
      if (!didSwitchChapter && scrollToFileElement(filename_8)) {
        return true;
      }
      setPendingScrollTarget({
        type: "file",
        filename: filename_8
      });
      return true;
    };
    $[332] = scrollToDraftComposerElement;
    $[333] = selectChapterForFilename;
    $[334] = setFileCollapsed;
    $[335] = t122;
  } else {
    t122 = $[335];
  }
  const navigateToFile = t122;
  let t123;
  if ($[336] !== navigateToFile) {
    t123 = filename_9 => {
      navigateToFile(filename_9, {
        expandFile: true,
        suppressScroll: true
      });
      setPendingScrollTarget({
        type: "file",
        filename: filename_9
      });
    };
    $[336] = navigateToFile;
    $[337] = t123;
  } else {
    t123 = $[337];
  }
  const onSelectGroupFile = t123;
  let t124;
  if ($[338] !== renderedFilenames) {
    t124 = () => {
      if (typeof window === "undefined") {
        return null;
      }
      const {
        viewportTopOffset,
        viewportBottom,
        sections
      } = measurePrV2FileSections(renderedFilenames);
      let topVisibleFilename = null;
      let topVisibleOffset = Number.POSITIVE_INFINITY;
      for (const {
        filename: filename_10,
        top,
        bottom
      } of sections) {
        const isVisible = bottom > viewportTopOffset && top < viewportBottom;
        if (isVisible && top < topVisibleOffset) {
          topVisibleOffset = top;
          topVisibleFilename = filename_10;
        }
      }
      return topVisibleFilename;
    };
    $[338] = renderedFilenames;
    $[339] = t124;
  } else {
    t124 = $[339];
  }
  const getTopVisibleRenderedFilename = t124;
  let t125;
  if ($[340] !== renderedFilenames) {
    t125 = () => {
      if (typeof window === "undefined" || suppressNextViewportActiveFilenameSyncRef.current) {
        suppressNextViewportActiveFilenameSyncRef.current = false;
        setManualActiveFilename(current_1 => resolveActiveFilename(current_1, renderedFilenames));
        return;
      }
      const {
        viewportTopOffset: viewportTopOffset_0,
        viewportBottom: viewportBottom_0,
        sections: sections_0
      } = measurePrV2FileSections(renderedFilenames);
      setManualActiveFilename(current_2 => resolveViewportActiveFilename({
        currentFilename: current_2,
        renderedFilenames,
        viewportTopOffset: viewportTopOffset_0,
        activationThreshold: FILE_VIEWPORT_ACTIVATION_THRESHOLD,
        viewportBottom: viewportBottom_0,
        candidates: sections_0
      }));
    };
    $[340] = renderedFilenames;
    $[341] = t125;
  } else {
    t125 = $[341];
  }
  const syncActiveFilenameToViewport = t125;
  let t126;
  if ($[342] !== syncActiveFilenameToViewport) {
    t126 = () => {
      if (typeof window === "undefined") {
        return;
      }
      if (scrollSyncFrameRef.current != null) {
        return;
      }
      const runViewportSync = () => {
        scrollSyncFrameRef.current = null;
        syncActiveFilenameToViewport();
      };
      if (typeof window.requestAnimationFrame !== "function") {
        runViewportSync();
        return;
      }
      scrollSyncFrameRef.current = window.requestAnimationFrame(runViewportSync);
    };
    $[342] = syncActiveFilenameToViewport;
    $[343] = t126;
  } else {
    t126 = $[343];
  }
  const scheduleActiveFilenameViewportSync = t126;
  const isFilenameVisibleInViewport = _temp55;
  let t127;
  if ($[344] !== renderedFilenameSet) {
    t127 = target => {
      if (!(target instanceof Element)) {
        return null;
      }
      const candidate = target.closest("[data-filename]");
      if (!(candidate instanceof HTMLElement)) {
        return null;
      }
      const filename_12 = candidate.getAttribute("data-filename");
      return filename_12 && renderedFilenameSet.has(filename_12) ? filename_12 : null;
    };
    $[344] = renderedFilenameSet;
    $[345] = t127;
  } else {
    t127 = $[345];
  }
  const getHotkeyTargetFilename = t127;
  let t128;
  if ($[346] !== activeFilename || $[347] !== getHotkeyTargetFilename || $[348] !== getTopVisibleRenderedFilename) {
    t128 = target_0 => {
      const targetFilename = getHotkeyTargetFilename(target_0 ?? null);
      if (!activeFilename && targetFilename) {
        return targetFilename;
      }
      const topVisibleFilename_0 = getTopVisibleRenderedFilename();
      const isActiveVisible = isFilenameVisibleInViewport(activeFilename);
      return resolveKeyboardAnchorFilename({
        activeFilename,
        topVisibleFilename: topVisibleFilename_0,
        isActiveVisible
      });
    };
    $[346] = activeFilename;
    $[347] = getHotkeyTargetFilename;
    $[348] = getTopVisibleRenderedFilename;
    $[349] = t128;
  } else {
    t128 = $[349];
  }
  const getKeyboardTargetFilename = t128;
  let t129;
  if ($[350] !== activeFilename || $[351] !== getKeyboardTargetFilename || $[352] !== navigateToFile || $[353] !== renderedFilenames) {
    t129 = (offset, target_1) => {
      const anchorFilename_0 = getKeyboardTargetFilename(target_1);
      if (activeFilename && anchorFilename_0 && anchorFilename_0 !== activeFilename) {
        navigateToFile(anchorFilename_0);
        return true;
      }
      const nextFilename = getNextFilenameInOrder(renderedFilenames, anchorFilename_0, offset);
      if (!nextFilename || nextFilename === anchorFilename_0) {
        return false;
      }
      navigateToFile(nextFilename);
      return true;
    };
    $[350] = activeFilename;
    $[351] = getKeyboardTargetFilename;
    $[352] = navigateToFile;
    $[353] = renderedFilenames;
    $[354] = t129;
  } else {
    t129 = $[354];
  }
  const navigateBetweenFiles = t129;
  let t130;
  if ($[355] !== renderedFilenameSet || $[356] !== toggleViewed) {
    t130 = (filename_13, isCurrentlyViewed) => {
      if (!filename_13 || !renderedFilenameSet.has(filename_13)) {
        return false;
      }
      setManualActiveFilenameOverride(filename_13);
      cancelPendingViewedFileScroll();
      toggleViewed(filename_13);
      if (!isCurrentlyViewed) {
        cancelPendingViewportSync();
        scrollToFileElementAfterNextFrame(filename_13);
      }
      return true;
    };
    $[355] = renderedFilenameSet;
    $[356] = toggleViewed;
    $[357] = t130;
  } else {
    t130 = $[357];
  }
  const toggleViewedForFile = t130;
  let t131;
  if ($[358] !== renderedFilenameSet || $[359] !== setFilesViewedState) {
    t131 = (filenames_0, viewed) => {
      const targetFilenames = Array.from(new Set(filenames_0)).filter(filename_14 => renderedFilenameSet.has(filename_14));
      if (targetFilenames.length === 0) {
        return false;
      }
      const firstFilename = targetFilenames[0];
      if (firstFilename) {
        setManualActiveFilenameOverride(firstFilename);
      }
      setFilesViewedState(targetFilenames, viewed);
      return true;
    };
    $[358] = renderedFilenameSet;
    $[359] = setFilesViewedState;
    $[360] = t131;
  } else {
    t131 = $[360];
  }
  const setFilesViewedFromBridge = t131;
  let t132;
  if ($[361] !== clearDraftForFile || $[362] !== openFileDraft || $[363] !== prDraftKey || $[364] !== setFileCollapsed) {
    t132 = filename_15 => {
      if (!filename_15) {
        return false;
      }
      const currentDraft = usePrV2DraftStore.getState().getDraft(prDraftKey, filename_15);
      const isFileLevelDraftOpen = Boolean(currentDraft && !asPositiveInteger(currentDraft.line || null));
      setManualActiveFilenameOverride(filename_15);
      if (isFileLevelDraftOpen) {
        clearDraftForFile(prDraftKey, filename_15);
        return true;
      }
      setFileCollapsed(filename_15, false);
      openFileDraft(prDraftKey, filename_15);
      return true;
    };
    $[361] = clearDraftForFile;
    $[362] = openFileDraft;
    $[363] = prDraftKey;
    $[364] = setFileCollapsed;
    $[365] = t132;
  } else {
    t132 = $[365];
  }
  const toggleFileDraft = t132;
  let t133;
  if ($[366] !== getKeyboardTargetFilename || $[367] !== isViewed || $[368] !== toggleViewedForFile) {
    t133 = () => {
      const targetFilename_0 = getKeyboardTargetFilename();
      if (!targetFilename_0) {
        return false;
      }
      return toggleViewedForFile(targetFilename_0, isViewed(targetFilename_0));
    };
    $[366] = getKeyboardTargetFilename;
    $[367] = isViewed;
    $[368] = toggleViewedForFile;
    $[369] = t133;
  } else {
    t133 = $[369];
  }
  const toggleActiveFileViewed = t133;
  let t134;
  if ($[370] !== effectiveHeadSha || $[371] !== getKeyboardTargetFilename || $[372] !== isMutating || $[373] !== isReadOnly || $[374] !== toggleFileDraft) {
    t134 = () => {
      const targetFilename_1 = getKeyboardTargetFilename();
      if (isReadOnly || !targetFilename_1 || !effectiveHeadSha || isMutating) {
        return false;
      }
      return toggleFileDraft(targetFilename_1);
    };
    $[370] = effectiveHeadSha;
    $[371] = getKeyboardTargetFilename;
    $[372] = isMutating;
    $[373] = isReadOnly;
    $[374] = toggleFileDraft;
    $[375] = t134;
  } else {
    t134 = $[375];
  }
  const toggleActiveFileDraft = t134;
  let t135;
  if ($[376] !== getKeyboardTargetFilename || $[377] !== isCollapsed || $[378] !== setFileCollapsed) {
    t135 = collapsed_0 => {
      const targetFilename_2 = getKeyboardTargetFilename();
      if (!targetFilename_2) {
        return false;
      }
      if (isCollapsed(targetFilename_2) === collapsed_0) {
        return false;
      }
      setManualActiveFilenameOverride(targetFilename_2);
      setFileCollapsed(targetFilename_2, collapsed_0);
      return true;
    };
    $[376] = getKeyboardTargetFilename;
    $[377] = isCollapsed;
    $[378] = setFileCollapsed;
    $[379] = t135;
  } else {
    t135 = $[379];
  }
  const setActiveFileCollapsed = t135;
  let t136;
  let t137;
  if ($[380] !== scheduleActiveFilenameViewportSync) {
    t136 = () => {
      if (typeof window === "undefined") {
        return;
      }
      const scrollContainer = document.getElementById("scrollable-content");
      const handleViewportChange = () => {
        scheduleActiveFilenameViewportSync();
      };
      window.addEventListener("resize", handleViewportChange);
      if (scrollContainer instanceof HTMLElement) {
        scrollContainer.addEventListener("scroll", handleViewportChange, {
          passive: true
        });
        return () => {
          window.removeEventListener("resize", handleViewportChange);
          scrollContainer.removeEventListener("scroll", handleViewportChange);
          if (scrollSyncFrameRef.current != null) {
            window.cancelAnimationFrame(scrollSyncFrameRef.current);
            scrollSyncFrameRef.current = null;
          }
        };
      }
      window.addEventListener("scroll", handleViewportChange, {
        passive: true
      });
      return () => {
        window.removeEventListener("resize", handleViewportChange);
        window.removeEventListener("scroll", handleViewportChange);
        if (scrollSyncFrameRef.current != null) {
          window.cancelAnimationFrame(scrollSyncFrameRef.current);
          scrollSyncFrameRef.current = null;
        }
      };
    };
    t137 = [scheduleActiveFilenameViewportSync];
    $[380] = scheduleActiveFilenameViewportSync;
    $[381] = t136;
    $[382] = t137;
  } else {
    t136 = $[381];
    t137 = $[382];
  }
  useEffect(t136, t137);
  let t138;
  if ($[383] !== setPrefersReducedMotion) {
    t138 = () => {
      if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
        return;
      }
      const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
      const syncReducedMotionPreference = () => {
        setPrefersReducedMotion(mediaQuery.matches);
      };
      syncReducedMotionPreference();
      if (typeof mediaQuery.addEventListener === "function") {
        mediaQuery.addEventListener("change", syncReducedMotionPreference);
        return () => {
          mediaQuery.removeEventListener("change", syncReducedMotionPreference);
        };
      }
      mediaQuery.addListener?.(syncReducedMotionPreference);
      return () => {
        mediaQuery.removeListener?.(syncReducedMotionPreference);
      };
    };
    $[383] = setPrefersReducedMotion;
    $[384] = t138;
  } else {
    t138 = $[384];
  }
  let t139;
  if ($[385] === Symbol.for("react.memo_cache_sentinel")) {
    t139 = [];
    $[385] = t139;
  } else {
    t139 = $[385];
  }
  useEffect(t138, t139);
  let t140;
  if ($[386] !== prefersReducedMotion) {
    t140 = direction => {
      if (typeof window === "undefined") {
        return;
      }
      const distance = Math.max(120, Math.round(window.innerHeight * 0.5));
      const behavior = prefersReducedMotion ? "auto" : "smooth";
      const scrollContainer_0 = document.getElementById("scrollable-content");
      if (scrollContainer_0 instanceof HTMLElement) {
        scrollContainer_0.scrollBy({
          left: 0,
          top: direction === "down" ? distance : -distance,
          behavior
        });
        return;
      }
      window.scrollBy({
        left: 0,
        top: direction === "down" ? distance : -distance,
        behavior
      });
    };
    $[386] = prefersReducedMotion;
    $[387] = t140;
  } else {
    t140 = $[387];
  }
  const scrollViewportByHalfPage = t140;
  let t141;
  if ($[388] !== chapterKeyByGroupId || $[389] !== markGroupJump || $[390] !== selectChapterByKey) {
    t141 = (groupId_0, t142) => {
      const options_1 = t142 === undefined ? {} : t142;
      if (!groupId_0) {
        return false;
      }
      suppressNextViewportActiveFilenameSyncRef.current = true;
      cancelPendingViewportSync();
      setManualActiveFilename(null);
      markGroupJump(groupId_0);
      const chapterKey_1 = chapterKeyByGroupId.get(groupId_0);
      const didSwitchChapter_0 = chapterKey_1 ? selectChapterByKey(chapterKey_1) : false;
      if (!didSwitchChapter_0 && scrollToGroupElement(groupId_0, options_1)) {
        return true;
      }
      setPendingScrollTarget({
        type: "group",
        groupId: groupId_0,
        ...options_1
      });
      return true;
    };
    $[388] = chapterKeyByGroupId;
    $[389] = markGroupJump;
    $[390] = selectChapterByKey;
    $[391] = t141;
  } else {
    t141 = $[391];
  }
  const navigateToGroup = t141;
  const t142 = versionsQuery.data !== undefined;
  let t143;
  if ($[392] !== baseSha || $[393] !== commentIdToFilename || $[394] !== comparisonKey || $[395] !== effectiveBaseSha || $[396] !== effectiveHeadSha || $[397] !== pendingScrollTarget || $[398] !== scrollToCommentElement || $[399] !== selectChapterForFilename || $[400] !== setComparison || $[401] !== setFileCollapsed || $[402] !== showComparisonLoadingPanel || $[403] !== t142 || $[404] !== versionOptions || $[405] !== versionsQuery.isError || $[406] !== versionsQuery.isFetching || $[407] !== versionsQuery.isLoading || $[408] !== visibleHeadSha) {
    t143 = {
      canonicalBaseSha: baseSha,
      canonicalHeadSha: visibleHeadSha,
      currentBaseSha: effectiveBaseSha,
      currentHeadSha: effectiveHeadSha,
      comparisonKey,
      versionOptions,
      versionsLoaded: t142,
      versionsLoading: versionsQuery.isLoading,
      versionsFetching: versionsQuery.isFetching,
      versionsError: versionsQuery.isError,
      showComparisonLoadingPanel,
      pendingScrollTarget,
      setPendingScrollTarget,
      setComparison,
      commentIdToFilename,
      selectChapterForFilename,
      scrollToCommentElement,
      scrollToFileElement,
      setFileCollapsed,
      setManualActiveFilenameOverride
    };
    $[392] = baseSha;
    $[393] = commentIdToFilename;
    $[394] = comparisonKey;
    $[395] = effectiveBaseSha;
    $[396] = effectiveHeadSha;
    $[397] = pendingScrollTarget;
    $[398] = scrollToCommentElement;
    $[399] = selectChapterForFilename;
    $[400] = setComparison;
    $[401] = setFileCollapsed;
    $[402] = showComparisonLoadingPanel;
    $[403] = t142;
    $[404] = versionOptions;
    $[405] = versionsQuery.isError;
    $[406] = versionsQuery.isFetching;
    $[407] = versionsQuery.isLoading;
    $[408] = visibleHeadSha;
    $[409] = t143;
  } else {
    t143 = $[409];
  }
  const {
    navigateToComment,
    isPendingScrollTargetReady
  } = usePrV2CommentNavigation(t143);
  let t144;
  let t145;
  if ($[410] !== clearDiffNavigationHandlers || $[411] !== getTopVisibleRenderedFilename || $[412] !== navigateToComment || $[413] !== navigateToFile || $[414] !== navigateToGroup || $[415] !== setDiffNavigationHandlers || $[416] !== setFilesViewedFromBridge) {
    t144 = () => {
      setDiffNavigationHandlers({
        getViewportAnchorFilename: getTopVisibleRenderedFilename,
        navigateToFile,
        navigateToGroup,
        navigateToComment,
        setFilesViewed: setFilesViewedFromBridge
      });
      return () => {
        clearDiffNavigationHandlers();
      };
    };
    t145 = [clearDiffNavigationHandlers, getTopVisibleRenderedFilename, navigateToComment, navigateToFile, navigateToGroup, setFilesViewedFromBridge, setDiffNavigationHandlers];
    $[410] = clearDiffNavigationHandlers;
    $[411] = getTopVisibleRenderedFilename;
    $[412] = navigateToComment;
    $[413] = navigateToFile;
    $[414] = navigateToGroup;
    $[415] = setDiffNavigationHandlers;
    $[416] = setFilesViewedFromBridge;
    $[417] = t144;
    $[418] = t145;
  } else {
    t144 = $[417];
    t145 = $[418];
  }
  useEffect(t144, t145);
  let t146;
  let t147;
  if ($[419] !== allGroupedCodeContent || $[420] !== diffStatus || $[421] !== emptyComparisonMessage || $[422] !== navigateToFile || $[423] !== viewedFiles) {
    t146 = () => buildPrV2FileRows({
      status: diffStatus,
      emptyMessage: emptyComparisonMessage,
      groups: allGroupedCodeContent,
      viewedFiles,
      navigateToFile
    });
    t147 = [allGroupedCodeContent, diffStatus, emptyComparisonMessage, navigateToFile, viewedFiles];
    $[419] = allGroupedCodeContent;
    $[420] = diffStatus;
    $[421] = emptyComparisonMessage;
    $[422] = navigateToFile;
    $[423] = viewedFiles;
    $[424] = t146;
    $[425] = t147;
  } else {
    t146 = $[424];
    t147 = $[425];
  }
  useRegisterCommands(t146, t147);
  let t148;
  if ($[426] !== chapterEntries) {
    t148 = chapterEntries.flatMap(_temp57);
    $[426] = chapterEntries;
    $[427] = t148;
  } else {
    t148 = $[427];
  }
  const reviewOrderFilenames = t148;
  let t149;
  if ($[428] !== activeFilename || $[429] !== navigateToFile || $[430] !== renderedFilenames[0] || $[431] !== reviewOrderFilenames || $[432] !== setAllRenderedFilesCollapsed || $[433] !== showComparisonLoadingPanel || $[434] !== toggleViewedForFile || $[435] !== viewedFiles) {
    t149 = () => buildPrV2ViewedCommands({
      isLoading: showComparisonLoadingPanel,
      activeFilename,
      firstRenderedFilename: renderedFilenames[0],
      reviewOrderFilenames,
      viewedFiles,
      navigateToFile,
      toggleViewedForFile,
      setAllRenderedFilesCollapsed
    });
    $[428] = activeFilename;
    $[429] = navigateToFile;
    $[430] = renderedFilenames[0];
    $[431] = reviewOrderFilenames;
    $[432] = setAllRenderedFilesCollapsed;
    $[433] = showComparisonLoadingPanel;
    $[434] = toggleViewedForFile;
    $[435] = viewedFiles;
    $[436] = t149;
  } else {
    t149 = $[436];
  }
  let t150;
  if ($[437] !== activeFilename || $[438] !== navigateToFile || $[439] !== renderedFilenames || $[440] !== reviewOrderFilenames || $[441] !== setAllRenderedFilesCollapsed || $[442] !== showComparisonLoadingPanel || $[443] !== toggleViewedForFile || $[444] !== viewedFiles) {
    t150 = [activeFilename, navigateToFile, renderedFilenames, reviewOrderFilenames, setAllRenderedFilesCollapsed, showComparisonLoadingPanel, toggleViewedForFile, viewedFiles];
    $[437] = activeFilename;
    $[438] = navigateToFile;
    $[439] = renderedFilenames;
    $[440] = reviewOrderFilenames;
    $[441] = setAllRenderedFilesCollapsed;
    $[442] = showComparisonLoadingPanel;
    $[443] = toggleViewedForFile;
    $[444] = viewedFiles;
    $[445] = t150;
  } else {
    t150 = $[445];
  }
  useRegisterCommands(t149, t150);
  let t151;
  if ($[446] !== defaultComparison.lastReviewedVersionSha || $[447] !== effectiveBaseSha || $[448] !== effectiveHeadSha || $[449] !== versionOptions) {
    t151 = getPrV2ComparisonChoices({
      versionOptions,
      selectedBaseSha: effectiveBaseSha,
      selectedHeadSha: effectiveHeadSha,
      lastReviewedVersionSha: defaultComparison.lastReviewedVersionSha
    });
    $[446] = defaultComparison.lastReviewedVersionSha;
    $[447] = effectiveBaseSha;
    $[448] = effectiveHeadSha;
    $[449] = versionOptions;
    $[450] = t151;
  } else {
    t151 = $[450];
  }
  const comparisonChoices = t151;
  const latestPushLabel = versionUpdate?.latestLabel ?? null;
  let t152;
  let t153;
  if ($[451] !== comparisonChoices || $[452] !== hasMeaningfulComparison || $[453] !== isComparisonLoading || $[454] !== latestPushLabel || $[455] !== reviewLatestHead || $[456] !== setComparison) {
    t152 = () => buildPrV2CompareCommands({
      choices: hasMeaningfulComparison ? comparisonChoices : null,
      isComparisonLoading,
      setComparison,
      latestPush: latestPushLabel === null ? null : {
        label: latestPushLabel,
        onReviewLatest: reviewLatestHead
      }
    });
    t153 = [comparisonChoices, hasMeaningfulComparison, isComparisonLoading, latestPushLabel, reviewLatestHead, setComparison];
    $[451] = comparisonChoices;
    $[452] = hasMeaningfulComparison;
    $[453] = isComparisonLoading;
    $[454] = latestPushLabel;
    $[455] = reviewLatestHead;
    $[456] = setComparison;
    $[457] = t152;
    $[458] = t153;
  } else {
    t152 = $[457];
    t153 = $[458];
  }
  useRegisterCommands(t152, t153);
  let t154;
  if ($[459] !== navigateBetweenFiles) {
    t154 = event => {
      if (isPrV2HotkeyInteractiveTarget(event.target) && !isPrV2FileNavigationHotkeyTarget(event.target)) {
        return;
      }
      event.preventDefault();
      navigateBetweenFiles(1, event.target);
    };
    $[459] = navigateBetweenFiles;
    $[460] = t154;
  } else {
    t154 = $[460];
  }
  let t155;
  if ($[461] === Symbol.for("react.memo_cache_sentinel")) {
    t155 = {
      enableOnFormTags: true,
      preventDefault: false
    };
    $[461] = t155;
  } else {
    t155 = $[461];
  }
  let t156;
  if ($[462] !== navigateBetweenFiles) {
    t156 = [navigateBetweenFiles];
    $[462] = navigateBetweenFiles;
    $[463] = t156;
  } else {
    t156 = $[463];
  }
  useHotkeys("j", t154, t155, t156);
  let t157;
  if ($[464] !== navigateBetweenFiles) {
    t157 = event_0 => {
      if (isPrV2HotkeyInteractiveTarget(event_0.target) && !isPrV2FileNavigationHotkeyTarget(event_0.target)) {
        return;
      }
      event_0.preventDefault();
      navigateBetweenFiles(-1, event_0.target);
    };
    $[464] = navigateBetweenFiles;
    $[465] = t157;
  } else {
    t157 = $[465];
  }
  let t158;
  if ($[466] === Symbol.for("react.memo_cache_sentinel")) {
    t158 = {
      enableOnFormTags: true,
      preventDefault: false
    };
    $[466] = t158;
  } else {
    t158 = $[466];
  }
  let t159;
  if ($[467] !== navigateBetweenFiles) {
    t159 = [navigateBetweenFiles];
    $[467] = navigateBetweenFiles;
    $[468] = t159;
  } else {
    t159 = $[468];
  }
  useHotkeys("k", t157, t158, t159);
  let t160;
  if ($[469] !== navigateBetweenFiles) {
    t160 = event_1 => {
      if (event_1.shiftKey || event_1.altKey || event_1.ctrlKey || event_1.metaKey || isPrV2HotkeyInteractiveTarget(event_1.target) && !isPrV2FileNavigationHotkeyTarget(event_1.target)) {
        return;
      }
      event_1.preventDefault();
      navigateBetweenFiles(1, event_1.target);
    };
    $[469] = navigateBetweenFiles;
    $[470] = t160;
  } else {
    t160 = $[470];
  }
  let t161;
  if ($[471] === Symbol.for("react.memo_cache_sentinel")) {
    t161 = {
      enableOnFormTags: true,
      preventDefault: false
    };
    $[471] = t161;
  } else {
    t161 = $[471];
  }
  let t162;
  if ($[472] !== navigateBetweenFiles) {
    t162 = [navigateBetweenFiles];
    $[472] = navigateBetweenFiles;
    $[473] = t162;
  } else {
    t162 = $[473];
  }
  useHotkeys("down", t160, t161, t162);
  let t163;
  if ($[474] !== navigateBetweenFiles) {
    t163 = event_2 => {
      if (event_2.shiftKey || event_2.altKey || event_2.ctrlKey || event_2.metaKey || isPrV2HotkeyInteractiveTarget(event_2.target) && !isPrV2FileNavigationHotkeyTarget(event_2.target)) {
        return;
      }
      event_2.preventDefault();
      navigateBetweenFiles(-1, event_2.target);
    };
    $[474] = navigateBetweenFiles;
    $[475] = t163;
  } else {
    t163 = $[475];
  }
  let t164;
  if ($[476] === Symbol.for("react.memo_cache_sentinel")) {
    t164 = {
      enableOnFormTags: true,
      preventDefault: false
    };
    $[476] = t164;
  } else {
    t164 = $[476];
  }
  let t165;
  if ($[477] !== navigateBetweenFiles) {
    t165 = [navigateBetweenFiles];
    $[477] = navigateBetweenFiles;
    $[478] = t165;
  } else {
    t165 = $[478];
  }
  useHotkeys("up", t163, t164, t165);
  let t166;
  let t167;
  if ($[479] !== scrollViewportByHalfPage) {
    t166 = () => {
      const handleShiftViewportScroll = event_3 => {
        if (!event_3.shiftKey || event_3.altKey || event_3.ctrlKey || event_3.metaKey || isPrV2HotkeyTextEntryTarget(event_3.target)) {
          return;
        }
        const normalizedKey = event_3.key.toLowerCase();
        if (normalizedKey !== "j" && normalizedKey !== "k" && normalizedKey !== "arrowdown" && normalizedKey !== "arrowup") {
          return;
        }
        event_3.preventDefault();
        scrollViewportByHalfPage(normalizedKey === "j" || normalizedKey === "arrowdown" ? "down" : "up");
      };
      document.addEventListener("keydown", handleShiftViewportScroll, true);
      return () => {
        document.removeEventListener("keydown", handleShiftViewportScroll, true);
      };
    };
    t167 = [scrollViewportByHalfPage];
    $[479] = scrollViewportByHalfPage;
    $[480] = t166;
    $[481] = t167;
  } else {
    t166 = $[480];
    t167 = $[481];
  }
  useEffect(t166, t167);
  let t168;
  if ($[482] !== toggleActiveFileViewed) {
    t168 = event_4 => {
      if (event_4.shiftKey || isPrV2HotkeyInteractiveTarget(event_4.target)) {
        return;
      }
      if (!toggleActiveFileViewed()) {
        return;
      }
      event_4.preventDefault();
    };
    $[482] = toggleActiveFileViewed;
    $[483] = t168;
  } else {
    t168 = $[483];
  }
  let t169;
  if ($[484] === Symbol.for("react.memo_cache_sentinel")) {
    t169 = {
      enableOnFormTags: true,
      preventDefault: false
    };
    $[484] = t169;
  } else {
    t169 = $[484];
  }
  let t170;
  if ($[485] !== toggleActiveFileViewed) {
    t170 = [toggleActiveFileViewed];
    $[485] = toggleActiveFileViewed;
    $[486] = t170;
  } else {
    t170 = $[486];
  }
  useHotkeys("space", t168, t169, t170);
  let t171;
  if ($[487] !== setActiveFileCollapsed) {
    t171 = event_5 => {
      if (event_5.shiftKey || event_5.altKey || event_5.ctrlKey || event_5.metaKey || isPrV2HotkeyInteractiveTarget(event_5.target)) {
        return;
      }
      if (!setActiveFileCollapsed(true)) {
        return;
      }
      event_5.preventDefault();
    };
    $[487] = setActiveFileCollapsed;
    $[488] = t171;
  } else {
    t171 = $[488];
  }
  let t172;
  if ($[489] === Symbol.for("react.memo_cache_sentinel")) {
    t172 = {
      enableOnFormTags: true,
      preventDefault: false
    };
    $[489] = t172;
  } else {
    t172 = $[489];
  }
  let t173;
  if ($[490] !== setActiveFileCollapsed) {
    t173 = [setActiveFileCollapsed];
    $[490] = setActiveFileCollapsed;
    $[491] = t173;
  } else {
    t173 = $[491];
  }
  useHotkeys("left", t171, t172, t173);
  let t174;
  if ($[492] !== setActiveFileCollapsed) {
    t174 = event_6 => {
      if (event_6.shiftKey || event_6.altKey || event_6.ctrlKey || event_6.metaKey || isPrV2HotkeyInteractiveTarget(event_6.target)) {
        return;
      }
      if (!setActiveFileCollapsed(false)) {
        return;
      }
      event_6.preventDefault();
    };
    $[492] = setActiveFileCollapsed;
    $[493] = t174;
  } else {
    t174 = $[493];
  }
  let t175;
  if ($[494] === Symbol.for("react.memo_cache_sentinel")) {
    t175 = {
      enableOnFormTags: true,
      preventDefault: false
    };
    $[494] = t175;
  } else {
    t175 = $[494];
  }
  let t176;
  if ($[495] !== setActiveFileCollapsed) {
    t176 = [setActiveFileCollapsed];
    $[495] = setActiveFileCollapsed;
    $[496] = t176;
  } else {
    t176 = $[496];
  }
  useHotkeys("right", t174, t175, t176);
  let t177;
  if ($[497] !== toggleActiveFileDraft) {
    t177 = event_7 => {
      if (event_7.repeat || event_7.shiftKey || event_7.altKey || event_7.ctrlKey || event_7.metaKey || isPrV2HotkeyInteractiveTarget(event_7.target)) {
        return;
      }
      if (!toggleActiveFileDraft()) {
        return;
      }
      event_7.preventDefault();
    };
    $[497] = toggleActiveFileDraft;
    $[498] = t177;
  } else {
    t177 = $[498];
  }
  let t178;
  if ($[499] === Symbol.for("react.memo_cache_sentinel")) {
    t178 = {
      enableOnFormTags: true,
      preventDefault: false
    };
    $[499] = t178;
  } else {
    t178 = $[499];
  }
  let t179;
  if ($[500] !== toggleActiveFileDraft) {
    t179 = [toggleActiveFileDraft];
    $[500] = toggleActiveFileDraft;
    $[501] = t179;
  } else {
    t179 = $[501];
  }
  useHotkeys("c", t177, t178, t179);
  let t180;
  let t181;
  if ($[502] !== canPaintDiffs || $[503] !== chapterKeyByGroupId || $[504] !== commentIdToFilename || $[505] !== isPendingScrollTargetReady || $[506] !== pendingScrollTarget || $[507] !== query.isLoading || $[508] !== scrollToCommentElement || $[509] !== scrollToDraftComposerElement || $[510] !== selectChapterByKey || $[511] !== selectChapterForFilename || $[512] !== setFileCollapsed) {
    t180 = () => {
      if (!pendingScrollTarget) {
        return;
      }
      if (!isPendingScrollTargetReady || query.isLoading || !canPaintDiffs) {
        return;
      }
      let attempts = 0;
      let cancelled = false;
      let timeoutId = null;
      const tryScroll = () => {
        if (cancelled) {
          return;
        }
        let didScroll;
        if (pendingScrollTarget.type === "file") {
          const didSwitchChapter_1 = selectChapterForFilename(pendingScrollTarget.filename);
          didScroll = !didSwitchChapter_1 && scrollToFileElement(pendingScrollTarget.filename);
        } else {
          if (pendingScrollTarget.type === "group") {
            const chapterKey_2 = chapterKeyByGroupId.get(pendingScrollTarget.groupId);
            const didSwitchChapter_2 = chapterKey_2 ? selectChapterByKey(chapterKey_2) : false;
            didScroll = !didSwitchChapter_2 && scrollToGroupElement(pendingScrollTarget.groupId, {
              alignHeaderToTop: pendingScrollTarget.alignHeaderToTop
            });
          } else {
            if (pendingScrollTarget.type === "comment") {
              const resolvedFilename = pendingScrollTarget.filename ?? commentIdToFilename.get(pendingScrollTarget.commentId) ?? undefined;
              if (!pendingScrollTarget.filename && resolvedFilename) {
                setPendingScrollTarget(currentTarget => {
                  if (currentTarget?.type !== "comment" || currentTarget.commentId !== pendingScrollTarget.commentId || currentTarget.filename === resolvedFilename) {
                    return currentTarget;
                  }
                  return {
                    ...currentTarget,
                    filename: resolvedFilename
                  };
                });
              }
              if (resolvedFilename) {
                setManualActiveFilenameOverride(resolvedFilename);
              }
              const didSwitchChapter_3 = resolvedFilename ? selectChapterForFilename(resolvedFilename) : false;
              didScroll = !didSwitchChapter_3 && scrollToCommentElement(pendingScrollTarget.commentId);
              if (!didScroll) {
                dispatchPrV2RevealComment(pendingScrollTarget.commentId);
              }
              if (!didScroll && resolvedFilename) {
                setFileCollapsed(resolvedFilename, false);
                if (!didSwitchChapter_3) {
                  scrollToFileElement(resolvedFilename, {
                    applyOffset: false
                  });
                }
              }
            } else {
              const didSwitchChapter_4 = selectChapterForFilename(pendingScrollTarget.filename);
              didScroll = !didSwitchChapter_4 && scrollToDraftComposerElement(pendingScrollTarget.filename);
              if (!didScroll) {
                setFileCollapsed(pendingScrollTarget.filename, false);
                if (!didSwitchChapter_4) {
                  scrollToFileElement(pendingScrollTarget.filename, {
                    applyOffset: false
                  });
                }
              }
            }
          }
        }
        if (didScroll) {
          setPendingScrollTarget(null);
          return;
        }
        attempts = attempts + 1;
        attempts;
        if (attempts >= MAX_SCROLL_RETRY_ATTEMPTS) {
          setPendingScrollTarget(null);
          return;
        }
        timeoutId = window.setTimeout(tryScroll, 100);
      };
      timeoutId = window.setTimeout(tryScroll, 0);
      return () => {
        cancelled = true;
        if (timeoutId != null) {
          window.clearTimeout(timeoutId);
        }
      };
    };
    t181 = [canPaintDiffs, chapterKeyByGroupId, commentIdToFilename, isPendingScrollTargetReady, pendingScrollTarget, query.isLoading, selectChapterByKey, selectChapterForFilename, scrollToCommentElement, scrollToDraftComposerElement, scrollToFileElement, scrollToGroupElement, setFileCollapsed, setManualActiveFilenameOverride];
    $[502] = canPaintDiffs;
    $[503] = chapterKeyByGroupId;
    $[504] = commentIdToFilename;
    $[505] = isPendingScrollTargetReady;
    $[506] = pendingScrollTarget;
    $[507] = query.isLoading;
    $[508] = scrollToCommentElement;
    $[509] = scrollToDraftComposerElement;
    $[510] = selectChapterByKey;
    $[511] = selectChapterForFilename;
    $[512] = setFileCollapsed;
    $[513] = t180;
    $[514] = t181;
  } else {
    t180 = $[513];
    t181 = $[514];
  }
  useEffect(t180, t181);
  let t182;
  if ($[515] !== addComment || $[516] !== addPendingComment || $[517] !== clearDraftForFile || $[518] !== currentUserAvatarUrl || $[519] !== currentUserLogin || $[520] !== disablePostImmediately || $[521] !== effectiveHeadSha || $[522] !== number || $[523] !== owner || $[524] !== prDraftKey || $[525] !== repo || $[526] !== setDraftForFile) {
    t182 = async (filename_16, t183) => {
      const submitMode = t183 === undefined ? "batch" : t183;
      if (!effectiveHeadSha) {
        return;
      }
      const draft = usePrV2DraftStore.getState().getDraft(prDraftKey, filename_16);
      const body = draft?.comment?.trim();
      if (!body) {
        return;
      }
      if (!draft) {
        return;
      }
      const draftSnapshot = {
        comment: draft.comment ?? "",
        line: draft.line ?? "",
        side: draft.side ?? null,
        startLine: draft.startLine ?? "",
        startSide: draft.startSide ?? null,
        selectedLines: draft.selectedLines ?? null,
        selectionOnly: Boolean(draft.selectionOnly)
      };
      const parsedLine = Number.parseInt(draft?.line ?? "", 10);
      const hasLineNumber = Number.isFinite(parsedLine) && parsedLine > 0;
      const parsedStartLine = Number.parseInt(draft?.startLine ?? "", 10);
      const hasStartLine = Number.isFinite(parsedStartLine) && parsedStartLine > 0;
      const selectedSide = draft?.side ?? "RIGHT";
      const selectedStartSide = draft?.startSide ?? selectedSide;
      const hasMultilineRange = hasLineNumber && hasStartLine && parsedStartLine !== parsedLine;
      const pendingCommentSide = githubReviewSideToCommentSide(selectedSide);
      const pendingCommentStartSide = hasMultilineRange ? githubReviewSideToCommentSide(selectedStartSide) : undefined;
      const shouldSubmitImmediately = submitMode === "immediate" && !disablePostImmediately;
      if (!shouldSubmitImmediately) {
        addPendingComment({
          id: createPendingCommentId(),
          owner,
          repo,
          pullNumber: Number(number),
          filename: filename_16,
          lineNumber: hasLineNumber ? parsedLine : 0,
          side: pendingCommentSide,
          content: body,
          user: {
            login: currentUserLogin,
            avatar_url: currentUserAvatarUrl,
            html_url: ""
          },
          created_at: new Date().toISOString(),
          commit_id: effectiveHeadSha,
          isPending: true,
          subject_type: hasLineNumber ? CommentSubjectType.line : CommentSubjectType.file,
          ...(hasMultilineRange ? {
            start_line: parsedStartLine,
            start_side: pendingCommentStartSide ?? pendingCommentSide
          } : {})
        });
        clearDraftForFile(prDraftKey, filename_16);
        return;
      }
      clearDraftForFile(prDraftKey, filename_16);
      const comment_4 = {
        body,
        commentType: "review_comment",
        path: filename_16,
        commitId: effectiveHeadSha,
        side: selectedSide,
        ...(hasLineNumber ? {
          line: parsedLine,
          subjectType: "line",
          ...(hasMultilineRange ? {
            startLine: parsedStartLine,
            startSide: selectedStartSide
          } : {})
        } : {
          subjectType: "file"
        })
      };
      ;
      try {
        await addComment(comment_4);
      } catch (t184) {
        const error = t184;
        const currentDraft_0 = usePrV2DraftStore.getState().getDraft(prDraftKey, filename_16);
        if (!currentDraft_0 || currentDraft_0.comment.trim().length === 0) {
          setDraftForFile(prDraftKey, filename_16, draftSnapshot);
        }
        clientLogger.error("Failed to post review comment in PR v2 diff", error);
        toast.error("Failed to post comment");
      }
    };
    $[515] = addComment;
    $[516] = addPendingComment;
    $[517] = clearDraftForFile;
    $[518] = currentUserAvatarUrl;
    $[519] = currentUserLogin;
    $[520] = disablePostImmediately;
    $[521] = effectiveHeadSha;
    $[522] = number;
    $[523] = owner;
    $[524] = prDraftKey;
    $[525] = repo;
    $[526] = setDraftForFile;
    $[527] = t182;
  } else {
    t182 = $[527];
  }
  const handlePostFileComment = t182;
  let t183;
  if ($[528] !== clearDraftForFile || $[529] !== prDraftKey || $[530] !== sendToSidebarChat) {
    t183 = sendToSidebarChat ? (filename_17, codeSource) => {
      const draft_0 = usePrV2DraftStore.getState().getDraft(prDraftKey, filename_17);
      if (!draft_0?.comment.trim()) {
        return;
      }
      sendToSidebarChat(buildDraftCommentAiChatText({
        filename: filename_17,
        draft: draft_0,
        ...codeSource
      }));
      clearDraftForFile(prDraftKey, filename_17);
    } : undefined;
    $[528] = clearDraftForFile;
    $[529] = prDraftKey;
    $[530] = sendToSidebarChat;
    $[531] = t183;
  } else {
    t183 = $[531];
  }
  const handleAskDraftInChat = t183;
  let t184;
  if ($[532] !== addPendingComment || $[533] !== commentByGithubId || $[534] !== currentUserAvatarUrl || $[535] !== currentUserLogin || $[536] !== disablePostImmediately || $[537] !== effectiveHeadSha || $[538] !== number || $[539] !== owner || $[540] !== prReplyDraftKey || $[541] !== replyToComment || $[542] !== repo || $[543] !== setReplyDraft) {
    t184 = async (githubCommentId, body_0, threadDraftKey_0) => {
      const submittedDraftValue = body_0;
      const replyBody = submittedDraftValue.trim();
      if (!replyBody) {
        return;
      }
      const clearReplyDraftIfUnchanged = () => {
        const currentDraft_1 = usePrV2ReplyDraftStore.getState().replyDraftsByPr[prReplyDraftKey]?.[threadDraftKey_0];
        if (currentDraft_1 == null || currentDraft_1 !== submittedDraftValue) {
          return;
        }
        setReplyDraft(prReplyDraftKey, threadDraftKey_0, "");
      };
      const pendingParentId = parsePendingGithubCommentId(githubCommentId);
      const shouldQueueReply = disablePostImmediately || pendingParentId != null;
      if (shouldQueueReply) {
        const parentComment = commentByGithubId.get(githubCommentId);
        if (!parentComment || !parentComment.path || !effectiveHeadSha) {
          toast.error("Failed to queue reply");
          throw new Error("Failed to queue reply");
        }
        const githubParentCommentId = asPositiveInteger(githubCommentId);
        const replyParentId = pendingParentId ?? githubParentCommentId;
        if (!replyParentId) {
          toast.error("Failed to queue reply");
          throw new Error("Failed to queue reply");
        }
        const parentRaw = asRecord(parentComment.raw);
        const parentSubjectType = resolveCommentSubjectType(parentComment);
        const parentSideRaw = asGithubDiffSide(parentRaw?.side ?? parentRaw?.start_side ?? parentRaw?.original_side) ?? "RIGHT";
        const parentStartSideRaw = asGithubDiffSide(parentRaw?.start_side);
        const parentStartLine = asPositiveInteger(parentRaw?.start_line);
        addPendingComment({
          id: createPendingCommentId(),
          owner,
          repo,
          pullNumber: Number(number),
          filename: parentComment.path,
          lineNumber: parentSubjectType === CommentSubjectType.line ? parentComment.line ?? 0 : 0,
          side: githubReviewSideToCommentSide(parentSideRaw),
          content: replyBody,
          user: {
            login: currentUserLogin,
            avatar_url: currentUserAvatarUrl,
            html_url: ""
          },
          created_at: new Date().toISOString(),
          commit_id: effectiveHeadSha,
          isPending: true,
          subject_type: parentSubjectType,
          ...(parentStartLine ? {
            start_line: parentStartLine,
            ...(parentStartSideRaw ? {
              start_side: githubReviewSideToCommentSide(parentStartSideRaw)
            } : {})
          } : {}),
          in_reply_to_id: replyParentId,
          parent_is_pending: pendingParentId != null
        });
        clearReplyDraftIfUnchanged();
        return;
      }
      const commentId_0 = Number.parseInt(githubCommentId, 10);
      if (!Number.isFinite(commentId_0)) {
        toast.error("Invalid parent comment id");
        throw new TypeError("Invalid parent comment id");
      }
      ;
      try {
        await replyToComment({
          commentId: commentId_0,
          body: replyBody
        });
        clearReplyDraftIfUnchanged();
      } catch (t185) {
        const error_0 = t185;
        const currentDraft_2 = usePrV2ReplyDraftStore.getState().replyDraftsByPr[prReplyDraftKey]?.[threadDraftKey_0];
        if (typeof currentDraft_2 === "string" && currentDraft_2.trim().length > 0 && currentDraft_2 !== submittedDraftValue) {
          return;
        }
        setReplyDraft(prReplyDraftKey, threadDraftKey_0, submittedDraftValue);
        clientLogger.error("Failed to post reply comment in PR v2 diff", error_0);
        const resolvedError = error_0 instanceof Error ? error_0 : new Error("Failed to post reply");
        toast.error(resolvedError.message);
        throw resolvedError;
      }
    };
    $[532] = addPendingComment;
    $[533] = commentByGithubId;
    $[534] = currentUserAvatarUrl;
    $[535] = currentUserLogin;
    $[536] = disablePostImmediately;
    $[537] = effectiveHeadSha;
    $[538] = number;
    $[539] = owner;
    $[540] = prReplyDraftKey;
    $[541] = replyToComment;
    $[542] = repo;
    $[543] = setReplyDraft;
    $[544] = t184;
  } else {
    t184 = $[544];
  }
  const handleReplyToComment = t184;
  let t185;
  if ($[545] !== resolveCommentThread || $[546] !== reviewThreadByCommentNodeId) {
    t185 = async (comment_5, resolve) => {
      const nodeId = extractCommentNodeId(comment_5);
      if (!nodeId) {
        throw new TypeError("Unable to locate review thread for this comment");
      }
      const thread = reviewThreadByCommentNodeId.get(nodeId);
      if (!thread) {
        throw new TypeError("Review thread not found");
      }
      const commentId_1 = Number.parseInt(comment_5.githubCommentId, 10);
      if (!Number.isFinite(commentId_1)) {
        throw new TypeError("Invalid comment id");
      }
      ;
      try {
        await resolveCommentThread({
          commentId: commentId_1,
          resolve,
          threadNodeId: thread.id
        });
      } catch (t186) {
        const error_1 = t186;
        clientLogger.error("Failed to toggle review thread state in PR v2 diff", error_1);
        throw error_1;
      }
    };
    $[545] = resolveCommentThread;
    $[546] = reviewThreadByCommentNodeId;
    $[547] = t185;
  } else {
    t185 = $[547];
  }
  const handleResolveThread = t185;
  let t186;
  if ($[548] !== editComment) {
    t186 = async (comment_6, body_1) => {
      const commentId_2 = asPositiveInteger(comment_6.githubCommentId);
      if (!commentId_2) {
        toast.error("Invalid comment id");
        throw new TypeError("Invalid comment id");
      }
      ;
      try {
        await editComment({
          commentId: commentId_2,
          body: body_1,
          commentType: normalizeCommentType(comment_6.commentType)
        });
      } catch (t187) {
        const error_2 = t187;
        clientLogger.error("Failed to edit comment in PR v2 diff", error_2);
        toast.error(error_2 instanceof Error ? error_2.message : "Failed to update comment");
        throw error_2;
      }
    };
    $[548] = editComment;
    $[549] = t186;
  } else {
    t186 = $[549];
  }
  const handleEditComment = t186;
  let t187;
  if ($[550] !== deleteComment) {
    t187 = async comment_7 => {
      const commentId_3 = asPositiveInteger(comment_7.githubCommentId);
      if (!commentId_3) {
        toast.error("Invalid comment id");
        throw new TypeError("Invalid comment id");
      }
      ;
      try {
        await deleteComment({
          commentId: commentId_3,
          commentType: normalizeCommentType(comment_7.commentType)
        });
      } catch (t188) {
        const error_3 = t188;
        clientLogger.error("Failed to delete comment in PR v2 diff", error_3);
        toast.error(error_3 instanceof Error ? error_3.message : "Failed to delete comment");
        throw error_3;
      }
    };
    $[550] = deleteComment;
    $[551] = t187;
  } else {
    t187 = $[551];
  }
  const handleDeleteComment = t187;
  let t188;
  if ($[552] !== editPendingComment) {
    t188 = async (comment_8, body_2) => {
      const pendingCommentId = parsePendingGithubCommentId(comment_8.githubCommentId);
      if (!pendingCommentId) {
        toast.error("Invalid pending comment id");
        throw new TypeError("Invalid pending comment id");
      }
      editPendingComment(pendingCommentId, body_2);
    };
    $[552] = editPendingComment;
    $[553] = t188;
  } else {
    t188 = $[553];
  }
  const handleEditPendingComment = t188;
  let t189;
  if ($[554] !== removePendingComment) {
    t189 = async comment_9 => {
      const pendingCommentId_0 = parsePendingGithubCommentId(comment_9.githubCommentId);
      if (!pendingCommentId_0) {
        toast.error("Invalid pending comment id");
        throw new TypeError("Invalid pending comment id");
      }
      removePendingComment(pendingCommentId_0);
    };
    $[554] = removePendingComment;
    $[555] = t189;
  } else {
    t189 = $[555];
  }
  const handleDeletePendingComment = t189;
  let t190;
  if ($[556] !== deleteComment || $[557] !== effectiveBaseSha || $[558] !== effectiveHeadSha || $[559] !== headRef || $[560] !== isCustomComparison || $[561] !== isMerged || $[562] !== number || $[563] !== owner || $[564] !== queryClient || $[565] !== repo) {
    t190 = async (comment_10, payload_0) => {
      if (isMerged) {
        return;
      }
      await applyPrV2Suggestion({
        owner,
        repo,
        number,
        headRef,
        headSha: effectiveHeadSha,
        baseSha: effectiveBaseSha,
        queryClient,
        queryKey: isCustomComparison ? null : prV2CodeChangesQueryKey(owner, repo, number, effectiveHeadSha, effectiveBaseSha),
        comment: comment_10,
        payload: payload_0,
        deleteComment: async t191 => {
          const {
            commentId: commentId_4,
            commentType
          } = t191;
          await deleteComment({
            commentId: commentId_4,
            commentType: normalizeCommentType(commentType)
          });
        }
      });
    };
    $[556] = deleteComment;
    $[557] = effectiveBaseSha;
    $[558] = effectiveHeadSha;
    $[559] = headRef;
    $[560] = isCustomComparison;
    $[561] = isMerged;
    $[562] = number;
    $[563] = owner;
    $[564] = queryClient;
    $[565] = repo;
    $[566] = t190;
  } else {
    t190 = $[566];
  }
  const handleApplySuggestion = t190;
  let t191;
  if ($[567] !== reactions) {
    t191 = (comment_11, emoji) => {
      reactions.toggleReaction(comment_11, emoji);
    };
    $[567] = reactions;
    $[568] = t191;
  } else {
    t191 = $[568];
  }
  const handleToggleThreadReaction = t191;
  let t192;
  if ($[569] !== reactions) {
    t192 = (comment_12, emoji_0) => reactions.getReactionCount(comment_12, emoji_0);
    $[569] = reactions;
    $[570] = t192;
  } else {
    t192 = $[570];
  }
  const getThreadReactionCount = t192;
  let t193;
  if ($[571] !== reactions) {
    t193 = (comment_13, emoji_1) => reactions.getReactionUsers(comment_13, emoji_1);
    $[571] = reactions;
    $[572] = t193;
  } else {
    t193 = $[572];
  }
  const getThreadReactionUsers = t193;
  let t194;
  if ($[573] !== reactions) {
    t194 = (comment_14, emoji_2) => reactions.isReactionActive(comment_14, emoji_2);
    $[573] = reactions;
    $[574] = t194;
  } else {
    t194 = $[574];
  }
  const isThreadReactionActive = t194;
  let t195;
  if ($[575] !== reactions) {
    t195 = comment_15 => reactions.isPendingForComment?.(comment_15) ?? reactions.isPending;
    $[575] = reactions;
    $[576] = t195;
  } else {
    t195 = $[576];
  }
  const isThreadReactionPendingForComment = t195;
  let t196;
  if ($[577] !== reactions) {
    t196 = (comment_16, emoji_3) => reactions.isPendingForReaction?.(comment_16, emoji_3) ?? reactions.isPendingForComment?.(comment_16) ?? reactions.isPending;
    $[577] = reactions;
    $[578] = t196;
  } else {
    t196 = $[578];
  }
  const isThreadReactionPendingForEmoji = t196;
  const canReactToThreadComment = _temp58;
  let t197;
  if ($[579] !== getThreadReactionCount || $[580] !== getThreadReactionUsers || $[581] !== handleToggleThreadReaction || $[582] !== isThreadReactionActive || $[583] !== isThreadReactionPendingForComment || $[584] !== isThreadReactionPendingForEmoji || $[585] !== reactions.isPending) {
    t197 = {
      isPending: reactions.isPending,
      isPendingForComment: isThreadReactionPendingForComment,
      isPendingForReaction: isThreadReactionPendingForEmoji,
      onToggleReaction: handleToggleThreadReaction,
      getReactionCount: getThreadReactionCount,
      getReactionUsers: getThreadReactionUsers,
      isReactionActive: isThreadReactionActive,
      canReactToComment: canReactToThreadComment
    };
    $[579] = getThreadReactionCount;
    $[580] = getThreadReactionUsers;
    $[581] = handleToggleThreadReaction;
    $[582] = isThreadReactionActive;
    $[583] = isThreadReactionPendingForComment;
    $[584] = isThreadReactionPendingForEmoji;
    $[585] = reactions.isPending;
    $[586] = t197;
  } else {
    t197 = $[586];
  }
  const threadReactions = t197;
  let t198;
  if ($[587] !== number || $[588] !== owner || $[589] !== queryClient || $[590] !== repo) {
    t198 = queryClient.getQueryData(prV2SummaryQueryKey(owner, repo, number))?.pullRequest.metadataSummary.changedFiles;
    $[587] = number;
    $[588] = owner;
    $[589] = queryClient;
    $[590] = repo;
    $[591] = t198;
  } else {
    t198 = $[591];
  }
  const summaryChangedFiles = t198;
  const changesSkeletonFileCount = Math.min(Math.max(summaryChangedFiles ?? 1, 1), 8);
  useMarkPrV2ContentPainted(prV2ActiveFileStoreKey, query.isSuccess && !showComparisonLoadingPanel && canPaintDiffs || query.isError);
  let t199;
  if ($[592] !== activeFilename || $[593] !== chapterEntries || $[594] !== fileReviewDecisionsByFilename || $[595] !== handleToggleGroupReviewed || $[596] !== hasPendingOrderedFilesLoading || $[597] !== navigateToGroup || $[598] !== onSelectGroupFile || $[599] !== viewedFiles) {
    t199 = t200 => {
      const {
        group: group_0,
        sectionIndex
      } = t200;
      return {
        group: group_0,
        previousGroup: chapterEntries[sectionIndex - 2]?.group,
        nextGroup: chapterEntries[sectionIndex]?.group,
        sectionIndex,
        sectionCount: chapterEntries.length,
        isGroupingPending: hasPendingOrderedFilesLoading,
        decisions: group_0.files.flatMap(file_2 => fileReviewDecisionsByFilename.get(file_2.filename) ?? []),
        activeFilename,
        isGroupReviewed: group_0.files.length > 0 && group_0.files.every(file_3 => viewedFiles.has(file_3.filename)),
        onToggleGroupReviewed: handleToggleGroupReviewed,
        onNavigateGroup: navigateToGroup,
        onSelectFile: onSelectGroupFile
      };
    };
    $[592] = activeFilename;
    $[593] = chapterEntries;
    $[594] = fileReviewDecisionsByFilename;
    $[595] = handleToggleGroupReviewed;
    $[596] = hasPendingOrderedFilesLoading;
    $[597] = navigateToGroup;
    $[598] = onSelectGroupFile;
    $[599] = viewedFiles;
    $[600] = t199;
  } else {
    t199 = $[600];
  }
  const getGroupHeaderArgs = t199;
  if (query.isLoading) {
    let t200;
    if ($[601] !== changesSkeletonFileCount || $[602] !== fileTreeOpenFrom || $[603] !== groupGuide || $[604] !== isCompactLayout) {
      t200 = <PrV2ChangesPaneSkeleton fileCount={changesSkeletonFileCount} groupGuide={groupGuide} fileTreeOpenFrom={fileTreeOpenFrom} compact={isCompactLayout} />;
      $[601] = changesSkeletonFileCount;
      $[602] = fileTreeOpenFrom;
      $[603] = groupGuide;
      $[604] = isCompactLayout;
      $[605] = t200;
    } else {
      t200 = $[605];
    }
    return t200;
  }
  if (query.error) {
    const t200 = query.error instanceof Error ? query.error.message : "Failed to load code changes";
    let t201;
    if ($[606] !== t200) {
      t201 = <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{t200}</div>;
      $[606] = t200;
      $[607] = t201;
    } else {
      t201 = $[607];
    }
    return t201;
  }
  let t200;
  if ($[608] !== activeFilename || $[609] !== applyViewPreferences || $[610] !== canPaintDiffs || $[611] !== changedSinceLastViewFiles || $[612] !== changesSkeletonFileCount || $[613] !== chapterEntries || $[614] !== codeContent || $[615] !== codeViewFilenames || $[616] !== commentsByFile || $[617] !== comparisonChoices || $[618] !== currentGroupEntry || $[619] !== currentUserAvatarUrl || $[620] !== currentUserLogin || $[621] !== defaultComparison.lastReviewedSubmittedAt || $[622] !== diffOptions || $[623] !== disablePostImmediately || $[624] !== effectiveBaseSha || $[625] !== effectiveHeadSha || $[626] !== emptyComparisonMessage || $[627] !== fileDiffByFilename || $[628] !== fileReviewDecisionsByFilename || $[629] !== files || $[630] !== filteredStats || $[631] !== getGroupHeaderArgs || $[632] !== groupCardSlot || $[633] !== groupGuide || $[634] !== handleApplySuggestion || $[635] !== handleAskDraftInChat || $[636] !== handleDecisionMarkerClick || $[637] !== handleDecisionMarkerHover || $[638] !== handleDeleteComment || $[639] !== handleDeletePendingComment || $[640] !== handleEditComment || $[641] !== handleEditPendingComment || $[642] !== handlePostFileComment || $[643] !== handleReplyToComment || $[644] !== handleResolveThread || $[645] !== handleToggleCollapsed || $[646] !== inlineAnchorByCommentId || $[647] !== isCollapsed || $[648] !== isMutating || $[649] !== isReadOnly || $[650] !== isShowingChangesSinceLastReview || $[651] !== isViewed || $[652] !== mergeComparisonNotice || $[653] !== mergedSuggestionApplyDisabledReason || $[654] !== number || $[655] !== owner || $[656] !== prDraftKey || $[657] !== prReplyDraftKey || $[658] !== readModelCommentByGithubId || $[659] !== repo || $[660] !== resolvedActiveChapterKey || $[661] !== reviewThreadByCommentNodeId || $[662] !== reviewThreadContextByCommentNodeId || $[663] !== selectChapterByKey || $[664] !== setComparison || $[665] !== severityByCommentId || $[666] !== showChapterSwitcher || $[667] !== showComparisonLoadingPanel || $[668] !== summaryChangedFiles || $[669] !== threadMutationState || $[670] !== threadReactions || $[671] !== toggleFileDraft || $[672] !== toggleViewedForFile || $[673] !== visibleChapterEntries || $[674] !== visibleCodeContent) {
    t200 = <div className="@container/pr-diff min-w-0 flex-1">{showComparisonLoadingPanel ? <PrV2ChangesContentSkeleton groupGuide={groupGuide} /> : !canPaintDiffs ? <PrV2ChangesContentSkeleton fileCount={changesSkeletonFileCount} groupGuide={groupGuide} /> : <div className="mr-1 space-y-6 pl-3 pr-2 pt-4 pb-10 md:pl-0.5">{mergeComparisonNotice ? <div className="rounded-md border border-zinc-800 bg-zinc-900/70 px-3 py-2 text-sm text-zinc-300">{mergeComparisonNotice}</div> : null}{isShowingChangesSinceLastReview ? <PrV2SinceLastReviewNotice comparisonChoices={comparisonChoices} fileCount={files.length} totalFileCount={summaryChangedFiles} lastReviewedSubmittedAt={defaultComparison.lastReviewedSubmittedAt} setComparison={setComparison} /> : null}{showChapterSwitcher ? <PrV2ChapterSwitcher chapterEntries={chapterEntries} activeChapterKey={resolvedActiveChapterKey} placement="top" onSelectChapter={selectChapterByKey} /> : null}{visibleCodeContent.length ? <>{groupCardSlot && currentGroupEntry ? createPortal(<PrV2GroupCard {...getGroupHeaderArgs(currentGroupEntry)} />, groupCardSlot) : null}{visibleChapterEntries.map((entry_2, visibleGroupIndex) => {
            const {
              group: group_1,
              key
            } = entry_2;
            return <PrV2GroupSection key={key} {...getGroupHeaderArgs(entry_2)} visibleGroupIndex={visibleGroupIndex} showGuide={groupGuide === "inline"}><div className="min-w-0 space-y-4">{group_1.files.map(file_4 => {
                  const fileReviewDecisions = fileReviewDecisionsByFilename.get(file_4.filename);
                  const fileIsCollapsed = isCollapsed(file_4.filename);
                  return <PrV2FileSection key={file_4.filename} owner={owner} repo={repo} number={number} isReadOnly={isReadOnly} prDraftKey={prDraftKey} prReplyDraftKey={prReplyDraftKey} currentUserLogin={currentUserLogin} currentUserAvatarUrl={currentUserAvatarUrl} file={file_4} fileDiff={getOwnFileDiffMetadata(fileDiffByFilename, file_4.filename)} changedFiles={codeContent} headSha={effectiveHeadSha} baseSha={effectiveBaseSha} diffOptions={diffOptions} isCodeView={codeViewFilenames.has(file_4.filename)} onToggleCodeView={handleToggleCodeView} commentsByFile={commentsByFile} severityByCommentId={severityByCommentId} readModelCommentByGithubId={readModelCommentByGithubId} reviewThreadContextByCommentNodeId={reviewThreadContextByCommentNodeId} reviewThreadByCommentNodeId={reviewThreadByCommentNodeId} inlineAnchorByCommentId={inlineAnchorByCommentId} fileIsViewed={isViewed(file_4.filename)} fileIsChangedSinceLastView={changedSinceLastViewFiles.has(file_4.filename)} fileIsCollapsed={fileIsCollapsed} fileReviewDecisions={fileReviewDecisions} onDecisionMarkerHover={handleDecisionMarkerHover} onDecisionMarkerClick={handleDecisionMarkerClick} isActiveFile={activeFilename === file_4.filename} isMutating={isMutating} mutationState={threadMutationState} disablePostImmediately={disablePostImmediately} onToggleCollapsed={handleToggleCollapsed} onToggleViewed={toggleViewedForFile} onToggleFileDraft={toggleFileDraft} onPostFileComment={handlePostFileComment} onAskDraftInChat={handleAskDraftInChat} onReplySubmit={handleReplyToComment} onResolveToggle={handleResolveThread} onEditComment={handleEditComment} onDeleteComment={handleDeleteComment} onEditPendingComment={handleEditPendingComment} onDeletePendingComment={handleDeletePendingComment} reactions={threadReactions} onApplySuggestion={handleApplySuggestion} suggestionApplyDisabledReason={mergedSuggestionApplyDisabledReason} />;
                })}</div></PrV2GroupSection>;
          })}{showChapterSwitcher ? <PrV2ChapterSwitcher chapterEntries={chapterEntries} activeChapterKey={resolvedActiveChapterKey} placement="bottom" onSelectChapter={selectChapterByKey} /> : null}</> : <div className="px-1 py-6 text-sm text-zinc-400 md:px-2">{filteredStats.hiddenTestFileCount > 0 && filteredStats.fileCount === 0 ? <>{filteredStats.hiddenTestFileCount === 1 ? "The only changed file is a test file." : `All ${filteredStats.hiddenTestFileCount} files are test files.`}{" "}<button type="button" className="underline underline-offset-2 hover:text-zinc-200" onClick={() => applyViewPreferences({
              hideTestFiles: false
            })}>Show test files</button></> : emptyComparisonMessage}</div>}</div>}</div>;
    $[608] = activeFilename;
    $[609] = applyViewPreferences;
    $[610] = canPaintDiffs;
    $[611] = changedSinceLastViewFiles;
    $[612] = changesSkeletonFileCount;
    $[613] = chapterEntries;
    $[614] = codeContent;
    $[615] = codeViewFilenames;
    $[616] = commentsByFile;
    $[617] = comparisonChoices;
    $[618] = currentGroupEntry;
    $[619] = currentUserAvatarUrl;
    $[620] = currentUserLogin;
    $[621] = defaultComparison.lastReviewedSubmittedAt;
    $[622] = diffOptions;
    $[623] = disablePostImmediately;
    $[624] = effectiveBaseSha;
    $[625] = effectiveHeadSha;
    $[626] = emptyComparisonMessage;
    $[627] = fileDiffByFilename;
    $[628] = fileReviewDecisionsByFilename;
    $[629] = files;
    $[630] = filteredStats;
    $[631] = getGroupHeaderArgs;
    $[632] = groupCardSlot;
    $[633] = groupGuide;
    $[634] = handleApplySuggestion;
    $[635] = handleAskDraftInChat;
    $[636] = handleDecisionMarkerClick;
    $[637] = handleDecisionMarkerHover;
    $[638] = handleDeleteComment;
    $[639] = handleDeletePendingComment;
    $[640] = handleEditComment;
    $[641] = handleEditPendingComment;
    $[642] = handlePostFileComment;
    $[643] = handleReplyToComment;
    $[644] = handleResolveThread;
    $[645] = handleToggleCollapsed;
    $[646] = inlineAnchorByCommentId;
    $[647] = isCollapsed;
    $[648] = isMutating;
    $[649] = isReadOnly;
    $[650] = isShowingChangesSinceLastReview;
    $[651] = isViewed;
    $[652] = mergeComparisonNotice;
    $[653] = mergedSuggestionApplyDisabledReason;
    $[654] = number;
    $[655] = owner;
    $[656] = prDraftKey;
    $[657] = prReplyDraftKey;
    $[658] = readModelCommentByGithubId;
    $[659] = repo;
    $[660] = resolvedActiveChapterKey;
    $[661] = reviewThreadByCommentNodeId;
    $[662] = reviewThreadContextByCommentNodeId;
    $[663] = selectChapterByKey;
    $[664] = setComparison;
    $[665] = severityByCommentId;
    $[666] = showChapterSwitcher;
    $[667] = showComparisonLoadingPanel;
    $[668] = summaryChangedFiles;
    $[669] = threadMutationState;
    $[670] = threadReactions;
    $[671] = toggleFileDraft;
    $[672] = toggleViewedForFile;
    $[673] = visibleChapterEntries;
    $[674] = visibleCodeContent;
    $[675] = t200;
  } else {
    t200 = $[675];
  }
  let t201;
  if ($[676] !== diffOptions || $[677] !== effectiveBaseSha || $[678] !== effectiveHeadSha || $[679] !== number || $[680] !== owner || $[681] !== repo) {
    t201 = <PrV2RepoFilePanel owner={owner} repo={repo} number={number} headSha={effectiveHeadSha} baseSha={effectiveBaseSha} display={diffOptions} />;
    $[676] = diffOptions;
    $[677] = effectiveBaseSha;
    $[678] = effectiveHeadSha;
    $[679] = number;
    $[680] = owner;
    $[681] = repo;
    $[682] = t201;
  } else {
    t201 = $[682];
  }
  let t202;
  if ($[683] !== navigateToFile) {
    t202 = filename_18 => {
      navigateToFile(filename_18);
    };
    $[683] = navigateToFile;
    $[684] = t202;
  } else {
    t202 = $[684];
  }
  let t203;
  if ($[685] !== navigateToGroup) {
    t203 = groupId_1 => {
      navigateToGroup(groupId_1);
    };
    $[685] = navigateToGroup;
    $[686] = t203;
  } else {
    t203 = $[686];
  }
  let t204;
  if ($[687] !== allComments || $[688] !== changedSinceLastViewFiles || $[689] !== defaultComparison.lastReviewedSubmittedAt || $[690] !== defaultComparison.lastReviewedVersionSha || $[691] !== effectiveBaseSha || $[692] !== effectiveHeadSha || $[693] !== fileReviewDecisionsByFilename || $[694] !== fileTreeLayout || $[695] !== fileTreeOpenFrom || $[696] !== fileTreeToolbarHost || $[697] !== getChapterKeyForFilename || $[698] !== hasAiFileGrouping || $[699] !== hasMeaningfulComparison || $[700] !== hasPendingOrderedFilesLoading || $[701] !== hasUserComparisonOverride || $[702] !== isComparisonLoading || $[703] !== isCustomComparison || $[704] !== isLargePrChapterMode || $[705] !== number || $[706] !== owner || $[707] !== repo || $[708] !== resetComparison || $[709] !== resolvedActiveChapterKey || $[710] !== reviewThreads || $[711] !== selectChapterByKey || $[712] !== setComparison || $[713] !== t202 || $[714] !== t203 || $[715] !== treeDisplayFiles || $[716] !== treeDisplayGroupedCodeContent || $[717] !== treeDisplayOrderedFiles || $[718] !== versionOptions || $[719] !== versionUpdate || $[720] !== viewedFiles) {
    t204 = <PrV2FileTree desktopSide="right" toolbarHost={fileTreeToolbarHost} openFrom={fileTreeOpenFrom} layout={fileTreeLayout} files={treeDisplayFiles} orderedFiles={treeDisplayOrderedFiles} groupedCodeContent={treeDisplayGroupedCodeContent} hasAiFileGrouping={hasAiFileGrouping} fileReviewDecisionsByFilename={fileReviewDecisionsByFilename} owner={owner} repo={repo} number={number} versionOptions={versionOptions} selectedBaseSha={effectiveBaseSha} selectedHeadSha={effectiveHeadSha} isComparisonLoading={isComparisonLoading} isOrderedFilesLoading={hasPendingOrderedFilesLoading} hasMeaningfulComparison={hasMeaningfulComparison} isCustomComparison={isCustomComparison} hasUserComparisonOverride={hasUserComparisonOverride} lastReviewedVersionSha={defaultComparison.lastReviewedVersionSha} lastReviewedSubmittedAt={defaultComparison.lastReviewedSubmittedAt} setComparisonAction={setComparison} resetComparisonAction={resetComparison} versionUpdate={versionUpdate} comments={allComments} reviewThreads={reviewThreads} viewedFilenames={viewedFiles} changedSinceLastViewFilenames={changedSinceLastViewFiles} chapterModeEnabled={isLargePrChapterMode} activeChapterKey={resolvedActiveChapterKey} getChapterKeyForFilename={getChapterKeyForFilename} selectChapterAction={selectChapterByKey} selectFileAction={t202} selectGroupAction={t203} />;
    $[687] = allComments;
    $[688] = changedSinceLastViewFiles;
    $[689] = defaultComparison.lastReviewedSubmittedAt;
    $[690] = defaultComparison.lastReviewedVersionSha;
    $[691] = effectiveBaseSha;
    $[692] = effectiveHeadSha;
    $[693] = fileReviewDecisionsByFilename;
    $[694] = fileTreeLayout;
    $[695] = fileTreeOpenFrom;
    $[696] = fileTreeToolbarHost;
    $[697] = getChapterKeyForFilename;
    $[698] = hasAiFileGrouping;
    $[699] = hasMeaningfulComparison;
    $[700] = hasPendingOrderedFilesLoading;
    $[701] = hasUserComparisonOverride;
    $[702] = isComparisonLoading;
    $[703] = isCustomComparison;
    $[704] = isLargePrChapterMode;
    $[705] = number;
    $[706] = owner;
    $[707] = repo;
    $[708] = resetComparison;
    $[709] = resolvedActiveChapterKey;
    $[710] = reviewThreads;
    $[711] = selectChapterByKey;
    $[712] = setComparison;
    $[713] = t202;
    $[714] = t203;
    $[715] = treeDisplayFiles;
    $[716] = treeDisplayGroupedCodeContent;
    $[717] = treeDisplayOrderedFiles;
    $[718] = versionOptions;
    $[719] = versionUpdate;
    $[720] = viewedFiles;
    $[721] = t204;
  } else {
    t204 = $[721];
  }
  let t205;
  if ($[722] !== t200 || $[723] !== t201 || $[724] !== t204) {
    t205 = <div className="relative flex w-full max-w-none gap-0">{t200}{t201}{t204}</div>;
    $[722] = t200;
    $[723] = t201;
    $[724] = t204;
    $[725] = t205;
  } else {
    t205 = $[725];
  }
  return t205;
}
function _temp58(comment_17) {
  return asPositiveInteger(comment_17.githubCommentId) != null && (comment_17.commentType === "issue_comment" || comment_17.commentType === "review_comment");
}
function _temp57(entry_1) {
  return entry_1.group.files.map(_temp56);
}
function _temp56(file_1) {
  return file_1.filename;
}
function _temp55(filename_11) {
  if (!filename_11 || typeof window === "undefined") {
    return false;
  }
  const element_5 = document.getElementById(getPrV2FileElementId(filename_11));
  if (!element_5) {
    return false;
  }
  const rect = element_5.getBoundingClientRect();
  return rect.bottom > getPrV2FileViewportTopOffset() && rect.top < window.innerHeight;
}
function _temp54(element, t0) {
  const applyOffset = t0 === undefined ? true : t0;
  element.scrollIntoView({
    behavior: "instant",
    block: "start"
  });
  if (!applyOffset) {
    return;
  }
  applyScrollOffsetForElement(element, getPrV2FileViewportTopOffset(element));
}
function _temp53(group) {
  return group.files.map(_temp52);
}
function _temp52(file_0) {
  return file_0.filename;
}
function _temp51(entry) {
  return entry.group;
}
function _temp50(file) {
  return file.filename;
}
function _temp49(state_26) {
  return state_26.applyPreferences;
}
function _temp48(state_25) {
  return state_25.hideTestFiles;
}
function _temp47(state_24) {
  return state_24.expandUnchanged;
}
function _temp46(state_23) {
  return state_23.showLineNumbers;
}
function _temp45(state_22) {
  return state_22.highlightTheme;
}
function _temp44(state_21) {
  return state_21.hunkSeparators;
}
function _temp43(state_20) {
  return state_20.lineDiffType;
}
function _temp42(state_19) {
  return state_19.diffIndicators;
}
function _temp41(state_18) {
  return state_18.overflow;
}
function _temp40(state_17) {
  return state_17.diffStyle;
}
function _temp39(state_16) {
  return state_16.clearActiveFilename;
}
function _temp38(state_15) {
  return state_15.setActiveFilename;
}
function _temp37(state_14) {
  return state_14.clearHandlers;
}
function _temp36(state_13) {
  return state_13.setHandlers;
}
function _temp35() {
  return new Set();
}
function _temp34() {
  return EMPTY_PR_V2_CHAPTER_MODE_STATE;
}
function _temp33(state_12) {
  return state_12.clearActiveReplyTargetsForPr;
}
function _temp32(state_11) {
  return state_11.clearReplyDraftsForPr;
}
function _temp31(state_10) {
  return state_10.pruneActiveReplyTargetsToExistingKeys;
}
function _temp30(state_9) {
  return state_9.pruneReplyDraftsToExistingKeys;
}
function _temp29(state_8) {
  return state_8.setReplyDraft;
}
function _temp28(state_7) {
  return state_7.clearDraftsForPr;
}
function _temp27(state_6) {
  return state_6.pruneDraftsToExistingFiles;
}
function _temp26(state_5) {
  return state_5.openFileDraft;
}
function _temp25(state_4) {
  return state_4.setDraftForFile;
}
function _temp24(state_3) {
  return state_3.clearDraftForFile;
}
function _temp23(commentEntry) {
  return commentEntry.comment;
}
function _temp22(pendingComment_0) {
  return toPendingGithubCommentId(pendingComment_0.id);
}
function _temp21(comment_0) {
  return comment_0.githubCommentId;
}
function _temp20(state_2) {
  return state_2.editPendingComment;
}
function _temp19(state_1) {
  return state_1.removePendingComment;
}
function _temp18(state_0) {
  return state_0.addPendingComment;
}
function _temp17(state) {
  return state.pendingComments;
}
function _temp16(comment) {
  return comment.commentType === "review_comment";
}
function _temp(state_2) {
  return state_2.setDraftComment;
}
function _temp2(comment) {
  return comment.commentType === "review_comment";
}
function _temp3(state_2) {
  return state_2.setReplyDraft;
}
function _temp4(state_3) {
  return state_3.setActiveReplyTarget;
}
function _temp5(comment_1) {
  return comment_1.commentType === "review_comment";
}
function _temp6(state_0) {
  return state_0.toggleLineDraft;
}
function _temp7(state_1) {
  return state_1.setLineSelection;
}
function _temp8(state_2) {
  return state_2.setDraftComment;
}
function _temp9(state_3) {
  return state_3.clearDraftForFile;
}
function _temp10(a, b) {
  return toMillis(getCommentCreatedAt(a)) - toMillis(getCommentCreatedAt(b));
}
function _temp11(a_0, b_0) {
  return toMillis(getCommentCreatedAt(a_0)) - toMillis(getCommentCreatedAt(b_0));
}
function _temp12(groupedFileLevelThread) {
  const sortedComments = [...groupedFileLevelThread.comments].sort(_temp11);
  return {
    ...groupedFileLevelThread,
    comments: sortedComments
  };
}
function _temp13(a_1, b_1) {
  return a_1.lineNumber - b_1.lineNumber;
}
function _temp14(bucket_0) {
  return {
    side: bucket_0.side,
    lineNumber: bucket_0.lineNumber,
    metadata: bucket_0
  } satisfies DiffLineAnnotation<PrV2DiffCommentAnnotationMetadata>;
}
function _temp15(callback) {
  return window.setTimeout(callback, 0);
}